import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { ApiClient } from "./api-client.js";
import { ErrorBoundary } from "./error-boundary.jsx";
import { AudioDeckController } from "./audio-deck-controller.js";
import { HapticHeartbeatController } from "./haptic-heartbeat.js";
import { decodeSpeechAudio } from "./speech-audio.js";
import { SpatialTranceEngine } from "./spatial-trance-engine.js";
import { countdownValue, genericForegroundFallback, orderedFullCountdown } from "./main-voice-fallback.js";
import { SessionBuilder } from "./session-builder.jsx";
import { SessionTimeline } from "./session-timeline.jsx";
import { applyHypnosisProtocol } from "./hypnosis-protocol.js";
import {
  BrainwaveLabTone,
  brainwaveLabScene,
  resolveArousalPulseHz,
  SUBLIMINAL_GAIN_CEILING,
  loadBrainwaveLab,
  normalizeBrainwaveLab,
  resolveBrainwaveLabPlaybackSettings,
  saveBrainwaveLab,
} from "./brainwave-lab.js";
import {
  deleteBrainwavePreset,
  listBrainwavePresets,
  markBrainwavePresetUsed,
  parseBrainwavePreset,
  saveBrainwavePreset,
  serializeBrainwavePreset,
} from "./brainwave-presets.js";
import {
  ensureSubliminalWorklet,
  createSubliminalShifterNode,
  clampCarrierShiftHz,
  clampShiftHz,
  applySubliminalBrainwaveScene,
  resolveSubliminalChannelMode,
  resolveCarrierShiftHz,
  resolveSubliminalBackgroundShiftHz,
} from "./subliminal-binaural.js";
import {
  resolveDuckedSubliminalGain,
  resolveSubliminalGain,
} from "./subliminal-level.js";
import {
  measureTimeDomainBytes,
  summarizeSubliminalSignal,
} from "./subliminal-metrics.js";
import { resolveAudioTransportProfile } from "./audio-transport-telemetry.js";
import { createCovertEngine } from "./covert-delivery.js";
import {
  LANE_MUSIC_DROPS,
  LANE_HYPNOSIS_SPOKEN_MAIN,
  LANE_WHISPER_LEFT,
  LANE_WHISPER_RIGHT,
  LANE_SUBLIMINAL_1,
  LANE_SUBLIMINAL_2,
  LANE_OVERLAY,
  LANE_ENCOUNTER_PREFIX,
  isSubliminalLane,
  isWhisperLane,
  isEncounterLane,
} from "./audio-lanes.js";
import {
  createSessionWelcomePlaybackGuard,
  isSessionWelcomeEvent,
  isSessionWelcomeTrack,
  selectContinuousMainVoiceQueue,
} from "./session-welcome-playback.js";
import {
  MESSAGE_DETECTION_METRIC,
  RELAXATION_OUTCOME_METRIC,
  TRANCE_OUTCOME_METRIC,
  buildOutcomeContext,
  detectionResponseLabel,
  normalizeDetectionResponse,
  normalizeOutcomeRating,
  normalizeTranceResponse,
  resolveOutcomeReportPhase,
} from "./session-outcome-checkin.js";
import "./app.css";

const api = new ApiClient();
const SESSION_BUILDER_LOCAL_KEY = "acelab.session-builder.preferences";

function sessionBuilderLocalKey(session) {
  const profile = String(session?.google_sub || "").trim();
  return profile ? `${SESSION_BUILDER_LOCAL_KEY}.${encodeURIComponent(profile)}` : `${SESSION_BUILDER_LOCAL_KEY}.guest`;
}

function readLocalSessionBuilderPreferences(session) {
  try {
    const raw = window.localStorage.getItem(sessionBuilderLocalKey(session));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// Whisper clips vary substantially in render loudness. Target the real-world
// whisper band rather than treating HTMLMediaElement.volume as dB: roughly
// -30 to -20 dBFS RMS, with -24 dBFS as the nominal target. The peak limiter
// remains separate so normalization cannot turn a whisper into foreground.
const WHISPER_TARGET_DBFS = -24;
const WHISPER_PEAK_CEILING_DBFS = -18;
const WHISPER_LEVEL_MIN = 0.06;
const WHISPER_LEVEL_DEFAULT = 0.09;
const WHISPER_LEVEL_MAX = 0.16;
const WHISPER_VOLUME_BOOST = 1.10;
const WHISPER_MAKEUP_GAIN = 1.4;
const WHISPER_NORMALIZED_TARGET_RMS = 0.055;
const WHISPER_NORMALIZED_GAIN_MIN = 0.06;
const WHISPER_NORMALIZED_GAIN_MAX = 0.42;
const WHISPER_PEAK_CEILING = 0.82;

const decodedAudioLevelCache = new WeakMap();

function resolveWhisperGain(scene = {}) {
  const requested = Number(scene?.whisperLevel);
  const base = Number.isFinite(requested) && requested > 0
    ? requested
    : WHISPER_LEVEL_DEFAULT;
  return Math.max(
    WHISPER_LEVEL_MIN,
    Math.min(WHISPER_LEVEL_MAX, base * WHISPER_VOLUME_BOOST),
  );
}

function measureAudioBufferLevel(buffer) {
  if (!buffer) return { rms: 0, peak: 0 };
  const cached = decodedAudioLevelCache.get(buffer);
  if (cached) return cached;
  const channelCount = Math.max(1, Number(buffer.numberOfChannels) || 1);
  const length = Math.max(1, Number(buffer.length) || 1);
  // The level only needs to make a stable mix decision. Sampling at most
  // 120k points keeps long clips cheap while still catching clipped peaks.
  const stride = Math.max(1, Math.ceil(length / 120000));
  let sumSquares = 0;
  let peak = 0;
  let samples = 0;
  for (let channel = 0; channel < channelCount; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < length; index += stride) {
      const sample = Number(data[index]) || 0;
      const magnitude = Math.abs(sample);
      sumSquares += sample * sample;
      peak = Math.max(peak, magnitude);
      samples += 1;
    }
  }
  const measured = {
    rms: Math.sqrt(sumSquares / Math.max(1, samples)),
    peak,
  };
  decodedAudioLevelCache.set(buffer, measured);
  return measured;
}

function resolveWhisperNormalizedGain(baseGain, buffer, track = {}) {
  const measured = measureAudioBufferLevel(buffer);
  const metadataRms = Number(track?.audio_rms ?? track?.rms);
  const metadataPeak = Number(track?.audio_peak ?? track?.peak);
  const sourceRms = Number.isFinite(metadataRms) && metadataRms > 0 && metadataRms <= 1
    ? metadataRms
    : measured.rms;
  const sourcePeak = Number.isFinite(metadataPeak) && metadataPeak > 0 && metadataPeak <= 1
    ? metadataPeak
    : measured.peak;
  // Normalize the source energy, not the authored whisperLevel. This keeps a
  // quiet render from disappearing and a hot render from shouting, while the
  // authored scene value remains the user's coarse intensity control.
  const rmsCompensation = WHISPER_NORMALIZED_TARGET_RMS / Math.max(0.004, sourceRms);
  const peakLimitedGain = sourcePeak > 0
    ? WHISPER_PEAK_CEILING / sourcePeak
    : WHISPER_NORMALIZED_GAIN_MAX;
  const normalizedGain = Math.min(
    WHISPER_NORMALIZED_GAIN_MAX,
    peakLimitedGain,
    Math.max(WHISPER_NORMALIZED_GAIN_MIN, Number(baseGain) * rmsCompensation),
  );
  return Math.max(WHISPER_NORMALIZED_GAIN_MIN, Number.isFinite(normalizedGain) ? normalizedGain : baseGain);
}

function resolveWhisperNativeGain(scene = {}, track = {}) {
  const explicit = Number(track?.normalized_whisper_gain ?? track?.whisper_gain);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.max(WHISPER_NORMALIZED_GAIN_MIN, Math.min(WHISPER_NORMALIZED_GAIN_MAX, explicit));
  }
  const baseGain = resolveWhisperGain(scene);
  const sourceRms = Number(track?.audio_rms ?? track?.rms);
  const sourcePeak = Number(track?.audio_peak ?? track?.peak);
  if (!(Number.isFinite(sourceRms) && sourceRms > 0 && sourceRms <= 1)) return baseGain;
  const rmsCompensation = WHISPER_NORMALIZED_TARGET_RMS / Math.max(0.004, sourceRms);
  const peakLimitedGain = Number.isFinite(sourcePeak) && sourcePeak > 0 && sourcePeak <= 1
    ? WHISPER_PEAK_CEILING / sourcePeak
    : WHISPER_NORMALIZED_GAIN_MAX;
  return Math.max(
    WHISPER_NORMALIZED_GAIN_MIN,
    Math.min(WHISPER_NORMALIZED_GAIN_MAX, peakLimitedGain, baseGain * rmsCompensation),
  );
}

function buildSilentWavDataUri(durationMs = 300) {
  // A valid, non-zero-duration PCM asset is required. A header-only WAV can
  // resolve play() without entering real playback and therefore grants no
  // durable Android media-element authorization.
  const sampleRate = 8000;
  const sampleCount = Math.max(1, Math.round(sampleRate * durationMs / 1000));
  const buffer = new ArrayBuffer(44 + sampleCount * 2);
  const view = new DataView(buffer);
  const writeAscii = (offset, value) => {
    for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
  };
  writeAscii(0, "RIFF");
  view.setUint32(4, 36 + sampleCount * 2, true);
  writeAscii(8, "WAVE");
  writeAscii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(36, "data");
  view.setUint32(40, sampleCount * 2, true);
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return `data:audio/wav;base64,${window.btoa(binary)}`;
}

const SILENT_WAV_DATA_URI = buildSilentWavDataUri();

const dreamlikeImpulseCache = new WeakMap();

function getDreamlikeVoiceImpulse(ctx) {
  let impulse = dreamlikeImpulseCache.get(ctx);
  if (impulse) return impulse;
  const seconds = 0.9;
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let index = 0; index < length; index += 1) {
      const decay = Math.pow(1 - index / length, 4.4);
      data[index] = (Math.random() * 2 - 1) * decay;
    }
  }
  dreamlikeImpulseCache.set(ctx, impulse);
  return impulse;
}

function sendFeedbackNonBlocking(payload, label = "feedback") {
  api.sendFeedback(payload).catch((error) => {
    console.warn(`[AceLabReact] ${label} failed`, error);
  });
}

// Take a small post-routing sample so subliminal experiments can be level
// matched on the actual browser/device path rather than trusting the authored
// gain alone. This is telemetry for A/B QA, not a brain-state measurement.
function sampleAnalyserLevel(analyser) {
  if (!analyser || typeof analyser.getFloatTimeDomainData !== "function") return null;
  try {
    const data = new Float32Array(Math.max(32, Number(analyser.fftSize) || 256));
    analyser.getFloatTimeDomainData(data);
    let sumSquares = 0;
    let peak = 0;
    for (const sample of data) {
      const value = Number(sample) || 0;
      sumSquares += value * value;
      peak = Math.max(peak, Math.abs(value));
    }
    return {
      rms: Math.sqrt(sumSquares / Math.max(1, data.length)),
      peak,
      sample_count: data.length,
    };
  } catch {
    return null;
  }
}

function isNsfwSessionState(currentState) {
  const channel = String(currentState?.activeTheme || "");
  const route = (currentState?.themes || []).find((theme) => String(theme?.slug || "") === channel);
  return Boolean(
    route?.is_nsfw
    || currentState?.currentTrack?.is_nsfw
    || currentState?.session?.nsfw
    || currentState?.customSessionRequest?.nsfw,
  );
}

function isMainMusicPlaying(currentState, controller) {
  // The backend keeps the primary deck music-only. Use the media element's
  // actual transport state rather than React's eventually-consistent flag so
  // background material can neither outlive nor precede the music.
  return Boolean(currentState?.currentTrack && controller?.isPlaying?.());
}

function resolveLiveAudioScene(currentState, liveBrainwaveScene) {
  const currentFile = String(currentState?.currentTrack?.file || "");
  if (liveBrainwaveScene?.scene && liveBrainwaveScene.trackFile === currentFile) {
    return liveBrainwaveScene.scene;
  }
  return currentState?.currentTrack?.audio_scene || {};
}

function resolveSpokenPlaybackRate(track) {
  const value = Number(track?.playback_rate);
  return Number.isFinite(value) ? Math.max(0.75, Math.min(1.5, value)) : 1;
}

const MAIN_VOICE_GAIN_SCALE = 0.70;

function resolveMainVoiceLevel(audioScene = {}) {
  const programmedLevel = Math.max(0.35, Math.min(0.65, Number(audioScene?.mainVoiceLevel) || 0.5));
  // The foreground guide should sit inside the music rather than above it.
  // Scale the authored level instead of replacing it so channel-specific mix
  // intent survives while the entire main lane becomes 30% quieter.
  return Math.max(0.20, Math.min(0.46, programmedLevel * MAIN_VOICE_GAIN_SCALE));
}

function normalizePreferredVoiceGender(value) {
  const normalized = String(value || "neutral").trim().toLowerCase();
  return ["female", "male", "nonbinary"].includes(normalized) ? normalized : "neutral";
}

function resolvePreferredVoiceGender(currentState = {}, fallback = "neutral") {
  return normalizePreferredVoiceGender(
    currentState?.session?.preferred_voice_gender
    || currentState?.preferences?.preferred_voice_gender
    || fallback,
  );
}

function voiceTrackMatchesPreference(track, preferredVoiceGender) {
  const preferred = normalizePreferredVoiceGender(preferredVoiceGender);
  if (preferred === "neutral") return true;
  return String(track?.speaker_voice_gender || "neutral").trim().toLowerCase() === preferred;
}

function filterSpokenTracksByVoice(tracks, preferredVoiceGender) {
  if (!Array.isArray(tracks)) return [];
  return tracks.filter((track) => (
    !isSessionWelcomeTrack(track)
    && voiceTrackMatchesPreference(track, preferredVoiceGender)
  ));
}

function voiceTrackMayRepeat(track) {
  if (track?.repeat_safe === true || track?.safe_for_repeat === true) return true;
  const policy = String(track?.repeat_policy || "").toLowerCase();
  return ["safe", "repeat_safe", "loopable"].includes(policy);
}

function BrainwaveSlider({ label, value, minimum, maximum, step, unit = "", onChange }) {
  const fieldId = `brainwave-${useId().replace(/:/g, "")}`;
  const boundedValue = Math.max(minimum, Math.min(maximum, Number(value) || 0));
  const ratio = (boundedValue - minimum) / Math.max(Number.EPSILON, maximum - minimum);
  const degrees = -135 + ratio * 270;
  const decimals = step < 0.01 ? 3 : step < 1 ? 2 : 0;
  return (
    <label className="control-knob">
      <span className="control-knob-label">{label}</span>
      <span className="control-knob-dial" style={{ "--knob-turn": `${degrees}deg`, "--knob-fill": `${ratio * 75}%` }}>
        <span className="control-knob-pointer" />
        <input
          id={fieldId}
          name={fieldId}
          aria-label={label}
          type="range"
          min={minimum}
          max={maximum}
          step={step}
          value={boundedValue}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </span>
      <output>{boundedValue.toFixed(decimals)}{unit}</output>
    </label>
  );
}

function ControlBoardSection({ title, enabled, onToggle, accent = "violet", children }) {
  return (
    <section className={`control-board-section ${enabled ? "is-on" : "is-off"}`} data-accent={accent}>
      <header>
        <div><span className="section-led" aria-hidden="true" /><h3>{title}</h3></div>
        <button
          type="button"
          className="section-power"
          aria-label={`${enabled ? "Disable" : "Enable"} ${title}`}
          aria-pressed={enabled}
          onClick={onToggle}
        >
          {enabled ? "ON" : "OFF"}
        </button>
      </header>
      <div className="control-board-bank">{children}</div>
    </section>
  );
}

function BoardSelect({ label, value, onChange, children }) {
  const fieldId = `board-${useId().replace(/:/g, "")}`;
  return (
    <label className="board-select" htmlFor={fieldId}>
      <span>{label}</span>
      <select id={fieldId} name={fieldId} value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
    </label>
  );
}

const INITIAL_STATE = {
  session: { chips: 0 },
  preferences: {},
  hypnosisProtocol: null,
  themes: [],
  personas: [],
  backgroundFiles: [],
  tags: { active: [], all: [] },
  queue: [],
  mainVoiceQueue: [],
  // The singleton opener is a scripted event asset, never a generic voice
  // queue member. Keeping it separate prevents queue refreshes and fallback
  // playback from treating it as reusable prose.
  sessionWelcomeTrack: null,
  sessionScript: null,
  customSessionRequest: null,
  overlayQueue: [],
  queueIndex: -1,
  currentTrack: null,
  featuredMate: null,
  trackHistory: [],
  activeTheme: "all",
  activeThemeLabel: "all",
  contentDepthLabel: "0 min",
  queueCountLabel: "0 tracks",
  sessionLabel: "local",
  status: "Booting",
  gateMessage: "",
  libraryMessage: "",
  isPlaying: false,
  showTosButton: false,
  welcomeVisible: false,
  tipFeedback: "",
  canGoBack: false,
  isBusy: false,
  covertMode: false,
  // Experimental "silent" ultrasonic covert mode. THEATER for covert words
  // (see Black Book §0/§3) — kept only for A/B testing, never the default.
  covertSilentExperiment: false,
};

function App() {
  const [state, setState] = useState(INITIAL_STATE);
  const stateRef = useRef(state);
  const audioRef = useRef(null);
  const mainVoiceAudioRef = useRef(null);
  // Dedicated foreground hypnosis program plus two independent positional
  // whisper lanes. Music remains owned by audioRef.
  const overlayAudioRef = useRef(null);
  const whisperLeftAudioRef = useRef(null);
  const whisperRightAudioRef = useRef(null);
  const subliminalAudioRef = useRef(null);
  const backgroundAudioRef = useRef(null);
  const canvasRef = useRef(null);
  const audioControllerRef = useRef(null);
  const hapticControllerRef = useRef(null);
  const firedOverlayCueKeysRef = useRef(new Set());
  // Which music file the fired/starting cue sets belong to. The cue guard must
  // be cleared ONCE per track, not once per effect run: the scheduling effect
  // re-runs on `isPlaying`/`sessionScript` changes, and clearing the guard on
  // every run made already-played cues re-fire. Because the re-run also used
  // preserveAudibleVoices:true (so the in-flight sentence kept playing), the
  // re-fired cue started ON TOP of it — the "audio is playing over each other"
  // report. See the guard in the scheduling effect.
  const overwatchTrackFileRef = useRef("");
  const firedSessionScriptEventKeysRef = useRef(new Set());
  // Unlike ordinary event keys, this survives queue/script refreshes and track
  // changes. It resets only when the listener explicitly compiles a new
  // session, guaranteeing one opening notice per listening session.
  const sessionWelcomePlaybackGuardRef = useRef(createSessionWelcomePlaybackGuard());
  const mainVoiceGestureStartInFlightRef = useRef(false);
  // A scheduler tick runs four times per second while cue startup can await
  // WebAudio work. Claim cues synchronously so adjacent ticks cannot start the
  // same whisper several times before the first play() promise settles.
  const startingOverlayCueKeysRef = useRef(new Set());
  const overlayScheduleFetchesRef = useRef(new Set());
  const overlayAudioContextRef = useRef(null);
  const brainwaveLabToneRef = useRef(null);
  const spatialTranceEngineRef = useRef(null);
  const overlayLanePrimedRef = useRef({ [LANE_HYPNOSIS_SPOKEN_MAIN]: false, [LANE_OVERLAY]: false, [LANE_WHISPER_LEFT]: false, [LANE_WHISPER_RIGHT]: false, [LANE_SUBLIMINAL_1]: false, [LANE_SUBLIMINAL_2]: false });
  const overlayLanePrimePromisesRef = useRef({});
  // True once the binaural frequency-shifter worklet is registered on the
  // overlay AudioContext. Checked synchronously when lane routing is built.
  const subliminalWorkletReadyRef = useRef(false);
  // Covert Hypnosis Mode engine: continuously rotates spoken subliminal
  // suggestion atoms on the subliminal lane while music plays.
  const covertEngineRef = useRef(null);
  const whisperEarRef = useRef("left");
  const overlayDirectorRef = useRef({ lastStartedByLane: {} });
  const duckStateRef = useRef({
    baseVolume: null,
    laneGains: { [LANE_HYPNOSIS_SPOKEN_MAIN]: null, [LANE_OVERLAY]: null, [LANE_WHISPER_LEFT]: null, [LANE_WHISPER_RIGHT]: null },
    animationFrame: 0,
  });
  const transientStatusRef = useRef({ timer: 0, token: 0 });
  // Track last 20 played DROP file IDs to suppress dupes in queue refreshes.
  // Keep 50 recently-played files so the exclude list stays fresh across longer sessions.
  const recentlyPlayedRef = useRef(/** @type {string[]} */([]));
  const recentlyPlayedOverlayRef = useRef(/** @type {string[]} */([]));
  const playedVoiceFilesRef = useRef(new Set());
  // A thumbs-down evaluates the whole live soundscape. Track voice layers so
  // their votes and replacement demand travel with the music vote.
  const activeOverlayFeedbackRef = useRef({});
  const mainVoiceIndexRef = useRef(0);
  const mainVoicePauseAfterRef = useRef(900);
  const lastMainVoiceActivityRef = useRef(0);
  const mainVoiceFallbackRunRef = useRef({ token: 0, active: false });
  const mainVoiceFallbackIndexRef = useRef(0);
  const mainVoiceFallbackSequenceRef = useRef(0);
  const lastStarvationReportAtRef = useRef(0);
  const countdownGuardRef = useRef({ lastStartedAt: 0, activeGroup: "" });
  // Countdown atoms share one centered serialized lane. This guard prevents
  // independent whisper timers from starting the next number before the prior
  // number's audio has actually ended.
  const countdownPlaybackRef = useRef({ active: false, eventKey: "" });
  // Epoch (ms) when the user first hit play this session — drives arc_stage.
  const sessionStartTimeRef = useRef(/** @type {number|null} */(null));
  const sessionPausedAtRef = useRef(/** @type {number|null} */(null));
  const pausedMediaLanesRef = useRef(/** @type {HTMLAudioElement[]} */([]));
  // Prevents concurrent background queue-refresh fetches.
  const queueRefreshInFlightRef = useRef(false);
  const freshDemandRetryRef = useRef(/** @type {number|null} */(null));
  const libraryRequestIdRef = useRef(0);
  const themeSelectionIdRef = useRef(0);
  // Retry timer for empty-queue state (content being generated).
  const emptyQueueRetryRef = useRef(/** @type {number|null} */(null));
  // Guard against double-fire of onTrackEnded (browser audio glitches).
  const advanceInFlightRef = useRef(false);
  const experimentOutcomeRecordedRef = useRef(false);
  // Prefetch a small runway, not merely the next song. This keeps repeated
  // Skip responsive on mobile connections while avoiding a warehouse download.
  const prefetchAudioRefs = useRef(/** @type {HTMLAudioElement[]} */([]));
  // Subliminal background rotation: shuffled order over ALL background files
  // so every asset gets exposure (ongoing experiment), not one looped file.
  const backgroundRotationRef = useRef({ order: /** @type {string[]} */ ([]), index: -1 });
  const backgroundStartInFlightRef = useRef(false);
  const liveBrainwaveSceneRef = useRef({ trackFile: "", scene: null });
  // Audible scheduled speech uses decoded WebAudio buffers once the Play
  // gesture has resumed the shared context. Android can report play() success
  // for a later HTMLMediaElement yet still produce silence; BufferSource nodes
  // avoid that per-element autoplay failure mode.
  const decodedAudioCacheRef = useRef(new Map());
  const audibleBufferLanesRef = useRef({ [LANE_HYPNOSIS_SPOKEN_MAIN]: null, [LANE_OVERLAY]: null, [LANE_WHISPER_LEFT]: null, [LANE_WHISPER_RIGHT]: null });
  // Hard per-lane rate limits (wall-clock). Belt-and-braces: no cue pathway
  // — stale schedule, cached bundle, resolver bug — may chain-fire a lane.
  const laneCooldownUntilRef = useRef({ [LANE_HYPNOSIS_SPOKEN_MAIN]: 0, [LANE_OVERLAY]: 0, whisper: 0, [LANE_SUBLIMINAL_1]: 0 });
  // Each ear is independent, but remains an occasional atmospheric accent.
  const whisperBudgetRef = useRef({ trackFile: "", left: 0, right: 0 });
  const overlayLaneRefs = useRef({
    [LANE_HYPNOSIS_SPOKEN_MAIN]: { source: null, gain: null, panner: null },
    [LANE_OVERLAY]: { source: null, gain: null, panner: null },
    [LANE_WHISPER_LEFT]: { source: null, gain: null, panner: null },
    [LANE_WHISPER_RIGHT]: { source: null, gain: null, panner: null },
    [LANE_SUBLIMINAL_1]: { source: null, gain: null, panner: null },
    [LANE_SUBLIMINAL_2]: { source: null, gain: null, panner: null },
  });
  const [miniPanelOpen, setMiniPanelOpen] = useState(false);
  const [brainwaveLabOpen, setBrainwaveLabOpen] = useState(false);
  const [brainwaveLabSettings, setBrainwaveLabSettings] = useState(() => loadBrainwaveLab());
  const brainwaveLabSettingsRef = useRef(brainwaveLabSettings);
  const [audibleRhythmOverride, setAudibleRhythmOverride] = useState(null);
  const audibleRhythmOverrideRef = useRef(null);
  const [brainwavePresets, setBrainwavePresets] = useState(() => listBrainwavePresets());
  const [selectedBrainwavePreset, setSelectedBrainwavePreset] = useState("builtin:velvet-descent");
  const [brainwavePresetName, setBrainwavePresetName] = useState("");
  const [brainwavePresetMessage, setBrainwavePresetMessage] = useState("");
  const brainwavePresetImportRef = useRef(null);
  const [matePanelOpen, setMatePanelOpen] = useState(false);
  // Voice audition for the HypnoMate picker: dedicated preview element so a
  // sample never touches the session's playback lanes.
  const sampleAudioRef = useRef(/** @type {HTMLAudioElement|null} */(null));
  const [sampleStatus, setSampleStatus] = useState("idle");
  const [themeSearch, setThemeSearch] = useState("");
  const [sessionBuilderOptions, setSessionBuilderOptions] = useState(null);
  const [sessionBuilderPreferences, setSessionBuilderPreferences] = useState(null);
  const [sessionBuilderOpen, setSessionBuilderOpen] = useState(false);
  const [sessionBuilderBusy, setSessionBuilderBusy] = useState(false);
  const [sessionBuilderError, setSessionBuilderError] = useState("");
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [sessionClock, setSessionClock] = useState(0);
  const [outcomeCheckinOpen, setOutcomeCheckinOpen] = useState(false);
  const [outcomeCheckinBusy, setOutcomeCheckinBusy] = useState(false);
  const [outcomeCheckinMessage, setOutcomeCheckinMessage] = useState("");
  const [detectionCheckinOpen, setDetectionCheckinOpen] = useState(false);
  const [detectionCheckinBusy, setDetectionCheckinBusy] = useState(false);
  const [detectionCheckinMessage, setDetectionCheckinMessage] = useState("");
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletMessage, setWalletMessage] = useState("");
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installPromptVisible, setInstallPromptVisible] = useState(false);
  const [installPromptIsIos, setInstallPromptIsIos] = useState(false);
  const sessionBuilderSaveTimerRef = useRef(null);

  const persistSessionBuilderPreferences = useCallback((preferences) => {
    if (!preferences || typeof preferences !== "object") return;
    const currentSession = stateRef.current.session || {};
    try {
      window.localStorage.setItem(sessionBuilderLocalKey(currentSession), JSON.stringify(preferences));
    } catch {
      // Browser storage is only a fallback; the profile API remains canonical.
    }
    if (!currentSession.authenticated) return;
    if (sessionBuilderSaveTimerRef.current) window.clearTimeout(sessionBuilderSaveTimerRef.current);
    sessionBuilderSaveTimerRef.current = window.setTimeout(() => {
      api.saveSessionBuilderPreferences(preferences).catch((error) => {
        console.warn("[AceLabReact] session preference save failed", error);
      });
    }, 250);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const standalone = Boolean(
      window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone,
    );
    if (standalone) return undefined;
    const visitKey = "hypnomates.pwa-visits";
    const dismissedKey = "hypnomates.pwa-install-dismissed";
    let visits = 0;
    try {
      visits = Number(window.localStorage.getItem(visitKey) || 0) + 1;
      window.localStorage.setItem(visitKey, String(visits));
    } catch {
      visits = 2;
    }
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
    const dismissed = (() => {
      try { return window.localStorage.getItem(dismissedKey) === "1"; } catch { return false; }
    })();
    const showPrompt = () => {
      if (visits >= 2 && !dismissed) setInstallPromptVisible(true);
    };
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setInstallPromptIsIos(false);
      showPrompt();
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((error) => {
        console.debug("[HypnoMates] service worker unavailable", error);
      });
    }
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", () => setInstallPromptVisible(false), { once: true });
    if (isIos && visits >= 2 && !dismissed) {
      setInstallPromptIsIos(true);
      setInstallPromptVisible(true);
    }
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  const dismissInstallPrompt = useCallback(() => {
    setInstallPromptVisible(false);
    try { window.localStorage.setItem("hypnomates.pwa-install-dismissed", "1"); } catch { }
  }, []);

  const installApp = useCallback(async () => {
    if (!installPromptEvent) {
      dismissInstallPrompt();
      return;
    }
    installPromptEvent.prompt();
    try { await installPromptEvent.userChoice; } catch { }
    setInstallPromptEvent(null);
    setInstallPromptVisible(false);
  }, [dismissInstallPrompt, installPromptEvent]);

  const mergeState = useCallback((patch) => {
    const resolvedPatch = typeof patch === "function" ? patch(stateRef.current) : patch;
    if (!resolvedPatch || typeof resolvedPatch !== "object") return;
    stateRef.current = { ...stateRef.current, ...resolvedPatch };
    setState({ ...stateRef.current });
  }, []);

  const patchNestedState = useCallback((key, value) => {
    stateRef.current = {
      ...stateRef.current,
      [key]: {
        ...(stateRef.current[key] || {}),
        ...(value || {}),
      },
    };
    setState({ ...stateRef.current });
  }, []);

  const mergeResolvedOverlaySchedule = useCallback((trackFile, resolvedOverlaySchedule) => {
    if (!trackFile) return;
    const current = stateRef.current;
    const queue = (current.queue || []).map((track) => (
      track?.file === trackFile
        ? { ...track, resolved_overlay_schedule: resolvedOverlaySchedule }
        : track
    ));
    const currentTrack = current.currentTrack?.file === trackFile
      ? { ...current.currentTrack, resolved_overlay_schedule: resolvedOverlaySchedule }
      : current.currentTrack;
    stateRef.current = { ...current, queue, currentTrack };
    setState({ ...stateRef.current });
  }, []);

  const setTransientStatus = useCallback((message, durationMs = 3000) => {
    const token = transientStatusRef.current.token + 1;
    transientStatusRef.current.token = token;
    if (transientStatusRef.current.timer) {
      window.clearTimeout(transientStatusRef.current.timer);
    }
    mergeState({ status: message });
    transientStatusRef.current.timer = window.setTimeout(() => {
      if (transientStatusRef.current.token !== token) return;
      const fallback = stateRef.current.isPlaying ? "Playing" : "Ready";
      mergeState({ status: fallback });
    }, durationMs);
  }, [mergeState]);

  const animateMainDeckVolume = useCallback((targetVolume, durationMs, onComplete) => {
    const mainAudio = audioRef.current;
    if (!mainAudio) return;
    const duckState = duckStateRef.current;
    if (duckState.animationFrame) {
      window.cancelAnimationFrame(duckState.animationFrame);
      duckState.animationFrame = 0;
    }
    const startVolume = Number.isFinite(mainAudio.volume) ? mainAudio.volume : 1;
    const clampedTarget = Math.max(0, Math.min(1, targetVolume));
    const startedAt = performance.now();
    const tick = (now) => {
      const progress = durationMs <= 0 ? 1 : Math.min(1, (now - startedAt) / durationMs);
      mainAudio.volume = startVolume + ((clampedTarget - startVolume) * progress);
      if (progress >= 1) {
        duckState.animationFrame = 0;
        if (onComplete) onComplete();
        return;
      }
      duckState.animationFrame = window.requestAnimationFrame(tick);
    };
    duckState.animationFrame = window.requestAnimationFrame(tick);
  }, []);

  const engageDeckDuck = useCallback((laneName, duckGain) => {
    const mainAudio = audioRef.current;
    if (!mainAudio) return;
    const duckState = duckStateRef.current;
    if (!Number.isFinite(duckState.baseVolume)) {
      duckState.baseVolume = Number.isFinite(mainAudio.volume) ? mainAudio.volume : 1;
    }
    duckState.laneGains[laneName] = Math.max(0, Math.min(1, duckGain));
    const activeGains = Object.values(duckState.laneGains).filter((value) => Number.isFinite(value));
    const gain = activeGains.length ? Math.min(...activeGains) : 1;
    animateMainDeckVolume((duckState.baseVolume ?? 1) * gain, 300);
  }, [animateMainDeckVolume]);

  const releaseDeckDuck = useCallback((laneName) => {
    const mainAudio = audioRef.current;
    if (!mainAudio) return;
    const duckState = duckStateRef.current;
    duckState.laneGains[laneName] = null;
    const baseVolume = Number.isFinite(duckState.baseVolume) ? duckState.baseVolume : (Number.isFinite(mainAudio.volume) ? mainAudio.volume : 1);
    const activeGains = Object.values(duckState.laneGains).filter((value) => Number.isFinite(value));
    if (activeGains.length) {
      animateMainDeckVolume(baseVolume * Math.min(...activeGains), 300);
      return;
    }
    animateMainDeckVolume(baseVolume, 600, () => {
      if (!Object.values(duckState.laneGains).some((value) => Number.isFinite(value))) {
        duckState.baseVolume = null;
      }
    });
  }, [animateMainDeckVolume]);

  const resolveInitialTheme = useCallback((session, themes) => {
    const clientReadyThemes = themes || [];
    const availableThemeSlugs = new Set(clientReadyThemes.map((theme) => theme.slug));
    if (availableThemeSlugs.has("all")) {
      const candidate = session.last_theme || session.last_channel || "all";
      return availableThemeSlugs.has(candidate) ? candidate : "all";
    }
    const candidate = session.last_theme || session.last_channel || "all";
    return availableThemeSlugs.has(candidate) ? candidate : "all";
  }, []);

  const sessionCanAccessContent = useCallback((session) => !!(
    session?.is_paid
    || session?.authenticated
  ), []);

  const buildAccessMessage = useCallback((session) => {
    if (session?.is_paid) return "Session ready.";
    if (!session?.authenticated) return "Welcome. Sign in with Google to continue.";
    return "Session ready.";
  }, []);

  const resolveCurrentTrack = useCallback(({ queue, priorCurrentTrack }) => {
    if (!queue.length) return null;
    if (!priorCurrentTrack?.file) return queue[0];
    return queue.find((track) => track.file === priorCurrentTrack.file) || queue[0];
  }, []);

  const pushTrackHistory = useCallback((history, track) => {
    if (!track?.file) return [...(history || [])];
    const nextHistory = [...(history || [])];
    const last = nextHistory[nextHistory.length - 1];
    if (last?.file === track.file) return nextHistory;
    nextHistory.push(track);
    return nextHistory.slice(-20);
  }, []);

  const rankDislikeAlternatives = useCallback((queue, rejectedTrack, history) => {
    const heard = new Set((history || []).map((track) => String(track?.file || "")));
    const rejectedTags = new Set((rejectedTrack?.tags || []).map((tag) => String(tag).toLowerCase()));
    return [...(queue || [])]
      .filter((track) => track?.file && track.file !== rejectedTrack?.file && !heard.has(track.file))
      .sort((left, right) => {
        const score = (track) => {
          const tags = new Set((track.tags || []).map((tag) => String(tag).toLowerCase()));
          let value = 0;
          if (track.category === rejectedTrack?.category) value += 12;
          if (track.theme_slug && track.theme_slug === rejectedTrack?.theme_slug) value += 9;
          rejectedTags.forEach((tag) => { if (tags.has(tag)) value += 1; });
          if (track.persona_id && track.persona_id !== rejectedTrack?.persona_id) value += 2;
          return value;
        };
        return score(right) - score(left);
      });
  }, []);

  const resolvePersonaArtUrl = useCallback((persona) => {
    const raw = String(persona?.profile_art_path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("/")) return raw;
    const normalized = raw.replace(/\\/g, "/").replace(/^\.?\//, "");
    return `/${normalized}`;
  }, []);

  const describeMateRole = useCallback((track) => {
    if (!track) return "Curating tonight’s experience for you.";
    const category = String(track.category || "").replace(/_/g, " ");
    if (track.category === "induction_start" || track.category === "induction_deepening") {
      return `Guiding your ${category} with a voice meant to take you deeper.`;
    }
    if (track.category === "deepening" || track.category === "affirmation") {
      return `Whispering ${category} cues over the session bed right now.`;
    }
    if (track.category === "intimate_reading" || track.category === "pleasure") {
      return `Curating a ${category} moment built around this fantasy lane.`;
    }
    return `Curating the current ${category || "session"} track for you.`;
  }, []);

  const resolveFeaturedMate = useCallback(({ personas, session, currentTrack, activeTheme }) => {
    const personaList = Array.isArray(personas) ? personas : [];
    const trackPersonaId = currentTrack?.persona_id || "";
    const activePersonaId = session?.active_persona_id || "";
    const persona = personaList.find((entry) => entry.persona_id === trackPersonaId)
      || personaList.find((entry) => entry.persona_id === activePersonaId)
      || personaList[0]
      || null;
    if (!persona) return null;
    const role = trackPersonaId
      ? describeMateRole(currentTrack)
      : `Curating the ${String(activeTheme || "all").replace(/_/g, " ")} channel for you.`;
    return {
      ...persona,
      role,
      signal: "Tips push this Mate higher in the inspired-by content queue.",
      profile_art_url: resolvePersonaArtUrl(persona),
      following: Array.isArray(session?.followed_personas) && session.followed_personas.includes(persona.persona_id),
    };
  }, [describeMateRole, resolvePersonaArtUrl]);

  const buildQueueMessage = useCallback((payload) => {
    const baseMessage = payload.message || payload.production?.message || "Session ready.";
    if (payload.queue_expanded_from_all) {
      return `${baseMessage} Queue widened from all for a livelier stream.`;
    }
    return baseMessage;
  }, []);

  const mergeTrackLists = useCallback((primaryTracks, fallbackTracks) => {
    const merged = [];
    const seen = new Set();
    [...(primaryTracks || []), ...(fallbackTracks || [])].forEach((track) => {
      const file = String(track?.file || "");
      if (!file || seen.has(file)) return;
      seen.add(file);
      merged.push(track);
    });
    return merged;
  }, []);

  const expandThinQueueIfNeeded = useCallback(async (payload, requestedChannel) => {
    const queue = Array.isArray(payload.tracks) ? payload.tracks : [];
    const effectiveChannel = payload.effective_channel || requestedChannel;
    const shouldExpand = effectiveChannel !== "all" && queue.length < 8;
    if (!shouldExpand) return payload;
    const fallbackPayload = await api.fetchLibrary("all", { lite: true });
    if (fallbackPayload.gate === "auth_required") return payload;
    const fallbackTracks = Array.isArray(fallbackPayload.tracks) ? fallbackPayload.tracks : [];
    const mergedTracks = mergeTrackLists(queue, fallbackTracks);
    if (mergedTracks.length <= queue.length) return payload;
    return {
      ...payload,
      tracks: mergedTracks,
      content_minutes: Math.max(payload.content_minutes || 0, fallbackPayload.content_minutes || 0),
      content_minutes_label: fallbackPayload.content_minutes_label || payload.content_minutes_label,
      production: payload.production || fallbackPayload.production,
      queue_expanded_from_all: true,
      queue_expansion_source: "all",
    };
  }, [mergeTrackLists]);

  const updateFeaturedMate = useCallback((updater) => {
    const current = stateRef.current.featuredMate || null;
    if (!current) return;
    mergeState({ featuredMate: updater(current) });
  }, [mergeState]);

  const mergeFeaturedMate = useCallback((personaPayload) => {
    if (!personaPayload?.persona_id) return;
    updateFeaturedMate((current) => ({
      ...current,
      ...personaPayload,
      name: personaPayload.name || current.name,
    }));
  }, [updateFeaturedMate]);

  const clearOverlayLanes = useCallback(({ preserveMainVoice = false, preserveAudibleVoices = false, preserveSubliminal = false } = {}) => {
    const preservedLanes = new Set();
    if (preserveMainVoice || preserveAudibleVoices) preservedLanes.add(LANE_HYPNOSIS_SPOKEN_MAIN);
    if (preserveAudibleVoices) {
      preservedLanes.add(LANE_OVERLAY);
      preservedLanes.add(LANE_WHISPER_LEFT);
      preservedLanes.add(LANE_WHISPER_RIGHT);
      Object.keys(audibleBufferLanesRef.current)
        .filter((laneName) => isEncounterLane(laneName))
        .forEach((laneName) => preservedLanes.add(laneName));
    }
    if (preserveSubliminal) preservedLanes.add(LANE_SUBLIMINAL_1);
    const audios = [
      [LANE_HYPNOSIS_SPOKEN_MAIN, mainVoiceAudioRef.current],
      [LANE_OVERLAY, overlayAudioRef.current],
      [LANE_WHISPER_LEFT, whisperLeftAudioRef.current],
      [LANE_WHISPER_RIGHT, whisperRightAudioRef.current],
      [LANE_SUBLIMINAL_1, subliminalAudioRef.current],
    ];
    audios.forEach(([laneName, audio]) => {
      if (preservedLanes.has(laneName)) return;
      if (!audio) return;
      try {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      } catch {
        // ignore media cleanup errors
      }
    });
    Object.entries(audibleBufferLanesRef.current).forEach(([laneName, active]) => {
      if (preservedLanes.has(laneName)) return;
      try { active?.source?.stop?.(); } catch { /* already stopped */ }
      audibleBufferLanesRef.current[laneName] = null;
    });
    [LANE_HYPNOSIS_SPOKEN_MAIN, LANE_OVERLAY, LANE_WHISPER_LEFT, LANE_WHISPER_RIGHT, LANE_SUBLIMINAL_1].forEach((laneName) => {
      if (preservedLanes.has(laneName)) return;
      const lane = overlayLaneRefs.current[laneName];
      if (lane?.motionTimer) {
        window.clearInterval(lane.motionTimer);
        lane.motionTimer = null;
      }
    });
    if (!preservedLanes.has(LANE_HYPNOSIS_SPOKEN_MAIN)) releaseDeckDuck(LANE_HYPNOSIS_SPOKEN_MAIN);
    if (!preservedLanes.has(LANE_OVERLAY)) releaseDeckDuck(LANE_OVERLAY);
    if (!preservedLanes.has(LANE_WHISPER_LEFT)) releaseDeckDuck(LANE_WHISPER_LEFT);
    if (!preservedLanes.has(LANE_WHISPER_RIGHT)) releaseDeckDuck(LANE_WHISPER_RIGHT);
    releaseDeckDuck(LANE_SUBLIMINAL_1);
    activeOverlayFeedbackRef.current = Object.fromEntries(
      Object.entries(activeOverlayFeedbackRef.current).filter(([laneName]) => preservedLanes.has(laneName)),
    );
  }, [releaseDeckDuck]);

  const ensureOverlayContextFromGesture = useCallback(async () => {
    if (typeof window === "undefined") return null;
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return null;
    if (!overlayAudioContextRef.current) {
      overlayAudioContextRef.current = new AudioContextCtor();
    }
    // Android Chrome may consume the transient activation while the worklet
    // module is loading. Resume the context immediately in the Play call stack
    // so every later MediaElementSource is connected to a running graph.
    const transportPaused = Boolean(
      sessionPausedAtRef.current
      && !audioControllerRef.current?.isPlaying?.(),
    );
    if (overlayAudioContextRef.current.state === "suspended" && !transportPaused) {
      try {
        await overlayAudioContextRef.current.resume();
      } catch {
        // gesture resume is best-effort only
      }
    }
    // Preload the binaural shifter worklet inside the gesture so it is
    // registered long before the first subliminal cue needs it.
    if (!subliminalWorkletReadyRef.current) {
      subliminalWorkletReadyRef.current = await ensureSubliminalWorklet(overlayAudioContextRef.current);
    }
    return overlayAudioContextRef.current;
  }, []);

  const primeOverlayLane = useCallback((laneName, laneAudio) => {
    if (!laneAudio || overlayLanePrimedRef.current[laneName]) return Promise.resolve(true);
    if (overlayLanePrimePromisesRef.current[laneName]) {
      return overlayLanePrimePromisesRef.current[laneName];
    }
    const originalMuted = laneAudio.muted;
    const originalVolume = laneAudio.volume;
    const originalSrc = laneAudio.getAttribute("src") || "";
    try {
      if (!originalSrc && !laneAudio.currentSrc) {
        laneAudio.src = SILENT_WAV_DATA_URI;
        laneAudio.load();
      }
      // The source itself is zero-PCM, so this remains silent without using
      // the muted flag. Android may treat muted autoplay as a different
      // permission class and still reject the later audible voice clip.
      laneAudio.muted = false;
      laneAudio.volume = 1;
      const primeAttempt = laneAudio.play();
      // Do not pause/load immediately: that aborts play() before Android grants
      // this particular HTMLMediaElement future playback permission. Clean up
      // only after the silent source actually starts.
      const lanePrimePromise = Promise.resolve(primeAttempt).then(() => {
        overlayLanePrimedRef.current[laneName] = true;
        laneAudio.pause();
        laneAudio.currentTime = 0;
        return true;
      }).catch((error) => {
        overlayLanePrimedRef.current[laneName] = false;
        console.warn(`[AceLabReact] ${laneName} lane prime failed`, error);
        return false;
      }).finally(() => {
        laneAudio.muted = originalMuted;
        laneAudio.volume = originalVolume;
        if (!originalSrc) {
          laneAudio.removeAttribute("src");
          laneAudio.load();
        }
        delete overlayLanePrimePromisesRef.current[laneName];
      });
      overlayLanePrimePromisesRef.current[laneName] = lanePrimePromise;
      return lanePrimePromise;
    } catch (error) {
      overlayLanePrimedRef.current[laneName] = false;
      console.warn(`[AceLabReact] ${laneName} lane prime failed`, error);
      laneAudio.muted = originalMuted;
      laneAudio.volume = originalVolume;
      if (!originalSrc) {
        laneAudio.removeAttribute("src");
        laneAudio.load();
      }
      return Promise.resolve(false);
    }
  }, []);

  const playDecodedAudibleLane = useCallback(async ({ laneName, track, volume = 1, pan = 0, spatial = null, onEnded = null }) => {
    const ctx = overlayAudioContextRef.current;
    if (!ctx || !track?.audio_src || isSubliminalLane(laneName)) return null;
    if (audibleBufferLanesRef.current[laneName]?.source) return null;
    try {
      if (ctx.state === "suspended") await ctx.resume();
      let decoded = decodedAudioCacheRef.current.get(track.audio_src);
      if (!decoded) {
        decoded = fetch(track.audio_src, { credentials: "same-origin", cache: "force-cache" })
          .then((response) => {
            if (!response.ok) throw new Error(`voice fetch ${response.status}`);
            return response.arrayBuffer();
          })
          .then((bytes) => decodeSpeechAudio(ctx, bytes));
        decodedAudioCacheRef.current.set(track.audio_src, decoded);
        if (decodedAudioCacheRef.current.size > 24) {
          const oldest = decodedAudioCacheRef.current.keys().next().value;
          decodedAudioCacheRef.current.delete(oldest);
        }
      }
      const buffer = await decoded;
      if (!buffer || audibleBufferLanesRef.current[laneName]?.source) return null;
      const source = ctx.createBufferSource();
      const gain = ctx.createGain();
      const spatialProfile = spatial && typeof spatial === "object" ? spatial : null;
      const whisperEnvelope = laneName === LANE_WHISPER_LEFT || laneName === LANE_WHISPER_RIGHT;
      const mainVoiceEnvelope = laneName === LANE_HYPNOSIS_SPOKEN_MAIN;
      const spectacleVoice = Boolean(
        mainVoiceEnvelope
        && brainwaveLabSettingsRef.current?.spatialSpectacleEnabled
        && brainwaveLabSettingsRef.current?.spatialSpiralEnabled
      );
      const usesHrtf = Boolean((spatialProfile || spectacleVoice) && typeof ctx.createPanner === "function");
      const panner = usesHrtf
        ? ctx.createPanner()
        : typeof ctx.createStereoPanner === "function"
          ? ctx.createStereoPanner()
          : null;
      const highpass = ctx.createBiquadFilter();
      const lowpass = ctx.createBiquadFilter();
      const compressor = ctx.createDynamicsCompressor();
      const convolver = ctx.createConvolver();
      const wetGain = ctx.createGain();
      let echoDelay = null;
      let echoSend = null;
      let echoFeedback = null;
      let whisperMakeup = null;
      source.buffer = buffer;
      source.loop = Boolean(spatialProfile?.loop);
      const authoredGain = Math.max(0, Math.min(1.35, Number(volume) || 0));
      const targetGain = whisperEnvelope
        ? resolveWhisperNormalizedGain(authoredGain, buffer, track)
        : authoredGain;
      const startAt = ctx.currentTime;
      highpass.type = "highpass";
      highpass.frequency.value = whisperEnvelope ? 105 : mainVoiceEnvelope ? 82 : 72;
      highpass.Q.value = 0.55;
      lowpass.type = "lowpass";
      lowpass.frequency.value = whisperEnvelope ? 5200 : mainVoiceEnvelope ? 5800 : 6800;
      lowpass.Q.value = 0.45;
      if (spatialProfile) {
        // Neighbouring-car encounters travel through distance, automotive
        // glass and steel, then through partly open windows. Keep the content
        // muffled but locatable instead of turning it into close-mic speech.
        lowpass.frequency.value = Math.max(
          900,
          Math.min(4200, Number(spatialProfile.occlusion_lowpass_hz) || 2100),
        );
        highpass.frequency.value = 115;
      }
      compressor.threshold.value = -27;
      compressor.knee.value = 14;
      compressor.ratio.value = whisperEnvelope ? 2.4 : 1.9;
      compressor.attack.value = 0.028;
      compressor.release.value = 0.38;
      convolver.buffer = getDreamlikeVoiceImpulse(ctx);
      wetGain.gain.value = whisperEnvelope ? 0.105 : mainVoiceEnvelope ? 0.04 : 0.065;
      if (spatialProfile) wetGain.gain.value = 0.018;
      if (whisperEnvelope) {
        whisperMakeup = ctx.createGain();
        whisperMakeup.gain.value = WHISPER_MAKEUP_GAIN;
        // Keep the intimate fade, but never spend a quarter of a short atom
        // fading in or the last 1.5 seconds fading out. Those envelopes were
        // swallowing first and final words and sounded like truncated files.
        const attackSeconds = Math.min(0.18, Math.max(0.045, buffer.duration * 0.06));
        const releaseSeconds = Math.min(0.08, Math.max(0.025, buffer.duration * 0.02));
        gain.gain.setValueAtTime(0.001, startAt);
        gain.gain.linearRampToValueAtTime(targetGain, startAt + attackSeconds);
        if (buffer.duration > attackSeconds + releaseSeconds + 0.05) {
          const fadeStart = startAt + buffer.duration - releaseSeconds;
          gain.gain.setValueAtTime(targetGain, fadeStart);
          gain.gain.exponentialRampToValueAtTime(0.001, startAt + buffer.duration);
        }
      } else {
        gain.gain.setValueAtTime(0.001, startAt);
        gain.gain.linearRampToValueAtTime(targetGain, startAt + Math.min(0.12, buffer.duration * 0.04));
        if (buffer.duration > 1.1) {
          const releaseSeconds = Math.min(0.06, buffer.duration * 0.015);
          const fadeStart = startAt + buffer.duration - releaseSeconds;
          gain.gain.setValueAtTime(targetGain, fadeStart);
          gain.gain.exponentialRampToValueAtTime(0.001, startAt + buffer.duration);
        }
      }
      if (usesHrtf && panner) {
        panner.panningModel = "HRTF";
        panner.distanceModel = "inverse";
        panner.refDistance = 1;
        panner.maxDistance = 12;
        panner.rolloffFactor = 0.78;
        panner.positionX.value = Number(spatialProfile?.x_m) || 0;
        panner.positionY.value = Number(spatialProfile?.y_m) || (spectacleVoice ? 0.12 : 0);
        panner.positionZ.value = Number(spatialProfile?.z_m) || (spectacleVoice ? -1.05 : -3.05);
      } else if (panner) {
        panner.pan.value = Math.max(-1, Math.min(1, Number(pan) || 0));
      }
      source.connect(highpass);
      highpass.connect(lowpass);
      lowpass.connect(compressor);
      compressor.connect(whisperMakeup || gain);
      if (whisperMakeup) whisperMakeup.connect(gain);
      if (panner) {
        gain.connect(panner);
        panner.connect(ctx.destination);
        panner.connect(wetGain);
      } else {
        gain.connect(ctx.destination);
        gain.connect(wetGain);
      }
      wetGain.connect(convolver);
      convolver.connect(ctx.destination);
      if (mainVoiceEnvelope && typeof ctx.createDelay === "function") {
        // A nearly subliminal single-repeat shimmer: enough spatial delay to
        // feel dreamlike on headphones, far below the level that blurs words.
        echoDelay = ctx.createDelay(0.35);
        echoDelay.delayTime.value = 0.13;
        echoSend = ctx.createGain();
        echoSend.gain.value = 0.012;
        echoFeedback = ctx.createGain();
        echoFeedback.gain.value = 0.02;
        const echoInput = panner || gain;
        echoInput.connect(echoSend);
        echoSend.connect(echoDelay);
        echoDelay.connect(ctx.destination);
        echoDelay.connect(echoFeedback);
        echoFeedback.connect(echoDelay);
      }
      const active = {
        source, gain, panner, highpass, lowpass, compressor, convolver, wetGain,
        echoDelay, echoSend, echoFeedback, trackFile: String(track.file || ""),
        motionTimer: null, motionPhase: Math.random() * Math.PI * 2,
      };
      audibleBufferLanesRef.current[laneName] = active;
      source.onended = () => {
        if (audibleBufferLanesRef.current[laneName] === active) {
          if (active.motionTimer) window.clearInterval(active.motionTimer);
          audibleBufferLanesRef.current[laneName] = null;
        }
        try { onEnded?.(); } catch { /* playback cleanup is best-effort */ }
      };
      source.start();
      return active;
    } catch (error) {
      decodedAudioCacheRef.current.delete(track.audio_src);
      console.warn(`[AceLabReact] ${laneName} decoded voice failed`, error);
      return null;
    }
  }, []);

  const buildIntimateReverbImpulse = useCallback((ctx, seconds = 0.7) => {
    // Small synthesized room: exponentially decaying noise. Gives whispered
    // overlays a sense of shared physical space without loading an IR file.
    const rate = ctx.sampleRate;
    const length = Math.max(1, Math.floor(rate * seconds));
    const impulse = ctx.createBuffer(2, length, rate);
    for (let channel = 0; channel < 2; channel += 1) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 3.2);
      }
    }
    return impulse;
  }, []);

  const ensureOverlayLaneRouting = useCallback((laneName) => {
    if (typeof window === "undefined") return null;
    const laneAudio = laneName === LANE_HYPNOSIS_SPOKEN_MAIN
      ? mainVoiceAudioRef.current
      : laneName === LANE_WHISPER_LEFT
        ? whisperLeftAudioRef.current
        : laneName === LANE_WHISPER_RIGHT
          ? whisperRightAudioRef.current
          : laneName === LANE_SUBLIMINAL_2
            ? backgroundAudioRef.current
            : laneName === LANE_SUBLIMINAL_1
              ? subliminalAudioRef.current
              : overlayAudioRef.current;
    if (!laneAudio) return null;
    // Audible speech deliberately stays on the native HTMLMediaElement path.
    // Android has repeatedly demonstrated that routing these later-starting
    // elements through MediaElementSource can produce a playing-but-silent
    // graph. Subliminal lanes still require WebAudio frequency processing;
    // main speech, overlays, and whispers prioritize guaranteed audibility.
    if (!isSubliminalLane(laneName)) return null;
    const ctx = overlayAudioContextRef.current;
    if (!ctx) return null;
    const channelMode = resolveSubliminalChannelMode({
      channelCount: ctx.destination?.channelCount,
      maxChannelCount: ctx.destination?.maxChannelCount,
    });
    const lane = overlayLaneRefs.current[laneName];
    if (lane?.source) {
      lane.channelMode = channelMode;
      return lane;
    }
    try {
      const source = ctx.createMediaElementSource(laneAudio);
      const gain = ctx.createGain();
      let highpass = null;
      let lowpass = null;
      let presence = null;
      let compressor = null;
      let limiter = null;
      let postGainLimiter = null;
      let peakFence = null;
      let shifter = null;
      let analyser = null;
      let subBassLowpass = null;
      let inputNode = source;
      if (isSubliminalLane(laneName) && subliminalWorkletReadyRef.current) {
        // Binaural brain-sync chain. The voice is confined to the speech
        // band so it tucks under the music bed's spectrum (masking does the
        // "subliminal" work, not just low gain), then split into two ears
        // with a constant ∓binHz/2 frequency offset. The brainstem resolves
        // the interaural difference as a beat at exactly binHz, so the
        // entrainment signal rides on the suggestion itself. No HRTF panner
        // here — spatializing would remix the ears and destroy the beat.
        shifter = createSubliminalShifterNode(ctx);
        if (shifter) {
          highpass = ctx.createBiquadFilter();
          highpass.type = "highpass";
          highpass.frequency.value = 240;
          highpass.Q.value = 0.7;
          lowpass = ctx.createBiquadFilter();
          lowpass.type = "lowpass";
          lowpass.frequency.value = 2000;
          lowpass.Q.value = 0.7;
          // Heavy compression BEFORE the level drop: subliminal material
          // (moans especially) swells, and peaks are exactly what breach the
          // music mask and become consciously audible. Flattening dynamics
          // lets the whole clip sit at a constant just-below-threshold level.
          compressor = ctx.createDynamicsCompressor();
          compressor.threshold.value = -42;
          compressor.knee.value = 12;
          compressor.ratio.value = 8;
          compressor.attack.value = 0.004;
          compressor.release.value = 0.35;
          source.connect(highpass);
          highpass.connect(lowpass);
          lowpass.connect(compressor);
          compressor.connect(shifter);
          // Post-shift sub-bass lowpass: after the shifter brings the moan
          // formants down into the felt band, this strips everything above
          // ~200 Hz so only the sub-bass rumble remains — the recognizable
          // sex-sound content is gone. This is what makes the sex bed FELT,
          // not heard (Director, 2026-08-19).
          const subBassLowpass = ctx.createBiquadFilter();
          subBassLowpass.type = "lowpass";
          subBassLowpass.frequency.value = 200;
          subBassLowpass.Q.value = 0.7;
          shifter.connect(subBassLowpass);
          inputNode = subBassLowpass;
          // Bluetooth playback can introduce codec overs and speaker
          // non-linearities. Apply a second, attenuation-only peak fence
          // after the lane gain so the transformed cue cannot punch above
          // the intended low-level mix ceiling. This is a delivery contract,
          // not a guarantee that every listener will perceive it subliminally.
          postGainLimiter = ctx.createDynamicsCompressor();
          postGainLimiter.threshold.value = -26;
          postGainLimiter.knee.value = 0;
          postGainLimiter.ratio.value = 20;
          postGainLimiter.attack.value = 0.001;
          postGainLimiter.release.value = 0.12;
          // A compressor is program-dependent and can overshoot briefly.
          // Follow it with an attenuation-only sample fence. The curve is
          // identity below the ceiling and clips only excursions above it,
          // so normal low-level material is not raised or reshaped.
          if (typeof ctx.createWaveShaper === "function") {
            peakFence = ctx.createWaveShaper();
            const peakCurve = new Float32Array(2049);
            for (let index = 0; index < peakCurve.length; index += 1) {
              const sample = (index * 2) / (peakCurve.length - 1) - 1;
              peakCurve[index] = Math.max(-SUBLIMINAL_GAIN_CEILING, Math.min(SUBLIMINAL_GAIN_CEILING, sample));
            }
            peakFence.curve = peakCurve;
            peakFence.oversample = "4x";
          }
        }
      }
      if (laneName === LANE_WHISPER_LEFT || laneName === LANE_WHISPER_RIGHT) {
        // Intelligibility chain: remove rumble, recover consonant presence,
        // compress breath-to-word variation, then catch peaks. This makes the
        // secret-in-the-ear delivery clearer without merely making vowels loud.
        highpass = ctx.createBiquadFilter();
        highpass.type = "highpass";
        highpass.frequency.value = 105;
        highpass.Q.value = 0.7;
        presence = ctx.createBiquadFilter();
        presence.type = "peaking";
        presence.frequency.value = 3200;
        presence.Q.value = 0.9;
        presence.gain.value = 3.5;
        compressor = ctx.createDynamicsCompressor();
        compressor.threshold.value = -30;
        compressor.knee.value = 18;
        compressor.ratio.value = 3.2;
        compressor.attack.value = 0.012;
        compressor.release.value = 0.22;
        limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = -3;
        limiter.knee.value = 0;
        limiter.ratio.value = 20;
        limiter.attack.value = 0.002;
        limiter.release.value = 0.08;
        const whisperMakeup = ctx.createGain();
        whisperMakeup.gain.value = WHISPER_MAKEUP_GAIN;
        source.connect(highpass);
        highpass.connect(presence);
        presence.connect(compressor);
        compressor.connect(limiter);
        limiter.connect(whisperMakeup);
        inputNode = whisperMakeup;
      }
      // True positional audio: HRTF panning externalizes the voice so it
      // reads as "someone in the room" on headphones/VR rather than an
      // in-head stereo pan. Falls back to StereoPanner when unavailable.
      let panner = null;
      let stereoPanner = null;
      if (shifter) {
        // Binaural subliminal owns its own stereo image; bypass spatial nodes.
      } else if (typeof ctx.createPanner === "function") {
        panner = ctx.createPanner();
        panner.panningModel = "HRTF";
        panner.distanceModel = "inverse";
        panner.refDistance = 0.5;
        panner.maxDistance = 8;
        panner.rolloffFactor = 1.1;
        panner.positionX.value = 0;
        panner.positionY.value = 0;
        panner.positionZ.value = -0.6;
      } else if (typeof ctx.createStereoPanner === "function") {
        stereoPanner = ctx.createStereoPanner();
      }
      inputNode.connect(gain);
      if (postGainLimiter) gain.connect(postGainLimiter);
      if (peakFence) (postGainLimiter || gain).connect(peakFence);
      const meteredNode = peakFence || postGainLimiter || gain;
      if (isSubliminalLane(laneName) && typeof ctx.createAnalyser === "function") {
        analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.72;
        meteredNode.connect(analyser);
      }
      const outputNode = analyser || meteredNode;
      const spatialNode = panner || stereoPanner;
      if (spatialNode) {
        outputNode.connect(spatialNode);
        spatialNode.connect(ctx.destination);
      } else {
        outputNode.connect(ctx.destination);
      }
      // Intimate-space reverb send for the whisper lane only — kept quiet so
      // it adds presence, not wash.
      let reverbSend = null;
      if ((laneName === LANE_WHISPER_LEFT || laneName === LANE_WHISPER_RIGHT) && spatialNode) {
        try {
          const convolver = ctx.createConvolver();
          convolver.buffer = buildIntimateReverbImpulse(ctx);
          reverbSend = ctx.createGain();
          reverbSend.gain.value = 0.16;
          spatialNode.connect(reverbSend);
          reverbSend.connect(convolver);
          convolver.connect(ctx.destination);
        } catch {
          reverbSend = null;
        }
      }
      overlayLaneRefs.current[laneName] = {
        source,
        gain,
        panner,
        stereoPanner,
        reverbSend,
        highpass,
        lowpass,
        subBassLowpass,
        presence,
        compressor,
        limiter,
        postGainLimiter,
        peakFence,
        shifter,
        channelMode,
        analyser,
        motionTimer: null,
        motionPhase: Math.random() * Math.PI * 2,
      };
      return overlayLaneRefs.current[laneName];
    } catch {
      return null;
    }
  }, [buildIntimateReverbImpulse]);

  const applyBrainwaveLabNow = useCallback(async (requested) => {
    const settings = normalizeBrainwaveLab(requested);
    brainwaveLabSettingsRef.current = settings;
    const ctx = await ensureOverlayContextFromGesture();
    if (ctx) {
      const baseScene = resolveLiveAudioScene(stateRef.current, liveBrainwaveSceneRef.current);
      // A protocol condition is an authored experimental assignment. Keep the
      // user's Lab controls in state for the next ordinary scene, but do not
      // let those global controls contaminate a locked condition while it is
      // playing.
      const effectiveSettings = resolveBrainwaveLabPlaybackSettings(baseScene, settings);
      const toneSettings = { ...effectiveSettings };
      if (!brainwaveLabToneRef.current) brainwaveLabToneRef.current = new BrainwaveLabTone(ctx);
      if (effectiveSettings.enabled && effectiveSettings.toneEnabled) {
        brainwaveLabToneRef.current.start(toneSettings);
      } else {
        brainwaveLabToneRef.current.apply(toneSettings);
      }
      const spatialSettings = {
        enabled: effectiveSettings.enabled
          && effectiveSettings.mixEnabled
          && effectiveSettings.spatialEnabled
          && effectiveSettings.spatialSpectacleEnabled,
        intensity: effectiveSettings.spatialSpectacleIntensity,
        spiralEnabled: effectiveSettings.spatialSpiralEnabled,
        spiralSeconds: effectiveSettings.spatialSpiralSeconds,
        spiralRadius: effectiveSettings.spatialSpiralRadius,
        spiralDescent: effectiveSettings.spatialSpiralDescent,
        staircaseEnabled: effectiveSettings.spatialStaircaseEnabled,
        staircaseLevel: effectiveSettings.spatialStaircaseLevel,
        staircaseSeconds: effectiveSettings.spatialStaircaseSeconds,
        vortexEnabled: effectiveSettings.spatialVortexEnabled,
        vortexLevel: effectiveSettings.spatialVortexLevel,
        breathRoomDepth: effectiveSettings.spatialBreathRoomDepth,
        trapdoorEnabled: effectiveSettings.spatialTrapdoorEnabled,
        trapdoorLevel: effectiveSettings.spatialTrapdoorLevel,
        trapdoorIntervalSeconds: effectiveSettings.spatialTrapdoorIntervalSeconds,
        breathInSeconds: effectiveSettings.breathInSeconds,
        breathOutSeconds: effectiveSettings.breathOutSeconds,
      };
      if (!spatialTranceEngineRef.current) spatialTranceEngineRef.current = new SpatialTranceEngine(ctx);
      if (spatialSettings.enabled) await spatialTranceEngineRef.current.start(spatialSettings);
      else spatialTranceEngineRef.current.apply(spatialSettings);
      const now = ctx.currentTime || 0;
      const scene = brainwaveLabScene(
        baseScene,
        settings,
        sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0,
      );
      audioControllerRef.current?.applyAudioScene?.(scene);
      audioControllerRef.current?.applyBrainwaveTarget?.(scene.targetBrainwaveHz ?? scene.binHz ?? settings.beatHz);
      // Board edits must update the actual live shifter nodes, not only React
      // state. Create both processed lanes here when the worklet is ready;
      // previously this path only updated already-created refs, so changing a
      // board control before the first subliminal cue appeared to do nothing.
      const subliminalRouting = subliminalWorkletReadyRef.current
        ? ensureOverlayLaneRouting(LANE_SUBLIMINAL_1)
        : null;
      const backgroundRouting = subliminalWorkletReadyRef.current
        ? ensureOverlayLaneRouting(LANE_SUBLIMINAL_2)
        : null;
      applySubliminalBrainwaveScene(subliminalRouting, scene, now);
      applySubliminalBrainwaveScene(
        backgroundRouting,
        scene,
        now,
        { background: true, carrierShiftHz: resolveSubliminalBackgroundShiftHz({ mode: "dark" }) },
      );
    }
    return settings;
  }, [ensureOverlayContextFromGesture, ensureOverlayLaneRouting]);

  const updateBrainwaveLab = useCallback((patch) => {
    const next = saveBrainwaveLab({
      ...brainwaveLabSettingsRef.current,
      ...patch,
    });
    brainwaveLabSettingsRef.current = next;
    setBrainwaveLabSettings(next);
    applyBrainwaveLabNow(next).catch(() => { });
  }, [applyBrainwaveLabNow]);

  const toggleAudibleRhythm = useCallback(() => {
    const scene = stateRef.current.currentTrack?.audio_scene || {};
    const available = scene.audibleRhythmEnabled === true;
    if (!available) return;
    const next = audibleRhythmOverrideRef.current === false;
    audibleRhythmOverrideRef.current = next;
    setAudibleRhythmOverride(next);
    audioControllerRef.current?.setAudibleRhythmEnabled?.(next);
  }, []);

  const refreshBrainwavePresets = useCallback(() => {
    setBrainwavePresets(listBrainwavePresets());
  }, []);

  const loadSelectedBrainwavePreset = useCallback(() => {
    const preset = brainwavePresets.find((entry) => entry.id === selectedBrainwavePreset);
    if (!preset) {
      setBrainwavePresetMessage("Preset not found.");
      return;
    }
    markBrainwavePresetUsed(preset.id);
    const next = saveBrainwaveLab(preset.settings);
    brainwaveLabSettingsRef.current = next;
    setBrainwaveLabSettings(next);
    setBrainwavePresetName(preset.name);
    setBrainwavePresetMessage(`Loaded ${preset.name}.`);
    applyBrainwaveLabNow(next).catch(() => { });
    refreshBrainwavePresets();
  }, [applyBrainwaveLabNow, brainwavePresets, refreshBrainwavePresets, selectedBrainwavePreset]);

  const saveCurrentBrainwavePreset = useCallback(() => {
    try {
      const preset = saveBrainwavePreset(brainwavePresetName, brainwaveLabSettingsRef.current);
      refreshBrainwavePresets();
      setSelectedBrainwavePreset(preset.id);
      setBrainwavePresetMessage(`Saved ${preset.name}.`);
    } catch (error) {
      setBrainwavePresetMessage(String(error?.message || error));
    }
  }, [brainwavePresetName, refreshBrainwavePresets]);

  const deleteSelectedBrainwavePreset = useCallback(() => {
    if (!selectedBrainwavePreset.startsWith("custom:")) {
      setBrainwavePresetMessage("Built-in presets cannot be deleted.");
      return;
    }
    if (deleteBrainwavePreset(selectedBrainwavePreset)) {
      refreshBrainwavePresets();
      setSelectedBrainwavePreset("builtin:velvet-descent");
      setBrainwavePresetMessage("Custom preset deleted.");
    }
  }, [refreshBrainwavePresets, selectedBrainwavePreset]);

  const exportCurrentBrainwavePreset = useCallback(() => {
    const name = brainwavePresetName.trim() || "AceLab Control Board";
    const blob = new Blob([serializeBrainwavePreset(name, brainwaveLabSettingsRef.current)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "acelab-preset"}.brainwave.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setBrainwavePresetMessage(`Exported ${name}.`);
  }, [brainwavePresetName]);

  const importBrainwavePresetFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const parsed = parseBrainwavePreset(await file.text());
      const preset = saveBrainwavePreset(parsed.name, parsed.settings);
      refreshBrainwavePresets();
      setSelectedBrainwavePreset(preset.id);
      setBrainwavePresetName(preset.name);
      const next = saveBrainwaveLab(preset.settings);
      brainwaveLabSettingsRef.current = next;
      setBrainwaveLabSettings(next);
      applyBrainwaveLabNow(next).catch(() => { });
      setBrainwavePresetMessage(`Imported and loaded ${preset.name}.`);
    } catch (error) {
      setBrainwavePresetMessage(String(error?.message || error));
    }
  }, [applyBrainwaveLabNow, refreshBrainwavePresets]);

  useEffect(() => () => {
    brainwaveLabToneRef.current?.stop?.();
    spatialTranceEngineRef.current?.destroy?.();
  }, []);

  const startMainVoiceFromGesture = useCallback((queue = [], sessionScript = null, sessionWelcomeTrack = null) => {
    const laneAudio = mainVoiceAudioRef.current;
    const available = Array.isArray(queue)
      ? queue.filter((track) => Boolean(track?.audio_src))
      : [];
    const welcomeSource = sessionWelcomeTrack || stateRef.current.sessionWelcomeTrack || null;
    const playableWelcome = welcomeSource?.audio_src ? welcomeSource : null;
    if (!laneAudio || (!available.length && !playableWelcome)) return Promise.resolve(false);

    // Idempotency guard: if the main voice lane is ALREADY actively playing
    // (either the decoded WebAudio buffer lane or the native media element),
    // do not start it again. This prevents the "main voice plays multiple
    // times at once" bug when Play fires the gesture handler AND the
    // continuous-main runner for the same first clip.
    const decodedActive = Boolean(audibleBufferLanesRef.current[LANE_HYPNOSIS_SPOKEN_MAIN]?.source);
    const nativeActive = Boolean(
      laneAudio
      && !laneAudio.paused
      && laneAudio.currentSrc
      && laneAudio.currentSrc !== SILENT_WAV_DATA_URI,
    );
    if (decodedActive || nativeActive) {
      return Promise.resolve(true);
    }

    const firstScriptEvent = Array.isArray(sessionScript?.main_voice)
      ? sessionScript.main_voice[0]
      : null;
    const welcomeTrackIndex = playableWelcome
      ? available.findIndex((track) => track.file === playableWelcome.file)
      : -1;
    // Legacy/partially generated payloads may have the singleton opener in
    // the queue without a compiled script event. Give it the same guard as a
    // scripted opener so a later queue refresh cannot start it again.
    const syntheticWelcomeEvent = !firstScriptEvent
      && Boolean(playableWelcome)
      && sessionWelcomePlaybackGuardRef.current.status() === "idle"
      ? {
        event_id: "session-welcome",
        role: "session_welcome",
        file: playableWelcome.file,
      }
      : null;
    const openingEvent = firstScriptEvent || syntheticWelcomeEvent;
    const scriptedIndex = firstScriptEvent?.file
      ? available.findIndex((track) => track.file === firstScriptEvent.file)
      : -1;
    const scriptedWelcomeAvailable = isSessionWelcomeEvent(firstScriptEvent)
      && playableWelcome
      && (!firstScriptEvent.file || firstScriptEvent.file === playableWelcome.file);
    // The welcome is an authored singleton, not a generic first voice clip.
    // If the compiled score names it but the voice-filtered queue cannot
    // supply that exact asset, do not fall back to another paragraph here and
    // then let the script runner play the welcome as well.
    if (isSessionWelcomeEvent(firstScriptEvent) && scriptedIndex < 0 && !scriptedWelcomeAvailable) {
      return Promise.resolve(false);
    }
    const index = scriptedIndex >= 0
      ? scriptedIndex
      : syntheticWelcomeEvent
        ? -1
        : (mainVoiceIndexRef.current % available.length);
    const track = scriptedWelcomeAvailable
      ? playableWelcome
      : scriptedIndex >= 0
        ? available[scriptedIndex]
        : syntheticWelcomeEvent
          ? playableWelcome
          : available[index];
    const welcomeClaim = isSessionWelcomeEvent(openingEvent)
      ? sessionWelcomePlaybackGuardRef.current.claim(openingEvent)
      : "not_welcome";
    if (welcomeClaim === "played" || welcomeClaim === "starting") {
      return Promise.resolve(false);
    }
    mainVoiceGestureStartInFlightRef.current = true;
    const routing = ensureOverlayLaneRouting(LANE_HYPNOSIS_SPOKEN_MAIN);
    if (routing?.gain) routing.gain.gain.value = resolveMainVoiceLevel(track.audio_scene);
    laneAudio.muted = false;
    const mainVoiceLevel = resolveMainVoiceLevel(track.audio_scene);
    laneAudio.volume = mainVoiceLevel;
    const spokenPlaybackRate = resolveSpokenPlaybackRate(track);
    laneAudio.playbackRate = spokenPlaybackRate;
    laneAudio.defaultPlaybackRate = spokenPlaybackRate;
    laneAudio.preservesPitch = true;
    laneAudio.loop = false;
    laneAudio.src = track.audio_src;
    laneAudio.load();

    // This play() is deliberately invoked in the original Play tap call
    // stack. The continuous-main effect owns subsequent clips, but Android
    // must see the first real spoken program begin under user activation.
    const startNativeFallback = () => {
      try {
        return laneAudio.play();
      } catch (error) {
        console.warn("[AceLabReact] main voice gesture start failed", error);
        return Promise.reject(error);
      }
    };
    // Use the shared gesture-resumed WebAudio path for every main program,
    // scripted or continuous. Native media remains the codec/autoplay fallback
    // rather than the default source of intermittent Android silence.
    const decodedAttempt = Math.abs(spokenPlaybackRate - 1) > 0.01 ? null : playDecodedAudibleLane({
      laneName: LANE_HYPNOSIS_SPOKEN_MAIN,
      track,
      volume: mainVoiceLevel,
      pan: 0,
      onEnded: () => releaseDeckDuck(LANE_HYPNOSIS_SPOKEN_MAIN),
    });
    let gestureDeliveryEngine = "html_media";
    return Promise.resolve(decodedAttempt).then((decoded) => (
      decoded
        ? (gestureDeliveryEngine = "decoded_webaudio", true)
        : Promise.resolve(startNativeFallback()).then(() => true)
    )).then(() => {
      sessionWelcomePlaybackGuardRef.current.settle(openingEvent, true);
      overlayLanePrimedRef.current[LANE_HYPNOSIS_SPOKEN_MAIN] = true;
      if (track.file) playedVoiceFilesRef.current.add(String(track.file));
      mainVoiceIndexRef.current = (index + 1) % available.length;
      mainVoicePauseAfterRef.current = Math.max(350, Math.min(3500, Number(track.pause_after_ms) || 900));
      activeOverlayFeedbackRef.current[LANE_HYPNOSIS_SPOKEN_MAIN] = {
        lane: LANE_HYPNOSIS_SPOKEN_MAIN,
        filename: String(track.file || ""),
        category: String(track.category || "main_hypnosis"),
        persona_id: String(track.persona_id || ""),
      };
      if (openingEvent?.event_id && openingEvent.file === track.file) {
        firedSessionScriptEventKeysRef.current.add(`base:${openingEvent.event_id}`);
      }
      engageDeckDuck(LANE_HYPNOSIS_SPOKEN_MAIN, 0.5);
      audioControllerRef.current?.setNarrationWindow?.(
        Math.max(8, Math.min(180, (Number.isFinite(Number(track.duration_seconds)) ? Number(track.duration_seconds) : Math.max(8, Math.min(180, Number(laneAudio.duration) || 30))) + 1)),
      );
      api.logPlayback({
        event_type: "overlay_fired",
        overlay_file: track.file || "",
        overlay_type: track.audio_type || "voiceover",
        overlay_category: track.category || "main_hypnosis",
        overlay_lane: LANE_HYPNOSIS_SPOKEN_MAIN,
        overlay_ear: "center",
        delivery_engine: gestureDeliveryEngine,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
      });
      return true;
    }).catch((error) => {
      sessionWelcomePlaybackGuardRef.current.settle(openingEvent, false);
      console.warn("[AceLabReact] main voice gesture start failed", error);
      api.logPlayback({
        event_type: "overlay_error",
        overlay_file: track.file || "",
        overlay_lane: LANE_HYPNOSIS_SPOKEN_MAIN,
        delivery_engine: gestureDeliveryEngine,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: String(error),
      });
      return false;
    }).finally(() => {
      mainVoiceGestureStartInFlightRef.current = false;
    });
  }, [engageDeckDuck, ensureOverlayLaneRouting, playDecodedAudibleLane, releaseDeckDuck]);

  const stopLaneMotion = useCallback((laneName) => {
    [overlayLaneRefs.current[laneName], audibleBufferLanesRef.current[laneName]].forEach((lane) => {
      if (lane?.motionTimer) {
        window.clearInterval(lane.motionTimer);
        lane.motionTimer = null;
      }
    });
  }, []);

  const startLaneMotion = useCallback((laneName, basePan, motionMode = "drift", countdownTotal = 0) => {
    // Subtle orbital drift (~±20° over ~9 s) around the base position.
    // Slow source movement strengthens externalization and holds attention
    // without being consciously trackable.
    const routedLane = overlayLaneRefs.current[laneName];
    const decodedLane = audibleBufferLanesRef.current[laneName];
    const lane = decodedLane?.panner?.positionX ? decodedLane : routedLane;
    const ctx = overlayAudioContextRef.current;
    if (!lane?.panner || !ctx) return;
    stopLaneMotion(laneName);
    const baseAngle = Math.asin(Math.max(-1, Math.min(1, basePan)));
    const spectacle = brainwaveLabSettingsRef.current;
    const spectacularMain = laneName === LANE_HYPNOSIS_SPOKEN_MAIN
      && spectacle?.spatialSpectacleEnabled
      && spectacle?.spatialSpiralEnabled;
    const radius = spectacularMain
      ? Number(spectacle.spatialSpiralRadius) || 0.72
      : isWhisperLane(laneName) ? 0.55 : 1.1;
    // Countdown spiral-down: the number spirals around the head, decelerating
    // and tightening toward center with each revolution, while dropping lower
    // (positionY descends). This reads as a descending vortex — each number
    // physically spirals DOWN, matching the countdown's downward-only meaning.
    // countdownTotal (e.g. 10) makes the descent proportional: higher numbers
    // start wide and high, "one" lands center and low.
    const isSpiralDown = motionMode === "spiral_down";
    const spiralDownSeconds = isSpiralDown
      ? Math.max(6, Math.min(30, (countdownTotal || 10) * 0.9))
      : 0;
    let spiralStart = ctx.currentTime;
    lane.motionTimer = window.setInterval(() => {
      try {
        if (isSpiralDown) {
          const t = ctx.currentTime;
          const elapsed = Math.max(0, t - spiralStart);
          const progress = Math.min(1, elapsed / spiralDownSeconds);
          // Decelerating angular speed: starts fast, eases to a stop at center.
          const eased = 1 - Math.pow(1 - progress, 2.2);
          // ~2.5 revolutions total, spiraling inward.
          const angle = eased * Math.PI * 2 * 2.5;
          const shrinkingRadius = radius * (1 - eased) * 0.9;
          // Drop from +0.18 (ear height) down to -0.1 (below) as it converges.
          const bob = 0.18 - eased * 0.28;
          lane.panner.positionX.setTargetAtTime(Math.sin(angle) * shrinkingRadius, t, 0.12);
          lane.panner.positionZ.setTargetAtTime(-Math.cos(angle) * shrinkingRadius, t, 0.12);
          lane.panner.positionY.setTargetAtTime(bob, t, 0.2);
          if (progress >= 1 && lane.motionTimer) {
            window.clearInterval(lane.motionTimer);
            lane.motionTimer = null;
          }
          return;
        }
        const spiralSeconds = Math.max(16, Number(spectacle?.spatialSpiralSeconds) || 32);
        lane.motionPhase += spectacularMain ? (Math.PI * 2 * 0.1 / spiralSeconds) : motionMode === "orbit" ? 0.012 : 0.07;
        const sway = Math.sin(lane.motionPhase) * 0.35;
        const angle = motionMode === "orbit" ? lane.motionPhase : baseAngle + sway;
        const helix = (lane.motionPhase / (Math.PI * 2)) % 1;
        const bob = spectacularMain
          ? 0.14 - helix * (Number(spectacle.spatialSpiralDescent) || 0.58)
          : Math.sin(lane.motionPhase * 0.6) * 0.05;
        const t = ctx.currentTime;
        lane.panner.positionX.setTargetAtTime(Math.sin(angle) * radius, t, 0.25);
        lane.panner.positionZ.setTargetAtTime(-Math.cos(angle) * radius, t, 0.25);
        lane.panner.positionY.setTargetAtTime(bob, t, 0.4);
      } catch {
        // motion is decorative; never let it break playback
      }
    }, 100);
  }, [stopLaneMotion]);

  const playResolvedOverlayCue = useCallback(async (cue) => {
    const requestedDeliveryRole = String(cue?.delivery_role || cue?.overlay_track?.delivery_role || cue?.delivery || "").toLowerCase();
    const requestedSubliminal = requestedDeliveryRole.includes("subliminal")
      || String(cue?.overlay_family || cue?.overlay_track?.overlay_family || "").toLowerCase() === "subliminal";
    const logSubliminalDecision = (eventType, reason, extra = {}) => {
      if (!requestedSubliminal) return;
      api.logPlayback({
        event_type: eventType,
        overlay_file: cue?.overlay_track?.file || "",
        overlay_lane: LANE_SUBLIMINAL_1,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        reason,
        ...extra,
      });
    };
    if (!cue?.overlay_track?.audio_src) {
      logSubliminalDecision("subliminal_skipped", "no_audio_src");
      api.logPlayback({
        event_type: "overlay_skipped",
        overlay_file: cue?.overlay_track?.file || "",
        overlay_type: cue?.overlay_track?.audio_type || "",
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: "no_audio_src",
      });
      return false;
    }
    const preferredVoiceGender = resolvePreferredVoiceGender(stateRef.current);
    if (!voiceTrackMatchesPreference(cue.overlay_track, preferredVoiceGender)) {
      api.logPlayback({
        event_type: "overlay_skipped",
        overlay_file: cue.overlay_track.file || "",
        overlay_lane: cue.line_role || cue.delivery_role || "spoken",
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: `voice_gender_mismatch:${preferredVoiceGender}`,
      });
      return false;
    }
    // Never replay a clip the listener just heard (cross-track schedule
    // collisions). Exempt: ordered sequences (skipping one atom breaks the
    // count) and the MAIN SPOKEN PROGRAM — main hypnosis files legitimately
    // repeat across music beds when the pool is small; blocking them as
    // "duplicates" silenced the foreground track entirely.
    const cueFile = String(cue.overlay_track.file || "");
    const cueLineRole = String(cue.line_role || cue.overlay_track?.line_role || "").toLowerCase();
    const cueFamily = String(cue.overlay_family || cue.overlay_track?.overlay_family || "").toLowerCase();
    const cueIsMainProgram = cueLineRole === "main_voiceover" || cueFamily === "main_hypnosis";
    const cueIsWhisperProgram = cueLineRole === "whisper" || String(cue.delivery_role || "").toLowerCase() === "whisper";
    if (
      cueFile
      && cueIsWhisperProgram
      && !cueIsMainProgram
      && playedVoiceFilesRef.current.has(cueFile)
      && !cue.sequence_group
      && !voiceTrackMayRepeat(cue.overlay_track)
    ) {
      api.logPlayback({
        event_type: "overlay_skipped",
        overlay_file: cueFile,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: "voice_repeat_not_authorized",
      });
      return false;
    }
    if (cueFile && !cue.sequence_group && !cueIsMainProgram && recentlyPlayedOverlayRef.current.slice(-2).includes(cueFile)) {
      api.logPlayback({
        event_type: "overlay_skipped",
        overlay_file: cueFile,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: "duplicate_recent",
      });
      return false;
    }
    const delivery = String(cue.delivery || cue.overlay_track?.drop_design?.delivery || "").toLowerCase();
    const spatialMode = String(cue.spatial_mode || cue.overlay_track?.drop_design?.spatial_mode || "").toLowerCase();
    const targetSpeaker = String(cue.target_speaker || "").toLowerCase();
    const panHint = String(cue.pan || cue.overlay_track?.drop_design?.pan || "").toLowerCase();
    const deliveryRole = String(cue.delivery_role || cue.overlay_track?.delivery_role || "spoken").toLowerCase();
    const lineRole = String(cue.line_role || cue.overlay_track?.line_role || "").toLowerCase();
    const overlayFamily = String(cue.overlay_family || cue.overlay_track?.overlay_family || "").toLowerCase();
    const cueCategory = String(cue.overlay_track?.category || "").toLowerCase();
    const cueIsCountdown = cueCategory === "countdown_atom"
      || overlayFamily === "countdown"
      || (Array.isArray(cue.overlay_track?.tags) && cue.overlay_track.tags.some(
        (tag) => ["countdown", "countdown_atom"].includes(String(tag || "").toLowerCase())
      ));
    if (cueIsCountdown) {
      const group = String(cue.sequence_group || cue.overlay_track?.sequence_group || cueFile || "countdown");
      const guard = countdownGuardRef.current;
      if (guard.activeGroup !== group && Date.now() - guard.lastStartedAt < 12 * 60_000) {
        api.logPlayback({
          event_type: "overlay_skipped",
          overlay_file: cueFile,
          overlay_category: "countdown_atom",
          channel: stateRef.current.activeTheme || "",
          arc_stage: getArcStage(),
          error: "countdown_cooldown",
        });
        return false;
      }
    }
    const spatialAudio = cue.spatial_audio || cue.overlay_track?.spatial_audio || null;
    const spatialLane = String(cue.spatial_lane || "").toLowerCase();
    const isSpatialEncounter = Boolean(
      spatialAudio && (
        spatialLane.startsWith(LANE_ENCOUNTER_PREFIX)
        || lineRole === "ambient_sensual_encounter"
        || overlayFamily === "sensual_encounter"
      )
    );
    const isMainVoiceover = lineRole === "main_voiceover" || overlayFamily === "main_hypnosis";
    const isAffirmationAccent = ["affirmation", "reward", "whisper_reward"].includes(lineRole)
      || overlayFamily === "affirmation";
    const subliminalLane = deliveryRole === "subliminal" || delivery.includes("subliminal");
    const requestsWhisper = isAffirmationAccent && (
      deliveryRole === "whisper" || delivery.includes("whisper")
    ) && !subliminalLane;
    const whisperDuration = Number.isFinite(Number(cue.overlay_track?.duration_seconds))
      ? Number(cue.overlay_track.duration_seconds)
      : 0;
    // Sidecar duration can be zero for older Resemble MP3s even when the audio
    // file is healthy. Do not silence those whispers. Reject only clips known
    // to be too long for an intimate positional visit.
    if (requestsWhisper && Number.isFinite(whisperDuration) && whisperDuration > 45) return false;
    const whisperLane = requestsWhisper;
    const requestedEar = targetSpeaker === "left" || panHint === "left"
      ? "left"
      : targetSpeaker === "right" || panHint === "right"
        ? "right"
        : whisperEarRef.current;
    const laneName = subliminalLane
      ? LANE_SUBLIMINAL_1
      : isMainVoiceover
        ? LANE_HYPNOSIS_SPOKEN_MAIN
        : isSpatialEncounter
          ? spatialLane || `${LANE_ENCOUNTER_PREFIX}${String(spatialAudio?.car_id || "car")}`
          : whisperLane
            ? `whisper_${requestedEar}`
            : LANE_OVERLAY;
    const laneAudio = laneName === LANE_HYPNOSIS_SPOKEN_MAIN
      ? mainVoiceAudioRef.current
      : subliminalLane
        ? subliminalAudioRef.current
        : laneName === LANE_WHISPER_LEFT
          ? whisperLeftAudioRef.current
          : laneName === LANE_WHISPER_RIGHT
            ? whisperRightAudioRef.current
            : overlayAudioRef.current;
    if (!laneAudio) return false;
    // Left/right are positional destinations for one intimate whisper lane,
    // not two simultaneous speakers. If either ear is occupied, both are busy.
    if (whisperLane && [whisperLeftAudioRef.current, whisperRightAudioRef.current].some(
      (audio) => audio && !audio.paused && audio.currentSrc
    )) return false;
    if (whisperLane && [LANE_WHISPER_LEFT, LANE_WHISPER_RIGHT].some(
      (name) => Boolean(audibleBufferLanesRef.current[name]?.source)
    )) return false;
    if (audibleBufferLanesRef.current[laneName]?.source) return false;
    if (!laneAudio.paused && laneAudio.currentSrc) {
      return false;
    }
    // Hard lane rate-limits: the main spoken program and ordered sequences
    // are the spine of the session and always play; accent lanes obey
    // wall-clock cooldowns so NO pathway (stale schedule, cached bundle,
    // resolver bug) can chain-fire audible clips into an endless loop.
    const nowWall = Date.now();
    const laneCooldownMs = whisperLane ? 35_000 : laneName === LANE_SUBLIMINAL_1 ? 15_000 : 30_000;
    if (!cue.sequence_group && !isMainVoiceover) {
      const cooldownKey = whisperLane ? "whisper" : laneName;
      if (nowWall < (laneCooldownUntilRef.current[cooldownKey] || 0)) {
        return false;
      }
      if (whisperLane) {
        const trackFile = String(stateRef.current.currentTrack?.file || "");
        if (whisperBudgetRef.current.trackFile !== trackFile) {
          whisperBudgetRef.current = { trackFile, left: 0, right: 0 };
        }
        // Two quiet visits per ear across a long music bed provide variety
        // without allowing the accent lane to become continuous chatter.
        if ((whisperBudgetRef.current[requestedEar] || 0) >= 2) {
          return false;
        }
      }
    }
    // Belt-and-braces: the worklet preloads at the play gesture, but if the
    // first subliminal cue wins that race, wait for registration here so the
    // lane's one-shot MediaElementSource routing is built binaural, not flat.
    if (subliminalLane && overlayAudioContextRef.current && !subliminalWorkletReadyRef.current) {
      try {
        subliminalWorkletReadyRef.current = await ensureSubliminalWorklet(overlayAudioContextRef.current);
      } catch {
        subliminalWorkletReadyRef.current = false;
      }
    }
    // Subliminal is a frequency-processing contract, not quiet foreground
    // speech. Never permit a flat fallback: if the worklet is unavailable,
    // skip the cue rather than making its words consciously audible.
    if (subliminalLane) {
      logSubliminalDecision("subliminal_delivery_attempt", "worklet_and_lane_check", {
        worklet_ready: Boolean(subliminalWorkletReadyRef.current),
        main_voice_active: Boolean(mainVoiceAudioRef.current && !mainVoiceAudioRef.current.paused && mainVoiceAudioRef.current.currentSrc),
        overlay_context_state: overlayAudioContextRef.current?.state || "missing",
      });
    }
    if (subliminalLane && !subliminalWorkletReadyRef.current) {
      logSubliminalDecision("subliminal_skipped", "worklet_unavailable");
      return false;
    }
    const overlayContext = overlayAudioContextRef.current;
    if (overlayContext && overlayContext.state !== "running") {
      try {
        await overlayContext.resume();
      } catch {
        return false;
      }
    }
    const routing = ensureOverlayLaneRouting(laneName);
    if (subliminalLane && !routing?.shifter) {
      logSubliminalDecision("subliminal_skipped", "routing_unavailable");
      return false;
    }
    // Lock the interaural beat to the channel's programmed brainwave target
    // (audio_scene.binHz, e.g. theta 6) for this specific cue. The target
    // glides toward the configured depth/type over session time (Black Book).
    const scene = brainwaveLabScene(
      resolveLiveAudioScene(stateRef.current, liveBrainwaveSceneRef.current),
      brainwaveLabSettingsRef.current,
      sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0,
    );
    const sceneBinHz = scene.binHz ?? scene.targetBrainwaveHz;
    const subliminalShiftHz = clampShiftHz(sceneBinHz);
    // Spectral mode is explicit and auditable. In-band is the clarity default;
    // "masked" is an opt-in 8.6 kHz low-salience experiment, not a covert
    // command path. Texture modes may relocate adult harmonic material.
    const cueTags = (cue.overlay_track?.tags || []).map((tag) => String(tag).toLowerCase());
    const cueAudioType = String(cue.overlay_track?.audio_type || "").toLowerCase();
    const cueAssetKind = String(cue.overlay_track?.asset_kind || "").toLowerCase();
    const cueIsSpeech = !(
      cueAssetKind === "sound_effect"
      || cueAudioType === "sound_effect"
      || cueTags.some((tag) => ["moan", "moans", "moaning", "breath", "breathing", "sfx", "sound_effect", "kiss", "wet", "ambient_noise"].includes(tag))
    );
    const subliminalCarrierHz = resolveCarrierShiftHz({
      mode: scene.subliminalSpectralMode,
      isSpeech: cueIsSpeech,
      darkShiftHz: scene.subliminalDarkShiftHz ?? -600,
    });
    if (subliminalLane && routing?.shifter) {
      try {
        const ctx = overlayAudioContextRef.current;
        const t = ctx ? ctx.currentTime : 0;
        applySubliminalBrainwaveScene(routing, scene, t);
        const carrier = routing.shifter.parameters.get("carrierShiftHz");
        carrier.cancelScheduledValues(t);
        carrier.setValueAtTime(subliminalCarrierHz, t);
        // Slow common-carrier motion creates a beneath/around positional
        // impression without HRTF remixing the ears and destroying the
        // interaural beat. Silent-mode speech glides within the upper band;
        // dark textures make a smaller, lower spectral orbit.
        const excursion = subliminalCarrierHz >= 10000 ? 1200 : 180;
        carrier.linearRampToValueAtTime(clampCarrierShiftHz(subliminalCarrierHz + excursion), t + 8);
        carrier.linearRampToValueAtTime(clampCarrierShiftHz(subliminalCarrierHz - excursion * 0.65), t + 18);
        carrier.linearRampToValueAtTime(subliminalCarrierHz, t + 28);
      } catch {
        return false;
      }
    }
    // Whisper placement is director-owned rather than asset-owned. Alternating
    // every successful whisper prevents a channel or stale sidecar from pinning
    // all intimate cues to one ear.
    const directedWhisperEar = requestedEar;
    const normalizedPan = whisperLane
      ? (directedWhisperEar === "left" ? -0.85 : 0.85)
      : targetSpeaker === "left" || panHint === "left"
        ? -0.85
        : targetSpeaker === "right" || panHint === "right"
          ? 0.85
          : Number.isFinite(Number(cue.pan))
            ? Number(cue.pan)
            : 0;
    if (routing?.panner) {
      try {
        // Place the voice in 3D space: whispers sit close to the ear,
        // standard overlays sit slightly forward of the listener.
        const radius = whisperLane ? 0.55 : 1.1;
        const angle = Math.asin(Math.max(-1, Math.min(1, normalizedPan)));
        const ctx = overlayAudioContextRef.current;
        const t = ctx ? ctx.currentTime : 0;
        routing.panner.positionX.setValueAtTime(Math.sin(angle) * radius, t);
        routing.panner.positionZ.setValueAtTime(-Math.cos(angle) * radius, t);
        routing.panner.positionY.setValueAtTime(0, t);
      } catch {
        // ignore positioning issues
      }
    } else if (routing?.stereoPanner) {
      try {
        routing.stereoPanner.pan.value = normalizedPan;
      } catch {
        // ignore pan assignment issues
      }
    }
    if (routing?.gain) {
      // Whisper accents remain intimate — a secret at the ear, clearly BELOW
      // the main program, never competing with it. The intelligibility chain's
      // compressor+limiter add automatic makeup gain, so the lane gain must
      // sit well under 1.0. Per-channel tunable via audio_scene.whisperLevel.
      const whisperGain = resolveWhisperGain(scene);
      // Explicit zero is a real control condition. Missing scene values use
      // the ordinary-scene fallback, while nonzero values stay under the
      // shared engineering ceiling.
      const subliminalBaseGain = resolveSubliminalGain(scene);
      const mainVoiceActiveForSubliminal = Boolean(
        mainVoiceAudioRef.current
        && !mainVoiceAudioRef.current.paused
        && mainVoiceAudioRef.current.currentSrc,
      );
      // A spoken foreground cue gets an additional 10 dB reduction. The
      // interval below reapplies this while the cue is running so a late
      // main-voice start cannot expose consonants between callbacks.
      const subliminalLevel = subliminalBaseGain * (mainVoiceActiveForSubliminal ? 0.316 : 1);
      if (subliminalLane) routing.subliminalBaseGain = subliminalBaseGain;
      routing.gain.gain.value = subliminalLane ? subliminalLevel : whisperLane ? whisperGain : (isMainVoiceover ? resolveMainVoiceLevel(scene) : 1.0);
    }
    // Audible lanes deliberately bypass MediaElementSource on Android. Apply
    // their safety mix directly to the HTMLMediaElement; otherwise whispers
    // ignore whisperLevel and jump to near-full volume.
    const nativeWhisperLevel = resolveWhisperNativeGain(scene, cue.overlay_track);
    const nativeMainVoiceLevel = resolveMainVoiceLevel(scene);
    const nativeEncounterLevel = Math.max(
      spatialAudio?.role === "car_background" ? 0.025 : 0.08,
      Math.min(0.32, Number(spatialAudio?.gain) || 0.20),
    );
    laneAudio.volume = subliminalLane
      ? 0.72
      : whisperLane
        ? nativeWhisperLevel
        : isSpatialEncounter
          ? nativeEncounterLevel
          : isMainVoiceover
            ? nativeMainVoiceLevel
            : 0.72;
    // Overlay lanes are one-shots. Do not inherit a stale loop flag from any
    // browser/media-element reuse path.
    try {
      const activeFilename = String(cue.overlay_track.file || "");
      const spokenPlaybackRate = isMainVoiceover ? resolveSpokenPlaybackRate(cue.overlay_track) : 1;
      laneAudio.playbackRate = spokenPlaybackRate;
      laneAudio.defaultPlaybackRate = spokenPlaybackRate;
      laneAudio.preservesPitch = true;
      const clearActiveFeedback = () => {
        if (activeOverlayFeedbackRef.current[laneName]?.filename === activeFilename) {
          delete activeOverlayFeedbackRef.current[laneName];
        }
        releaseDeckDuck(laneName);
        if (isMainVoiceover) audioControllerRef.current?.setNarrationWindow?.(0);
        try { cue.on_ended?.(); } catch { /* lane continuation is best-effort */ }
      };
      let decodedPlayback = null;
      if (!subliminalLane && Math.abs(spokenPlaybackRate - 1) <= 0.01) {
        decodedPlayback = await playDecodedAudibleLane({
          laneName,
          track: cue.overlay_track,
          volume: laneAudio.volume,
          pan: normalizedPan,
          spatial: spatialAudio,
          onEnded: clearActiveFeedback,
        });
      }
      if (!decodedPlayback) {
        laneAudio.loop = Boolean(subliminalLane && cue.continuous_loop);
        laneAudio.src = cue.overlay_track.audio_src;
        laneAudio.load();
        await laneAudio.play();
      }
      if (isMainVoiceover) {
        lastMainVoiceActivityRef.current = Date.now();
        const narrationSeconds = Math.max(
          8,
          Math.min(180, (Number.isFinite(Number(cue.overlay_track?.duration_seconds))
            ? Number(cue.overlay_track.duration_seconds)
            : Math.max(8, Math.min(180, Number(laneAudio.duration) || 30))) + 1),
        );
        audioControllerRef.current?.setNarrationWindow?.(narrationSeconds);
      }
      if (cueIsCountdown) {
        // One shared guard covers authored whisper countdowns, scheduled main
        // countdowns and starvation fallback. Without recording successful
        // starts here, lastStartedAt remained zero and the watchdog considered
        // a new full sequence eligible immediately after every prior one.
        countdownGuardRef.current = {
          lastStartedAt: Date.now(),
          activeGroup: String(cue.sequence_group || cue.overlay_track?.sequence_group || "countdown"),
        };
      }
      activeOverlayFeedbackRef.current[laneName] = {
        lane: laneName,
        filename: String(cue.overlay_track.file || ""),
        category: String(cue.overlay_track.category || cue.overlay_family || ""),
        persona_id: String(cue.overlay_track.persona_id || ""),
      };
      if (!decodedPlayback) laneAudio.onended = clearActiveFeedback;
      const overlayHistory = recentlyPlayedOverlayRef.current;
      const overlayFile = String(cue.overlay_track.file || "");
      if (overlayFile) {
        const priorIndex = overlayHistory.indexOf(overlayFile);
        if (priorIndex >= 0) overlayHistory.splice(priorIndex, 1);
        overlayHistory.push(overlayFile);
        if (overlayHistory.length > 100) overlayHistory.shift();
        if (isMainVoiceover || whisperLane) playedVoiceFilesRef.current.add(overlayFile);
      }
      if (whisperLane) {
        whisperEarRef.current = directedWhisperEar === "left" ? "right" : "left";
      }
      if (!cue.sequence_group && !isMainVoiceover) {
        const cooldownKey = whisperLane ? "whisper" : laneName;
        laneCooldownUntilRef.current[cooldownKey] = Date.now() + laneCooldownMs;
        if (whisperLane) whisperBudgetRef.current[directedWhisperEar] = (whisperBudgetRef.current[directedWhisperEar] || 0) + 1;
      }
      const duckDb = Number(cue?.duck_db);
      const duckGain = Number.isFinite(duckDb) ? Math.pow(10, duckDb / 20) : 0.55;
      if (!subliminalLane) engageDeckDuck(laneName, duckGain);
      setTransientStatus(
        subliminalLane
          ? "Subliminal layered"
          : whisperLane
            ? "Affirmation whispered"
            : isSpatialEncounter
              ? "A nearby car stirs"
              : (isMainVoiceover ? "HypnoMate guiding" : "Overlay layered in"),
      );
      const subliminalLevelSample = subliminalLane
        ? sampleAnalyserLevel(routing?.analyser)
        : null;
      if (routing?.panner && !whisperLane) {
        // Countdowns spiral DOWN: the number decelerates and tightens toward
        // center while dropping lower, so the count physically descends. The
        // countdown value (e.g. 10) sets the spiral duration — higher numbers
        // start wide and high, "one" lands center and low.
        const motionMode = cueIsCountdown
          ? "spiral_down"
          : isMainVoiceover
            ? "orbit"
            : "drift";
        startLaneMotion(laneName, normalizedPan, motionMode, countdownValue(cue.overlay_track) || 10);
      }
      api.logPlayback({
        event_type: "overlay_fired",
        overlay_file: cue.overlay_track.file || "",
        overlay_type: cue.overlay_track.audio_type || "",
        overlay_category: cue.overlay_track.category || "",
        overlay_lane: laneName,
        overlay_ear: whisperLane ? directedWhisperEar : "center",
        spatial_car_id: isSpatialEncounter ? String(spatialAudio?.car_id || "") : "",
        spatial_distance_m: isSpatialEncounter ? Number(spatialAudio?.distance_m || 0) : 0,
        delivery_engine: decodedPlayback ? "decoded_webaudio" : (subliminalLane ? "audio_worklet_media" : "html_media"),
        // Records which engine actually delivered the cue so binaural vs flat
        // sessions can be compared in playback analytics (impact proof).
        subliminal_engine: subliminalLane ? (routing?.shifter ? "binaural_worklet" : "flat_fallback") : "",
        subliminal_shift_hz: subliminalLane && routing?.shifter ? subliminalShiftHz : 0,
        subliminal_carrier_shift_hz: subliminalLane && routing?.shifter ? subliminalCarrierHz : 0,
        spectral_mode: subliminalLane ? String(scene?.subliminalSpectralMode || "in_band") : "",
        subliminal_worklet_ready: subliminalLane ? Boolean(subliminalWorkletReadyRef.current) : false,
        subliminal_gain: subliminalLane && routing?.gain?.gain ? Number(routing.gain.gain.value) : 0,
        subliminal_base_gain: subliminalLane && Number.isFinite(Number(routing?.subliminalBaseGain))
          ? Number(routing.subliminalBaseGain) : 0,
        subliminal_rms: subliminalLevelSample?.rms ?? 0,
        subliminal_peak: subliminalLevelSample?.peak ?? 0,
        subliminal_peak_ceiling: SUBLIMINAL_GAIN_CEILING,
        subliminal_level_sample_count: subliminalLevelSample?.sample_count ?? 0,
        main_voice_active: subliminalLane
          ? Boolean(mainVoiceAudioRef.current && !mainVoiceAudioRef.current.paused && mainVoiceAudioRef.current.currentSrc)
          : false,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
      });
      return true;
    } catch (error) {
      logSubliminalDecision("subliminal_error", String(error));
      console.warn("[AceLabReact] overlay cue failed", error);
      api.logPlayback({
        event_type: "overlay_error",
        overlay_file: cue.overlay_track.file || "",
        overlay_lane: laneName,
        channel: stateRef.current.activeTheme || "",
        arc_stage: getArcStage(),
        error: String(error),
      });
      return false;
    }
  }, [engageDeckDuck, ensureOverlayLaneRouting, playDecodedAudibleLane, releaseDeckDuck, setTransientStatus, startLaneMotion]);

  const resumePlaybackIfNeeded = useCallback(async ({ currentTrack, previouslyPlaying, previousTrackFile, activeTheme }) => {
    if (!previouslyPlaying || !currentTrack?.file) {
      mergeState({ isBusy: false });
      return;
    }

    const controller = audioControllerRef.current;
    const activeFile = controller?.currentTrackFile?.() || "";
    const trackAlreadyPlaying = controller?.isPlaying?.() && activeFile === currentTrack.file;
    if (trackAlreadyPlaying) {
      mergeState({ status: "Playing", isBusy: false });
      return;
    }

    const requiresReload = activeFile !== currentTrack.file;
    if (requiresReload && controller) {
      controller.loadTrack(currentTrack);
      controller.updateMediaSessionMetadata(currentTrack);
    }

    try {
      await controller?.play?.();
    } catch (error) {
      const message = String(error?.message || error || "");
      const blockedByBrowser = error?.name === "NotAllowedError" || /interact|gesture|allowed/i.test(message);
      if (blockedByBrowser) {
        mergeState({
          status: "Ready",
          gateMessage: "Session loaded. Tap play to begin your Drop.",
          isBusy: false,
        });
        return;
      }
      throw error;
    }

    if (previousTrackFile !== currentTrack.file) {
      await api.sendFeedback({
        event: "play",
        filename: currentTrack.file,
        category: currentTrack.category,
        theme: activeTheme,
        persona_id: currentTrack.persona_id,
      });
    }

    mergeState({ status: "Playing", isBusy: false });
  }, [mergeState]);

  const loadLibrary = useCallback(async ({ channel, preserveCurrentTrack = true, resumePlayback = null }) => {
    const requestId = ++libraryRequestIdRef.current;
    try {
      const stateBeforeLoad = stateRef.current;
      const previouslyPlaying = resumePlayback === null ? !!stateBeforeLoad.isPlaying : !!resumePlayback;
      // Never revoke Android's already-granted foreground media element during
      // a channel refresh. Let the current sentence finish; the continuous
      // program advances into the newly loaded channel queue on `ended`.
      clearOverlayLanes({ preserveAudibleVoices: previouslyPlaying });
      firedOverlayCueKeysRef.current = new Set();
      mergeState({ status: "Loading the queue…", isBusy: true });
      const payload = await api.fetchLibrary(channel, { lite: false });
      if (requestId !== libraryRequestIdRef.current) return;

      if (payload.gate === "auth_required") {
        // A RUNNING SESSION MUST NEVER BE DESTROYED BY A CONTENT GATE.
        //
        // This branch used to empty the queue, null the current track, and set
        // welcomeVisible: true. That set body.app-locked, which sets
        // opacity: 0 / visibility: hidden on every shell element — and because
        // html/body are #000, the result was a solid black screen with the
        // audio stopped. The Director reported exactly this: "it played intro,
        // then one sound bite, then stopped playing, browser window is solid
        // black."
        //
        // The trigger is any mid-session refresh that fails the access check —
        // an expired session cookie, a TOS version bump, a cold host. The
        // harmless trigger used to have a catastrophic effect.
        //
        // If a session is already active, keep the queue, keep the current
        // track, and keep the shell visible. Surface why no NEW material is
        // arriving instead. Only a cold start may show the full gate.
        const activeSession = Boolean(
          stateBeforeLoad.currentTrack?.file
          || stateBeforeLoad.isPlaying
          || (Array.isArray(stateBeforeLoad.queue) && stateBeforeLoad.queue.length),
        );
        if (activeSession) {
          mergeState({
            status: "Session continues — sign in again to load more",
            gateMessage: payload.message || "Sign in with Google to load more content.",
            showTosButton: true,
            isBusy: false,
          });
          return;
        }
        mergeState({
          queue: [],
          currentTrack: null,
          trackHistory: [],
          canGoBack: false,
          queueCountLabel: "0 tracks",
          contentDepthLabel: "locked",
          status: "Access required",
          showTosButton: false,
          welcomeVisible: true,
          isBusy: false,
        });
        return;
      }

      const effectivePayload = await expandThinQueueIfNeeded(payload, channel);
      if (requestId !== libraryRequestIdRef.current) return;
      const hypnosisProtocol = effectivePayload.hypnosis_protocol || stateBeforeLoad.hypnosisProtocol || null;
      api.setHypnosisProtocol(hypnosisProtocol);
      const elapsedSeconds = sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0;
      const queue = (Array.isArray(effectivePayload.tracks) ? effectivePayload.tracks : [])
        .map((track) => applyHypnosisProtocol(track, hypnosisProtocol, elapsedSeconds));
      const responseSession = effectivePayload.session || stateBeforeLoad.session || {};
      const preferredVoiceGender = resolvePreferredVoiceGender(
        { session: responseSession, preferences: effectivePayload.preferences || stateBeforeLoad.preferences },
      );
      const rawMainVoiceTracks = Array.isArray(effectivePayload.main_voice_tracks)
        ? effectivePayload.main_voice_tracks
        : [];
      const sessionWelcomeTrack = effectivePayload.session_welcome_track
        || rawMainVoiceTracks.find((track) => isSessionWelcomeTrack(track))
        || null;
      const mainVoiceQueue = filterSpokenTracksByVoice(rawMainVoiceTracks, preferredVoiceGender);
      const overlayQueue = filterSpokenTracksByVoice(effectivePayload.overlay_tracks, preferredVoiceGender);
      const sessionScript = effectivePayload.session_script?.script_id
        ? effectivePayload.session_script
        : null;
      mainVoiceIndexRef.current = 0;
      firedSessionScriptEventKeysRef.current = new Set();
      const currentTrack = resolveCurrentTrack({
        queue,
        mainVoiceQueue,
        priorCurrentTrack: preserveCurrentTrack ? stateBeforeLoad.currentTrack : null,
      });
      const queueIndex = currentTrack ? queue.findIndex((track) => track.file === currentTrack.file) : -1;
      const canGoBack = (stateBeforeLoad.trackHistory || []).length > 0;

      mergeState({
        queue,
        hypnosisProtocol,
        mainVoiceQueue,
        sessionWelcomeTrack,
        sessionScript,
        queueIndex,
        currentTrack,
        overlayQueue,
        // The user's selected theme is authoritative. effective_channel is a
        // server routing diagnostic and must never change the selector value.
        activeTheme: channel,
        activeThemeLabel: stateBeforeLoad.themes?.find((theme) => theme.slug === channel)?.label || channel,
        queueCountLabel: `${queue.length} track${queue.length === 1 ? "" : "s"}`,
        contentDepthLabel: (() => {
          const inv = effectivePayload.inventory || {};
          const playable = Number(inv.route_eligible_after_access || inv.playable_main_deck || 0);
          const returned = Number(inv.returned_main_deck || effectivePayload.count || 0);
          const minutes = effectivePayload.content_minutes_label || `${effectivePayload.content_minutes || 0} min`;
          return `${minutes} · ${returned}/${playable} playable`;
        })(),
        status: queue.length ? "Ready" : "Preparing your session…",
        libraryMessage: effectivePayload.production?.message || "",
        gateMessage: buildQueueMessage(effectivePayload),
        showTosButton: false,
        welcomeVisible: false,
        isBusy: false,
        canGoBack,
        session: responseSession,
        featuredMate: resolveFeaturedMate({
          personas: stateBeforeLoad.personas || [],
          session: responseSession,
          currentTrack,
          activeTheme: channel,
        }),
      });

      if (effectivePayload.preferences) patchNestedState("preferences", effectivePayload.preferences);
      if (effectivePayload.session) patchNestedState("session", effectivePayload.session);

      // Empty queue: content is being generated. Auto-retry every 30s so the
      // session starts the moment new tracks land — no manual refresh needed.
      if (emptyQueueRetryRef.current) {
        clearInterval(emptyQueueRetryRef.current);
        emptyQueueRetryRef.current = null;
      }
      if (!queue.length) {
        const _retryChannel = effectivePayload.effective_channel || channel;
        let _retryCountdown = 30;
        emptyQueueRetryRef.current = window.setInterval(async () => {
          _retryCountdown -= 1;
          if (_retryCountdown > 0) {
            mergeState({ status: `Your session is being prepared… checking in ${_retryCountdown}s` });
            return;
          }
          _retryCountdown = 30;
          try {
            const retryPayload = await api.fetchLibrary(_retryChannel, { lite: false });
            const retryTracks = Array.isArray(retryPayload.tracks) ? retryPayload.tracks : [];
            if (retryTracks.length) {
              clearInterval(emptyQueueRetryRef.current);
              emptyQueueRetryRef.current = null;
              loadLibrary({ channel: _retryChannel, preserveCurrentTrack: true });
            }
          } catch (_e) { /* non-fatal — keep retrying */ }
        }, 1_000);
      }

      if (currentTrack) {
        const controller = audioControllerRef.current;
        // A channel switch is authoritative even when both channels can reuse
        // the same music filename. Force the new metadata, scene and stream
        // binding instead of mistaking the old channel's deck for ready.
        const needsPreload = !preserveCurrentTrack
          || (!previouslyPlaying && controller?.currentTrackFile?.() !== currentTrack.file);
        if (needsPreload) controller.loadTrack(currentTrack);
        controller.updateMediaSessionMetadata(currentTrack);
      } else {
        audioControllerRef.current?.updateMediaSessionMetadata?.(null);
      }

      await resumePlaybackIfNeeded({
        currentTrack,
        previouslyPlaying,
        previousTrackFile: stateBeforeLoad.currentTrack?.file || "",
        activeTheme: channel,
      });
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Queue failed",
        gateMessage: "The library could not be loaded right now. Check the browser console and backend logs if it continues.",
        isBusy: false,
      });
    }
  }, [buildAccessMessage, buildQueueMessage, expandThinQueueIfNeeded, patchNestedState, resolveCurrentTrack, resolveFeaturedMate, resumePlaybackIfNeeded, mergeState]);

  const compileCustomSession = useCallback(async (request) => {
    setSessionBuilderBusy(true);
    setSessionBuilderError("");
    if (emptyQueueRetryRef.current) {
      clearInterval(emptyQueueRetryRef.current);
      emptyQueueRetryRef.current = null;
    }
    try {
      // A newly compiled session owns a fresh score clock. Previously, if a
      // listener built it while an older brainwave/music session was already
      // playing, the old clock survived. The script runner then treated the
      // opening spoken events as long-missed and silently discarded them,
      // leaving only the brainwave bed audible. Stop every old lane and reset
      // timing before installing the new queue; the next Play tap starts the
      // main voice at second zero under the browser's user gesture.
      await Promise.resolve(audioControllerRef.current?.pause?.());
      clearOverlayLanes();
      hapticControllerRef.current?.pause?.();
      if (overlayAudioContextRef.current?.state === "running") {
        await overlayAudioContextRef.current.suspend().catch(() => null);
      }
      sessionStartTimeRef.current = null;
      sessionPausedAtRef.current = null;
      pausedMediaLanesRef.current = [];
      const payload = await api.compileSession(request);
      // Bundles own their narrators (a single voice or, for alternating bundles
      // like a male/female Kama Sutra reading, several). A bundle compile keeps
      // every declared narrator track rather than filtering to one gender: the
      // authored voices ARE the cast. Non-bundle compiles still honor the
      // listener's explicit voice preference.
      const requestedVoiceGender = request.bundle_id
        ? "neutral"
        : (Array.isArray(request.voice_genders) ? request.voice_genders : [])
          .map((voice) => normalizePreferredVoiceGender(voice))
          .find((voice) => voice !== "neutral") || "neutral";

      // Install both the initial compile and an in-place generation refresh
      // through one path. Refreshes must not reset the welcome guard: the
      // server keeps the same session id and the browser may already have
      // started the singleton opener.
      const installPayload = (nextPayload, { resetWelcome = false } = {}) => {
        if (resetWelcome) sessionWelcomePlaybackGuardRef.current.reset();
        const nextQueue = Array.isArray(nextPayload?.tracks) ? nextPayload.tracks : [];
        const rawMainVoiceTracks = Array.isArray(nextPayload?.main_voice_tracks)
          ? nextPayload.main_voice_tracks
          : [];
        const sessionWelcomeTrack = nextPayload?.session_welcome_track
          || rawMainVoiceTracks.find((track) => isSessionWelcomeTrack(track))
          || null;
        const nextMainVoiceQueue = filterSpokenTracksByVoice(rawMainVoiceTracks, requestedVoiceGender);
        const nextOverlayQueue = filterSpokenTracksByVoice(nextPayload?.overlay_tracks, requestedVoiceGender);
        const nextCurrentTrack = nextQueue[0] || null;
        clearOverlayLanes();
        mainVoiceIndexRef.current = 0;
        firedSessionScriptEventKeysRef.current = new Set();
        mergeState({
          queue: nextQueue,
          mainVoiceQueue: nextMainVoiceQueue,
          sessionWelcomeTrack,
          overlayQueue: nextOverlayQueue,
          sessionScript: nextPayload?.session_script?.script_id ? nextPayload.session_script : null,
          session: {
            ...stateRef.current.session,
            preferred_voice_gender: requestedVoiceGender,
            nsfw: Boolean(request.nsfw),
            subliminal_testing: Boolean(request.subliminal_testing),
            strong_induction: Boolean(request.strong_induction),
          },
          customSessionRequest: { ...request },
          queueIndex: nextCurrentTrack ? 0 : -1,
          currentTrack: nextCurrentTrack,
          // Bundles route their music bed to a dedicated theme so the daemon
          // generates the right narration-first bed. The Firefly Cargo Bay
          // bundle gets the space-western Americana bed (cowboy guitar, slide
          // guitar, fiddle, banjo, harmonica); other bundles get the harem
          // bed (tanpura drone, bansuri, frame drum). Non-bundle compiles keep
          // the custom_session label.
          activeTheme: request.bundle_id === "firefly_cargo_bay_nsfw"
            ? "firefly_cargo_bay"
            : request.bundle_id ? "clouds_harem" : "custom_session",
          activeThemeLabel: request.bundle_id
            ? (nextPayload?.title || "Harem session")
            : (nextPayload?.title || "Custom session"),
          queueCountLabel: `${nextQueue.length} music track${nextQueue.length === 1 ? "" : "s"}`,
          contentDepthLabel: `${Math.round((Number.isFinite(Number(nextPayload?.duration_seconds)) ? Number(nextPayload.duration_seconds) : 0) / 60)} min planned`,
          status: nextCurrentTrack || nextMainVoiceQueue.length
            ? "Session ready — press Play"
            : "Session needs compatible audio",
          gateMessage: (nextPayload?.gaps || []).join(" "),
          libraryMessage: "Strict coherence filtering is active.",
          isPlaying: false,
          isBusy: false,
        });
        if (nextCurrentTrack) {
          audioControllerRef.current?.loadTrack(nextCurrentTrack);
          audioControllerRef.current?.updateMediaSessionMetadata(nextCurrentTrack);
        }
        return {
          hasPlayableAudio: Boolean(nextCurrentTrack || nextMainVoiceQueue.length),
          generationPending: Boolean(nextPayload?.generation_pending),
          coherentReady: Boolean(nextPayload?.session_script?.coherence?.ready),
        };
      };

      const installed = installPayload(payload, { resetWelcome: true });
      // A compile can return a populated legacy queue while the daemon is
      // still publishing the ordered text-plan tasks. Poll the in-place
      // refresh contract in that case, rather than assuming a non-empty queue
      // is current. Stop before playback begins so a live score is never
      // replaced underneath an already-running listener.
      // The coherent opening is the playback gate. The five-hour tail may
      // continue rendering in the background; do not make a listener wait on
      // every future paragraph once the body-scan/count/deepening runway is
      // auditable and playable.
      if (!installed.coherentReady || !installed.hasPlayableAudio) {
        let retryCountdown = 20;
        emptyQueueRetryRef.current = window.setInterval(async () => {
          if (stateRef.current.isPlaying) {
            clearInterval(emptyQueueRetryRef.current);
            emptyQueueRetryRef.current = null;
            return;
          }
          retryCountdown -= 1;
          if (retryCountdown > 0) return;
          retryCountdown = 20;
          try {
            const retryPayload = await api.refreshSession(request);
            const refreshed = installPayload(retryPayload);
            if (refreshed.coherentReady && refreshed.hasPlayableAudio) {
              clearInterval(emptyQueueRetryRef.current);
              emptyQueueRetryRef.current = null;
            }
          } catch (_e) { /* daemon may still be rendering; retry next interval */ }
        }, 1_000);
      }
      setSessionBuilderOpen(false);
    } catch (error) {
      setSessionBuilderError(String(error?.message || error));
    } finally {
      setSessionBuilderBusy(false);
    }
  }, [clearOverlayLanes, emptyQueueRetryRef, loadLibrary, mergeState]);

  // Derive the user's trance arc stage from elapsed session time.
  // Used as a hint to the backend to bias content selection.
  const getArcStage = useCallback(() => {
    if (!sessionStartTimeRef.current) return "induction";
    const elapsedMin = (Date.now() - sessionStartTimeRef.current) / 60_000;
    if (elapsedMin < 5) return "attention_capture";
    if (elapsedMin < 20) return "induction";
    if (elapsedMin < 45) return "trance";
    return "post_hypnotic";
  }, []);

  const submitOutcomeCheckin = useCallback(async (rawRating) => {
    const rating = normalizeOutcomeRating(rawRating);
    if (rating === null || outcomeCheckinBusy) return;
    const current = stateRef.current;
    const reportPhase = resolveOutcomeReportPhase({
      sessionStarted:
        sessionStartTimeRef.current === null
          ? false
          : Number.isFinite(sessionStartTimeRef.current)
            ? true
            : null,
    });
    const elapsedSessionSeconds = sessionStartTimeRef.current
      ? Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000)
      : 0;
    const layerStatus = audioControllerRef.current?.layerStatus?.() || {};
    const transportProfile = resolveAudioTransportProfile({
      context: overlayAudioContextRef.current,
      media: subliminalAudioRef.current,
      track: current.currentTrack,
    });
    setOutcomeCheckinBusy(true);
    setOutcomeCheckinMessage("");
    try {
      const result = await api.recordHypnosisOutcome({
        metric: RELAXATION_OUTCOME_METRIC,
        value: rating,
        channel: current.activeTheme || current.sessionRequest?.setting || "custom_session",
        context: buildOutcomeContext({
          track: current.currentTrack,
          elapsedSessionSeconds,
          playbackState: current.isPlaying ? "playing" : "paused",
          layerStatus,
          arcStage: getArcStage(),
          condition: {
            id: current.hypnosisProtocol?.protocol_id,
            block: current.hypnosisProtocol?.block_index,
            label: current.hypnosisProtocol?.label || current.activeThemeLabel,
          },
          reportPhase,
          transportProfile,
        }),
      });
      if (!result) throw new Error("outcome was not saved");
      setOutcomeCheckinOpen(false);
      setOutcomeCheckinMessage("Relaxation rating saved.");
    } catch (error) {
      console.warn("[AceLabReact] outcome check-in failed", error);
      setOutcomeCheckinMessage("Could not save that rating.");
    } finally {
      setOutcomeCheckinBusy(false);
    }
  }, [getArcStage, outcomeCheckinBusy]);

  const submitDetectionCheckin = useCallback(async (rawResponse) => {
    const response = normalizeDetectionResponse(rawResponse);
    if (response === null || detectionCheckinBusy) return;
    const current = stateRef.current;
    const reportPhase = resolveOutcomeReportPhase({
      sessionStarted:
        sessionStartTimeRef.current === null
          ? false
          : Number.isFinite(sessionStartTimeRef.current)
            ? true
            : null,
    });
    if (reportPhase !== "during_or_after") return;
    const elapsedSessionSeconds = sessionStartTimeRef.current
      ? Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000)
      : 0;
    const layerStatus = audioControllerRef.current?.layerStatus?.() || {};
    const transportProfile = resolveAudioTransportProfile({
      context: overlayAudioContextRef.current,
      media: subliminalAudioRef.current,
      track: current.currentTrack,
    });
    setDetectionCheckinBusy(true);
    setDetectionCheckinMessage("");
    try {
      const result = await api.recordHypnosisOutcome({
        metric: MESSAGE_DETECTION_METRIC,
        value: response,
        channel: current.activeTheme || current.sessionRequest?.setting || "custom_session",
        context: {
          ...buildOutcomeContext({
            track: current.currentTrack,
            elapsedSessionSeconds,
            playbackState: current.isPlaying ? "playing" : "paused",
            layerStatus,
            arcStage: getArcStage(),
            condition: {
              id: current.hypnosisProtocol?.protocol_id,
              block: current.hypnosisProtocol?.block_index,
              label: current.hypnosisProtocol?.label || current.activeThemeLabel,
            },
            reportPhase,
            transportProfile,
          }),
          detection_response_label: detectionResponseLabel(response),
        },
      });
      if (!result) throw new Error("detection report was not saved");
      setDetectionCheckinOpen(false);
      setDetectionCheckinMessage("Detection report saved.");
    } catch (error) {
      console.warn("[AceLabReact] detection check-in failed", error);
      setDetectionCheckinMessage("Could not save that report.");
    } finally {
      setDetectionCheckinBusy(false);
    }
  }, [detectionCheckinBusy, getArcStage]);

  // Background queue refresh: fetches fresh tracks for the active channel,
  // filters out recently played files, and appends them to the live queue.
  const refreshQueueInBackground = useCallback(async ({ musicOnly = false } = {}) => {
    if (queueRefreshInFlightRef.current) return;
    const currentState = stateRef.current;
    const channel = currentState.activeTheme || "all";
    if (!channel) return;
    queueRefreshInFlightRef.current = true;
    try {
      // Exclude: recently played + files already sitting in the queue.
      // This tells the server to prioritise truly new drops over tracks
      // the client already has lined up.
      const recentSet = new Set(recentlyPlayedRef.current.slice(-20));
      const queuedFiles = (stateRef.current.queue || []).map((t) => t.file).filter(Boolean);
      const excludeSet = new Set([
        ...recentSet,
        ...recentlyPlayedOverlayRef.current,
        ...queuedFiles,
      ]);
      const exclude = [...excludeSet].join(",");
      const arcStage = getArcStage();
      const fresh = await api.fetchLibrary(channel, { lite: musicOnly, exclude, arc_stage: arcStage });
      if (fresh.gate === "auth_required") return;
      const hypnosisProtocol = fresh.hypnosis_protocol || currentState.hypnosisProtocol || null;
      api.setHypnosisProtocol(hypnosisProtocol);
      const elapsedSeconds = sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0;
      const freshTracks = (Array.isArray(fresh.tracks) ? fresh.tracks : [])
        .map((track) => applyHypnosisProtocol(track, hypnosisProtocol, elapsedSeconds));
      const preferredVoiceGender = resolvePreferredVoiceGender(currentState);
      const freshOverlays = filterSpokenTracksByVoice(fresh.overlay_tracks, preferredVoiceGender);
      const rawFreshMainVoice = Array.isArray(fresh.main_voice_tracks)
        ? fresh.main_voice_tracks
        : [];
      const freshWelcomeTrack = rawFreshMainVoice.find((track) => isSessionWelcomeTrack(track)) || null;
      const freshMainVoice = filterSpokenTracksByVoice(rawFreshMainVoice, preferredVoiceGender);
      const novelTracks = freshTracks.filter((t) => t.file && !excludeSet.has(t.file));
      const novelOverlays = freshOverlays.filter((t) => t.file && !excludeSet.has(t.file));
      const novelMainVoice = freshMainVoice.filter((t) => t.file && !excludeSet.has(t.file));
      if (!novelTracks.length && !novelOverlays.length && !novelMainVoice.length) return;
      // Splice novel tracks after the current position. Even when the music-bed
      // inventory is unchanged, replace schedules on existing beds so newly
      // generated guidance enters the continuous stream immediately.
      mergeState((prev) => {
        const existing = prev.queue || [];
        const existingFiles = new Set(existing.map((t) => t.file));
        const toAppend = novelTracks.filter((t) => !existingFiles.has(t.file));
        const freshByFile = new Map(freshTracks.map((track) => [track.file, track]));
        const refreshedExisting = existing.map((track) => {
          const replacement = freshByFile.get(track.file);
          return replacement?.resolved_overlay_schedule?.length
            ? { ...track, resolved_overlay_schedule: replacement.resolved_overlay_schedule }
            : track;
        });
        const insertAt = (prev.queueIndex ?? 0) + 1;
        const nextQueue = [
          ...refreshedExisting.slice(0, insertAt),
          ...toAppend,
          ...refreshedExisting.slice(insertAt),
        ];
        const overlayFiles = new Set((prev.overlayQueue || []).map((track) => track.file));
        const appendedOverlays = novelOverlays.filter((track) => !overlayFiles.has(track.file));
        const mainVoiceFiles = new Set((prev.mainVoiceQueue || []).map((track) => track.file));
        const appendedMainVoice = novelMainVoice.filter((track) => !mainVoiceFiles.has(track.file));
        return {
          queue: nextQueue,
          currentTrack: prev.currentTrack || nextQueue[0] || null,
          queueIndex: prev.currentTrack ? prev.queueIndex : (nextQueue.length ? 0 : -1),
          queueCountLabel: `${nextQueue.length} track${nextQueue.length === 1 ? "" : "s"}`,
          overlayQueue: [
            ...(prev.overlayQueue || []),
            ...appendedOverlays,
          ],
          mainVoiceQueue: [
            ...(prev.mainVoiceQueue || []),
            ...appendedMainVoice,
          ],
          sessionWelcomeTrack: prev.sessionWelcomeTrack || freshWelcomeTrack,
          hypnosisProtocol,
        };
      });
    } catch (e) {
      console.warn("[AceLabReact] background queue refresh failed", e);
    } finally {
      queueRefreshInFlightRef.current = false;
    }
  }, [getArcStage, mergeState]);

  const advanceBackgroundRotation = useCallback(async () => {
    // Play every subliminal background in shuffled order, reshuffling each
    // full pass. Rotation (vs looping one file) both prevents habituation and
    // gives the experiment coverage: each file's exposure is logged so impact
    // can be attributed per-asset.
    const backgroundAudio = backgroundAudioRef.current;
    const files = stateRef.current.backgroundFiles || [];
    const controller = audioControllerRef.current;
    if (
      !backgroundAudio
      || !files.length
      || !isNsfwSessionState(stateRef.current)
      || !isMainMusicPlaying(stateRef.current, controller)
    ) {
      backgroundAudio?.pause?.();
      return null;
    }
    await ensureOverlayContextFromGesture();
    if (!subliminalWorkletReadyRef.current) return null;
    const routing = ensureOverlayLaneRouting(LANE_SUBLIMINAL_2);
    if (!routing?.shifter || !routing?.gain) return null;
    const ctx = overlayAudioContextRef.current;
    const now = ctx?.currentTime || 0;
    const scene = brainwaveLabScene(
      resolveLiveAudioScene(stateRef.current, liveBrainwaveSceneRef.current),
      brainwaveLabSettingsRef.current,
      sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0,
    );
    const appliedScene = applySubliminalBrainwaveScene(
      routing, scene, now, {
      background: true,
      // Shift the sex bed DOWN into the felt sub-bass (20–100 Hz) so it is
      // FELT, not consciously parsed as moans. Volume is not the issue —
      // frequency is (Director, 2026-08-19).
      carrierShiftHz: resolveSubliminalBackgroundShiftHz({ mode: "dark" }),
    },
    );
    if (!appliedScene) return null;
    const backgroundShiftHz = appliedScene.shiftHz;
    const backgroundRippleDepth = appliedScene.rippleDepth;
    // Hardware nonlinearities can fold ultrasonic energy back into audible
    // speech. Keep transformed background energy bounded while never allowing
    // an unprocessed media-element path.
    const backgroundBaseGain = resolveSubliminalGain(scene, { background: true });
    routing.subliminalBaseGain = backgroundBaseGain;
    routing.gain.gain.value = backgroundBaseGain;
    // Remove stale names if the server's background inventory changed while
    // preserving the current shuffled pass.
    const eligibleFiles = [...new Set(files.filter(Boolean))];
    const eligibleSet = new Set(eligibleFiles);
    const rotation = backgroundRotationRef.current;
    rotation.order = rotation.order.filter((name) => eligibleSet.has(name));
    rotation.index = Math.min(rotation.index, rotation.order.length - 1);
    rotation.index += 1;
    if (rotation.index >= rotation.order.length) {
      const lastPlayed = rotation.order[rotation.order.length - 1];
      const shuffled = [...eligibleFiles];
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      // Avoid the same file back-to-back across reshuffle boundaries.
      if (shuffled.length > 1 && shuffled[0] === lastPlayed) {
        [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
      }
      rotation.order = shuffled;
      rotation.index = 0;
    }
    const file = rotation.order[rotation.index];
    backgroundAudio.loop = false;
    backgroundAudio.onended = () => {
      advanceBackgroundRotation()?.catch?.(() => { });
    };
    backgroundAudio.onerror = () => {
      // A corrupt/missing atom must not end the continuous mix. The transport
      // guard inside advanceBackgroundRotation prevents retries after music
      // has stopped.
      window.setTimeout(() => advanceBackgroundRotation()?.catch?.(() => { }), 100);
    };
    backgroundAudio.src = `/background/${encodeURIComponent(file)}`;
    // The sex bed is a SUBLIMINAL layer: it must be felt, not heard. The
    // native element feeds the shifter which shifts it down into the sub-bass
    // (20–100 Hz) and lowpasses to 200 Hz, but the raw source volume must also
    // be low so the transformed rumble stays at the perceptual edge — never
    // "sounds like I'm in a porno" (Director, 2026-08-19).
    backgroundAudio.volume = 0.28;
    backgroundAudio.load();
    api.logPlayback({
      event_type: "background_rotated",
      overlay_file: file,
      overlay_lane: "background",
      channel: stateRef.current.activeTheme || "",
      arc_stage: getArcStage(),
      subliminal_shift_hz: backgroundShiftHz,
      subliminal_ripple_depth: backgroundRippleDepth,
    });
    return backgroundAudio.play().catch(() => null);
  }, [ensureOverlayContextFromGesture, ensureOverlayLaneRouting, getArcStage]);

  const pauseAllSessionAudio = useCallback(async () => {
    const sessionAudios = [
      mainVoiceAudioRef.current,
      overlayAudioRef.current,
      whisperLeftAudioRef.current,
      whisperRightAudioRef.current,
      subliminalAudioRef.current,
      backgroundAudioRef.current,
    ].filter(Boolean);
    pausedMediaLanesRef.current = sessionAudios.filter((audio) => (
      !audio.paused && !audio.ended && Boolean(audio.currentSrc || audio.getAttribute("src"))
    ));
    sessionAudios.forEach((audio) => {
      try { audio.pause(); } catch { }
    });
    hapticControllerRef.current?.pause?.();
    const controllerPause = audioControllerRef.current?.pause?.();
    const overlayContext = overlayAudioContextRef.current;
    const overlayPause = overlayContext?.state === "running"
      ? overlayContext.suspend().catch(() => null)
      : Promise.resolve();
    await Promise.all([controllerPause, overlayPause]);
  }, []);

  const resumePausedSessionAudio = useCallback(async () => {
    const overlayContext = overlayAudioContextRef.current;
    if (overlayContext?.state === "suspended") {
      await overlayContext.resume();
    }
    const pausedMedia = [...pausedMediaLanesRef.current];
    pausedMediaLanesRef.current = [];
    await Promise.all(pausedMedia.map((audio) => (
      audio.play().catch(() => null)
    )));
    hapticControllerRef.current?.resume?.(
      stateRef.current.currentTrack?.audio_scene || {},
    );
  }, []);

  const handlePlayPause = useCallback(async (requestedState = "toggle") => {
    try {
      const currentState = stateRef.current;
      const currentTrack = currentState.currentTrack || currentState.queue[0] || null;
      const voiceOnly = !currentTrack && Boolean(currentState.mainVoiceQueue?.length);
      if (!currentTrack && !voiceOnly) {
        mergeState({ status: "No playable track is available." });
        return;
      }

      const controller = audioControllerRef.current;
      const explicitState = typeof requestedState === "string" ? requestedState : "toggle";
      const anySessionAudioPlaying = Boolean(currentState.isPlaying || controller?.isPlaying?.());
      const shouldPause = explicitState === "pause"
        || (explicitState === "toggle" && anySessionAudioPlaying);
      if (shouldPause) {
        if (!anySessionAudioPlaying) return;
        sessionPausedAtRef.current = Date.now();
        await pauseAllSessionAudio();
        mergeState({ status: "Paused" });
        return;
      }
      if (explicitState === "play" && anySessionAudioPlaying) return;

      mergeState({ isBusy: true });
      // The authored hour score is driven by this clock. It used to be stamped
      // only after Skip/track-end, which left the initial music bed with no
      // scheduled main voice or whispers for its entire first play. Start the
      // score in the same user gesture that unlocks every Android audio lane.
      if (!sessionStartTimeRef.current) {
        sessionStartTimeRef.current = Date.now();
        firedSessionScriptEventKeysRef.current = new Set();
      }
      const transportWasPaused = Boolean(sessionPausedAtRef.current);
      if (transportWasPaused && sessionStartTimeRef.current) {
        sessionStartTimeRef.current += Date.now() - sessionPausedAtRef.current;
      }
      sessionPausedAtRef.current = null;
      const requiresLoad = !voiceOnly && controller?.currentTrackFile?.() !== currentTrack?.file;
      if (!voiceOnly && requiresLoad) {
        controller.loadTrack(currentTrack);
        controller.updateMediaSessionMetadata(currentTrack);
      } else if (!voiceOnly) {
        controller?.updateMediaSessionMetadata?.(currentTrack);
      }
      // Invoke primary media playback immediately inside the tap gesture.
      // Awaiting AudioContext setup first caused mobile browsers to expire the
      // user activation token and reject audio.play().
      const playNativePrimary = () => {
        const primaryAudio = audioRef.current;
        if (!primaryAudio) return Promise.reject(new Error("Primary audio element is unavailable"));
        if (!primaryAudio.currentSrc && currentTrack.audio_src) {
          primaryAudio.src = currentTrack.audio_src;
          primaryAudio.load();
        }
        return primaryAudio.play();
      };
      // Prefer the controller, but retry the same user-gesture media element
      // directly if optional controller setup rejects after native playback
      // has already begun. This makes the primary transport independent of
      // browser WebAudio capability differences.
      const controllerPlayback = voiceOnly ? null : controller?.play?.();
      const primaryPlayback = voiceOnly
        ? Promise.resolve(true)
        : controllerPlayback
          ? Promise.resolve(controllerPlayback).catch((error) => {
            console.warn("[AceLabReact] controller playback failed; retrying native deck", error);
            return playNativePrimary();
          })
          : playNativePrimary();
      // navigator.vibrate requires sticky activation and a visible document.
      // Start synchronously inside the Play tap; failures are best-effort.
      if (!transportWasPaused && !voiceOnly) {
        hapticControllerRef.current?.start?.(currentTrack.audio_scene || {});
      }
      const overlayContextReady = ensureOverlayContextFromGesture();
      const pausedLanePlayback = transportWasPaused
        ? overlayContextReady.then(() => resumePausedSessionAudio())
        : Promise.resolve();
      const brainwaveLabPlayback = overlayContextReady.then(() => (
        applyBrainwaveLabNow(brainwaveLabSettingsRef.current)
      ));
      // Prime first, then bind the real program. This mirrors the Director's
      // reliable manual pause/play workaround inside the original Play tap.
      const mainVoicePrime = transportWasPaused
        ? Promise.resolve(true)
        : primeOverlayLane(LANE_HYPNOSIS_SPOKEN_MAIN, mainVoiceAudioRef.current);
      const mainVoicePlayback = transportWasPaused
        ? pausedLanePlayback
        : currentState.mainVoiceQueue?.length
          ? Promise.all([overlayContextReady, mainVoicePrime]).then(() => (
            startMainVoiceFromGesture(
              currentState.mainVoiceQueue,
              currentState.sessionScript,
              currentState.sessionWelcomeTrack,
            )
          ))
          : mainVoicePrime;
      const backgroundAudio = backgroundAudioRef.current;
      const backgroundHadSource = Boolean(
        backgroundAudio?.currentSrc || backgroundAudio?.getAttribute("src"),
      );
      const backgroundPrime = backgroundAudio && !transportWasPaused
        ? primeOverlayLane(LANE_SUBLIMINAL_2, backgroundAudio)
        : Promise.resolve(false);
      let backgroundPlayback = null;
      if (
        !transportWasPaused
        && backgroundAudio
        && currentState.backgroundFiles?.length
        && isNsfwSessionState(currentState)
      ) {
        if (!backgroundHadSource) {
          // Rotate through ALL subliminal backgrounds instead of looping one.
          backgroundPlayback = Promise.all([overlayContextReady, backgroundPrime])
            .then(() => advanceBackgroundRotation());
        } else {
          backgroundPlayback = Promise.all([overlayContextReady, backgroundPrime]).then(() => {
            const routing = ensureOverlayLaneRouting(LANE_SUBLIMINAL_2);
            return routing?.shifter ? backgroundAudio.play().catch(() => null) : null;
          });
        }
      }
      const lanePrimes = transportWasPaused ? [] : [
        primeOverlayLane(LANE_OVERLAY, overlayAudioRef.current),
        primeOverlayLane(LANE_WHISPER_LEFT, whisperLeftAudioRef.current),
        primeOverlayLane(LANE_WHISPER_RIGHT, whisperRightAudioRef.current),
        primeOverlayLane(LANE_SUBLIMINAL_1, subliminalAudioRef.current),
      ];
      // Only the primary deck is transport-critical. Overlay priming,
      // AudioWorklet registration, and optional NSFW ambience are best-effort
      // side lanes; awaiting them made Play look broken when a mobile browser
      // delayed one auxiliary media element. Start them concurrently and let
      // the native deck determine the visible transport state.
      const auxiliaryPlayback = Promise.all([
        overlayContextReady,
        brainwaveLabPlayback,
        mainVoicePlayback,
        backgroundPrime,
        backgroundPlayback,
        pausedLanePlayback,
        ...lanePrimes,
      ]).catch((error) => {
        console.warn("[AceLabReact] optional playback lane failed", error);
      });
      await primaryPlayback;
      if (!voiceOnly && requiresLoad) {
        api.sendFeedback({
          event: "play",
          filename: currentTrack.file,
          category: currentTrack.category,
          theme: stateRef.current.activeTheme,
          persona_id: currentTrack.persona_id,
        }).catch((error) => {
          console.warn("[AceLabReact] play feedback failed", error);
        });
      }
      mergeState(voiceOnly
        ? { status: "Playing voice-first (no music bed)", isBusy: false, isPlaying: true }
        : { status: "Playing", isBusy: false });
      auxiliaryPlayback.catch(() => { });
    } catch (error) {
      console.error("[AceLabReact]", error);
      const detail = String(error?.message || error || "").trim();
      const blocked = error?.name === "NotAllowedError" || /interact|gesture|allowed/i.test(detail);
      mergeState({
        status: "Playback failed",
        gateMessage: blocked
          ? "Playback was blocked by the browser. Tap Play again to grant audio permission."
          : `The selected track could not start${detail ? `: ${detail}` : "."}`,
        isBusy: false,
      });
    }
  }, [
    applyBrainwaveLabNow,
    advanceBackgroundRotation,
    ensureOverlayContextFromGesture,
    ensureOverlayLaneRouting,
    mergeState,
    pauseAllSessionAudio,
    primeOverlayLane,
    resumePausedSessionAudio,
    startMainVoiceFromGesture,
  ]);

  const ensureContinuousNsfwBackground = useCallback(async () => {
    const currentState = stateRef.current;
    const backgroundAudio = backgroundAudioRef.current;
    if (!backgroundAudio) return null;
    if (
      !currentState.isPlaying
      || !isNsfwSessionState(currentState)
      || !currentState.backgroundFiles?.length
      || !isMainMusicPlaying(currentState, audioControllerRef.current)
    ) {
      backgroundAudio.pause();
      return null;
    }
    if (backgroundStartInFlightRef.current) return null;
    backgroundStartInFlightRef.current = true;
    try {
      await ensureOverlayContextFromGesture();
      const routing = ensureOverlayLaneRouting(LANE_SUBLIMINAL_2);
      if (!routing?.shifter || !routing?.gain) return null;
      const scene = brainwaveLabScene(
        resolveLiveAudioScene(currentState, liveBrainwaveSceneRef.current),
        brainwaveLabSettingsRef.current,
        sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0,
      );
      const now = overlayAudioContextRef.current?.currentTime || 0;
      applySubliminalBrainwaveScene(
        routing, scene, now, { background: true, carrierShiftHz: resolveSubliminalBackgroundShiftHz({ mode: "dark" }) },
      );
      if (!backgroundAudio.currentSrc || backgroundAudio.ended) {
        return await advanceBackgroundRotation();
      }
      if (backgroundAudio.paused) return await backgroundAudio.play().catch(() => null);
      return null;
    } finally {
      backgroundStartInFlightRef.current = false;
    }
  }, [advanceBackgroundRotation, ensureOverlayContextFromGesture, ensureOverlayLaneRouting]);

  useEffect(() => {
    // The NSFW mood carrier is infrastructure, not a scheduled accent. Keep it
    // alive across main-deck changes while scheduled spoken subliminals retain
    // their independent one-shot lane. A watchdog repairs mobile/media stalls.
    ensureContinuousNsfwBackground()?.catch?.(() => { });
    if (!state.isPlaying || !isNsfwSessionState(state)) return undefined;
    const watchdog = window.setInterval(() => {
      ensureContinuousNsfwBackground()?.catch?.(() => { });
    }, 2_000);
    return () => window.clearInterval(watchdog);
  }, [
    ensureContinuousNsfwBackground,
    state.activeTheme,
    state.backgroundFiles?.length,
    state.currentTrack?.file,
    state.isPlaying,
    state.themes,
  ]);

  // ── Covert Hypnosis Mode ─────────────────────────────────────────────────
  // Continuously rotates short, high-impact spoken subliminal suggestion atoms
  // on the subliminal lane while music plays, placed in 3D space around the
  // head (BT headphones) so they arrive as a moving, surrounding presence.
  const startCovertEngine = useCallback(() => {
    if (!stateRef.current.covertMode) return;
    if (covertEngineRef.current) return;
    covertEngineRef.current = createCovertEngine({
      api,
      getState: () => stateRef.current,
      overlayContext: () => overlayAudioContextRef.current,
      ensureWorklet: async () => {
        if (subliminalWorkletReadyRef.current) return true;
        if (!overlayAudioContextRef.current) return false;
        try {
          subliminalWorkletReadyRef.current = await ensureSubliminalWorklet(overlayAudioContextRef.current);
        } catch {
          subliminalWorkletReadyRef.current = false;
        }
        return subliminalWorkletReadyRef.current;
      },
      ensureRouting: (lane) => ensureOverlayLaneRouting(lane),
      applyScene: (routing, scene, t) => applySubliminalBrainwaveScene(routing, scene, t),
      resolveScene: () => brainwaveLabScene(
        resolveLiveAudioScene(stateRef.current, liveBrainwaveSceneRef.current),
        brainwaveLabSettingsRef.current,
        sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0,
      ),
      resolveGain: (scene) => resolveSubliminalGain(scene),
      // Arousal-pulse state + geometry, resolved from the same session clock
      // the covert engine uses, so atoms land deterministically on the drop.
      resolvePulse: () => {
        const elapsedS = sessionStartTimeRef.current
          ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0;
        return resolveArousalPulseHz(elapsedS, brainwaveLabSettingsRef.current);
      },
      getElapsedMs: () => (sessionStartTimeRef.current
        ? Date.now() - sessionStartTimeRef.current : 0),
      audioForLane: (lane) => (lane === LANE_SUBLIMINAL_1 ? subliminalAudioRef.current : null),
      logPlayback: (payload) => api.logPlayback(payload).catch(() => { }),
      isPlaying: () => Boolean(stateRef.current.isPlaying),
      isNsfw: () => isNsfwSessionState(stateRef.current),
      isMainMusic: () => isMainMusicPlaying(stateRef.current, audioControllerRef.current),
    });
    covertEngineRef.current.start();
  }, [ensureOverlayLaneRouting]);

  const stopCovertEngine = useCallback(() => {
    if (covertEngineRef.current) {
      covertEngineRef.current.stop();
      covertEngineRef.current = null;
    }
  }, []);

  // Start/stop the covert engine with playback state.
  useEffect(() => {
    if (state.isPlaying && state.covertMode) {
      startCovertEngine();
    } else {
      stopCovertEngine();
    }
  }, [state.isPlaying, state.covertMode, startCovertEngine, stopCovertEngine]);

  const toggleCovertMode = useCallback(() => {
    mergeState({ covertMode: !stateRef.current.covertMode });
  }, [mergeState]);

  const playNextTrack = useCallback(async ({ recordGap, fromPlaybackError = false }) => {
    // Double-fire guard: audio "ended" can fire twice on glitchy streams.
    if (advanceInFlightRef.current) return;
    advanceInFlightRef.current = true;
    try {
      // A manual Skip means the opening welcome has been handled for this
      // session, even if its scripted event has not reached its cue time yet.
      if (recordGap) {
        sessionWelcomePlaybackGuardRef.current.markPlayed({ role: "session_welcome" });
        countdownPlaybackRef.current = { active: false, eventKey: "" };
      }
      // A manual skip is a hard boundary: stop every old voice immediately.
      // Preserving the audible lanes here lets the welcome clip continue over
      // the next bed, which is exactly how a skipped intro came back later.
      clearOverlayLanes({ preserveAudibleVoices: !recordGap && !fromPlaybackError });
      firedOverlayCueKeysRef.current = new Set();
      const currentState = stateRef.current;

      if (currentState.currentTrack?.file && currentState.hypnosisProtocol?.protocol_id && !experimentOutcomeRecordedRef.current) {
        experimentOutcomeRecordedRef.current = true;
        api.recordHypnosisOutcome({
          metric: "track_completed",
          value: recordGap || fromPlaybackError ? 0 : 1,
          channel: currentState.activeTheme || "all",
          context: {
            track_file: currentState.currentTrack.file,
            transition: recordGap ? "skip" : fromPlaybackError ? "playback_error" : "natural_end",
          },
        }).catch(() => { });
      }

      // Queue empty — reload the library for the active channel rather than stopping.
      if (!currentState.queue.length) {
        mergeState({ status: "Refreshing queue…", isBusy: true });
        const channel = currentState.activeTheme || "all";
        try {
          const fresh = await api.fetchLibrary(channel, { lite: false });
          const hypnosisProtocol = fresh.hypnosis_protocol || currentState.hypnosisProtocol || null;
          api.setHypnosisProtocol(hypnosisProtocol);
          const elapsedSeconds = sessionStartTimeRef.current ? (Date.now() - sessionStartTimeRef.current) / 1000 : 0;
          const freshTracks = (Array.isArray(fresh.tracks) ? fresh.tracks : [])
            .map((track) => applyHypnosisProtocol(track, hypnosisProtocol, elapsedSeconds));
          if (freshTracks.length) {
            mergeState({ queue: freshTracks, queueIndex: -1, isBusy: false, hypnosisProtocol });
            advanceInFlightRef.current = false;
            playNextTrack({ recordGap: false });
            return;
          }
        } catch (_e) { /* fall through to exhausted */ }
        mergeState({ status: "Your session is being prepared…", isBusy: false });
        advanceInFlightRef.current = false;
        return;
      }

      const playableQueue = fromPlaybackError
        ? currentState.queue.filter((track) => track.file !== currentState.currentTrack?.file)
        : currentState.queue;

      if (!playableQueue.length) {
        audioControllerRef.current?.pause?.();
        hapticControllerRef.current?.stop?.();
        audioControllerRef.current?.clear?.();
        mergeState({
          queue: [],
          queueIndex: -1,
          currentTrack: null,
          queueCountLabel: "0 tracks",
          status: "Refreshing queue…",
          isBusy: false,
        });
        advanceInFlightRef.current = false;
        playNextTrack({ recordGap: false });
        return;
      }

      // Record the outgoing track in the recently-played ring buffer (max 50).
      if (currentState.currentTrack?.file) {
        const ring = recentlyPlayedRef.current;
        if (!ring.includes(currentState.currentTrack.file)) {
          ring.push(currentState.currentTrack.file);
          if (ring.length > 50) ring.shift();
        }
      }
      // Stamp session start on first advance.
      if (!sessionStartTimeRef.current) sessionStartTimeRef.current = Date.now();

      const nextIndex = currentState.currentTrack && !fromPlaybackError
        ? currentState.queueIndex + 1
        : 0;
      // When we've reached the end of the queue, wrap but also trigger a
      // background refresh so the next cycle has fresh content.
      const wrapping = nextIndex >= currentState.queue.length;
      const safeIndex = wrapping ? 0 : nextIndex;
      const nextTrack = fromPlaybackError ? playableQueue[0] : currentState.queue[safeIndex];
      const history = pushTrackHistory(currentState.trackHistory, currentState.currentTrack);
      const updatedQueue = fromPlaybackError ? playableQueue : currentState.queue;
      const updatedQueueIndex = updatedQueue.findIndex((track) => track.file === nextTrack.file);

      // Queue management: on wrap force a full reload for fresh content;
      // on low queue (≤3 remaining) do a background append.
      const remainingAfterAdvance = updatedQueue.length - (updatedQueueIndex + 1);
      if (recordGap) {
        // An explicit Skip is also a request for fresh material. Fetch immediately
        // instead of waiting for the low-queue threshold or periodic refresh.
        refreshQueueInBackground();
      } else if (wrapping) {
        // Reload so the next cycle isn't just the same tracks repeated.
        const _wrapChannel = currentState.activeTheme || "all";
        setTimeout(() => loadLibrary({ channel: _wrapChannel, preserveCurrentTrack: true }), 0);
      } else if (remainingAfterAdvance <= 3) {
        refreshQueueInBackground();
      }

      mergeState({
        queue: updatedQueue,
        queueIndex: updatedQueueIndex,
        currentTrack: nextTrack,
        trackHistory: history,
        canGoBack: history.length > 0,
        queueCountLabel: `${updatedQueue.length} track${updatedQueue.length === 1 ? "" : "s"}`,
        isBusy: true,
        featuredMate: resolveFeaturedMate({
          personas: currentState.personas || [],
          session: currentState.session || {},
          currentTrack: nextTrack,
          activeTheme: currentState.activeTheme,
        }),
      });

      if (recordGap && currentState.currentTrack) {
        sendFeedbackNonBlocking({
          event: "request_more",
          filename: currentState.currentTrack.file,
          category: currentState.currentTrack.category,
          theme: currentState.activeTheme,
          persona_id: currentState.currentTrack.persona_id,
          queue_minutes: Math.ceil(
            updatedQueue
              .slice(updatedQueueIndex + 1)
              .reduce((seconds, track) => seconds + Number(track.duration_seconds || 0), 0) / 60,
          ),
        }, "fresh-content request");
      }

      audioControllerRef.current?.loadTrack?.(nextTrack);
      audioControllerRef.current?.updateMediaSessionMetadata?.(nextTrack);
      await audioControllerRef.current?.play?.();
      experimentOutcomeRecordedRef.current = false;
      api.logPlayback({
        event_type: "track_started",
        track_file: nextTrack.file || "",
        track_title: nextTrack.title || "",
        channel: currentState.activeTheme || "",
        arc_stage: getArcStage(),
        queue_index: updatedQueueIndex,
        queue_size: updatedQueue.length,
        layer_status: audioControllerRef.current?.layerStatus?.() || {},
      });
      if (!fromPlaybackError) {
        sendFeedbackNonBlocking({
          event: "play",
          filename: nextTrack.file,
          category: nextTrack.category,
          theme: currentState.activeTheme,
          persona_id: nextTrack.persona_id,
        }, "play feedback");
      }
      mergeState({ status: "Playing", isBusy: false });

      // Cache the next three distinct music tracks so Skip has a real runway.
      prefetchAudioRefs.current.forEach((prefetch, offset) => {
        const upcoming = updatedQueue[updatedQueueIndex + offset + 1];
        if (!upcoming?.audio_src) {
          prefetch.removeAttribute("src");
          prefetch.load();
          return;
        }
        prefetch.src = upcoming.audio_src;
        prefetch.preload = "auto";
        prefetch.load();
      });
      // If dislike exhausted the queue, resume the instant a fresh music bed
      // appears instead of waiting for another user gesture or page refresh.
      if (!currentState.currentTrack && novelTracks[0]) {
        const recoveryTrack = novelTracks[0];
        audioControllerRef.current?.loadTrack?.(recoveryTrack);
        audioControllerRef.current?.updateMediaSessionMetadata?.(recoveryTrack);
        await audioControllerRef.current?.play?.().catch(() => null);
        mergeState({ status: "Fresh track ready", isBusy: false });
      }
    } catch (error) {
      console.error("[AceLabReact]", error);
      // Clear the error gate automatically after 4 s so the UI doesn't stay frozen.
      mergeState({ status: "Track error — retrying…", gateMessage: "", isBusy: false });
      setTimeout(() => playNextTrack({ recordGap: false, fromPlaybackError: true }), 2000);
    } finally {
      advanceInFlightRef.current = false;
    }
  }, [mergeState, pushTrackHistory, resolveFeaturedMate, refreshQueueInBackground, loadLibrary]);

  const setDeepeningAssist = useCallback(async (enabled) => {
    const current = stateRef.current;
    try {
      const preferences = await api.updatePreferences({
        ...(current.preferences || {}),
        deepening_assist: Boolean(enabled),
      });
      patchNestedState("preferences", preferences);
      patchNestedState("session", {
        deepening_assist: Boolean(enabled),
        ...(enabled ? {
          induction_enabled: true,
          induction_complete: false,
          induction_clips_served: 0,
          induction_length_minutes: 20,
        } : {}),
      });
      mergeState({
        status: enabled ? "First Trance Assist engaged" : "Standard deepening",
        gateMessage: enabled
          ? "No need to force anything. The guidance will establish one small, unmistakable shift at a time."
          : "Deepening Assist is off.",
      });
      await loadLibrary({
        channel: current.activeTheme || "all",
        preserveCurrentTrack: false,
        resumePlayback: current.isPlaying,
      });
    } catch (error) {
      console.warn("[AceLabReact] deepening assist update failed", error);
    }
  }, [loadLibrary, mergeState, patchNestedState]);

  const playPreviousTrack = useCallback(async () => {
    try {
      clearOverlayLanes();
      firedOverlayCueKeysRef.current = new Set();
      const currentState = stateRef.current;
      const history = [...(currentState.trackHistory || [])];
      const previousTrack = history.pop() || null;
      if (!previousTrack) return;

      const previousIndex = currentState.queue.findIndex((track) => track.file === previousTrack.file);
      mergeState({
        queueIndex: previousIndex,
        currentTrack: previousTrack,
        trackHistory: history,
        canGoBack: history.length > 0,
        isBusy: true,
        featuredMate: resolveFeaturedMate({
          personas: currentState.personas || [],
          session: currentState.session || {},
          currentTrack: previousTrack,
          activeTheme: currentState.activeTheme,
        }),
      });

      audioControllerRef.current?.loadTrack?.(previousTrack);
      audioControllerRef.current?.updateMediaSessionMetadata?.(previousTrack);
      await audioControllerRef.current?.play?.();
      mergeState({ status: "Playing", isBusy: false });
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Back failed",
        gateMessage: "The previous track could not be restored. Check the browser console and backend logs if it continues.",
        isBusy: false,
      });
    }
  }, [mergeState, resolveFeaturedMate]);

  /**
   * The trance report. This is the Director's "YES, this hypnotized me" signal,
   * delivered through the existing thumbs buttons so no pop-up is ever needed.
   *
   * It is deliberately NOT a like/dislike. A like says "I enjoyed this clip"; a
   * trance report says "this put me under". Those are different questions, and
   * only the second one tells us whether the induction actually worked. The
   * report is recorded against the live playback condition (protocol, arc stage,
   * layer status, elapsed time) so a confirmed trance can be traced back to the
   * exact technique, voice, and audio scene that produced it.
   */
  const reportTranceOutcome = useCallback(async (hypnotized) => {
    const value = normalizeTranceResponse(hypnotized);
    if (value === null) return;
    const current = stateRef.current;
    const elapsedSessionSeconds = sessionStartTimeRef.current
      ? Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000)
      : 0;
    const layerStatus = audioControllerRef.current?.layerStatus?.() || {};
    const transportProfile = resolveAudioTransportProfile({
      context: overlayAudioContextRef.current,
      media: subliminalAudioRef.current,
      track: current.currentTrack,
    });
    try {
      await api.recordHypnosisOutcome({
        metric: TRANCE_OUTCOME_METRIC,
        value,
        channel: current.activeTheme || current.sessionRequest?.setting || "custom_session",
        context: buildOutcomeContext({
          track: current.currentTrack,
          elapsedSessionSeconds,
          playbackState: current.isPlaying ? "playing" : "paused",
          layerStatus,
          arcStage: getArcStage(),
          condition: {
            id: current.hypnosisProtocol?.protocol_id,
            block: current.hypnosisProtocol?.block_index,
            label: current.hypnosisProtocol?.label || current.activeThemeLabel,
          },
          reportPhase: resolveOutcomeReportPhase({
            sessionStarted: sessionStartTimeRef.current === null ? false : true,
          }),
          transportProfile,
        }),
      });
      mergeState({
        status: value === 1 ? "Trance confirmed — logged" : "Resistance logged",
        gateMessage: value === 1
          ? "Recorded. The exact technique, voice, and audio scene that took you under is now on file."
          : "Recorded. That combination did not land — it will be weighted down.",
      });
    } catch (error) {
      console.warn("[AceLabReact] trance report failed", error);
      mergeState({ status: "Trance report failed", gateMessage: "The report could not be saved." });
    }
  }, [getArcStage, mergeState]);

  const handleLikeCurrentTrack = useCallback(async () => {
    try {
      const currentTrack = stateRef.current.currentTrack;
      if (!currentTrack) return;
      mergeState({ isBusy: true });
      await Promise.all([
        api.favoriteTrack(currentTrack.file),
        api.sendFeedback({
          event: "favorite",
          filename: currentTrack.file,
          category: currentTrack.category,
          theme: stateRef.current.activeTheme,
          persona_id: currentTrack.persona_id,
        }),
      ]);
      mergeState({ status: "Saved to favorites", isBusy: false });
      // The same gesture is also the trance report: a favourite on a hypnosis
      // clip means it worked. Recorded separately from the like so the two
      // signals never contaminate each other.
      void reportTranceOutcome(true);
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Favorite failed",
        gateMessage: "The track could not be saved to favorites.",
        isBusy: false,
      });
    }
  }, [mergeState, reportTranceOutcome]);

  const handleDislikeCurrentTrack = useCallback(async () => {
    try {
      const currentState = stateRef.current;
      const currentTrack = currentState.currentTrack;
      if (!currentTrack) return;

      // Thumbs-down is an immediate hard takedown of this music Drop.  Stop it
      // before any network work so a slow mobile connection cannot leave the
      // rejected audio playing.
      audioControllerRef.current?.pause?.();

      const nextQueue = rankDislikeAlternatives(
        currentState.queue,
        currentTrack,
        currentState.trackHistory,
      );
      const nextTrack = nextQueue[0] || null;
      const nextHistory = (currentState.trackHistory || []).filter(
        (track) => track?.file && track.file !== currentTrack.file,
      );

      mergeState({ isBusy: true });
      // A thumbs-down on a hypnosis clip is a resistance report: this did not
      // put me under. Recorded before the takedown so the signal survives even
      // if the replacement fetch fails.
      void reportTranceOutcome(false);
      sendFeedbackNonBlocking({
        event: "dislike",
        filename: currentTrack.file,
        category: currentTrack.category,
        theme: currentState.activeTheme,
        persona_id: currentTrack.persona_id,
      }, "Drop takedown");

      // The dislike is weight-10 replacement demand. Poll aggressively enough
      // for newly generated music to enter this live session without a refresh.
      refreshQueueInBackground({ musicOnly: true });
      if (freshDemandRetryRef.current) window.clearInterval(freshDemandRetryRef.current);
      freshDemandRetryRef.current = window.setInterval(() => {
        if ((stateRef.current.queue || []).length >= 8) {
          window.clearInterval(freshDemandRetryRef.current);
          freshDemandRetryRef.current = null;
          return;
        }
        refreshQueueInBackground({ musicOnly: true });
      }, 10_000);

      mergeState({
        queue: nextQueue,
        queueIndex: nextTrack ? 0 : -1,
        currentTrack: nextTrack,
        trackHistory: nextHistory,
        canGoBack: nextHistory.length > 0,
        queueCountLabel: `${nextQueue.length} track${nextQueue.length === 1 ? "" : "s"}`,
        status: nextTrack ? "Drop taken offline" : "Drop offline — finding replacement",
        isBusy: true,
        featuredMate: resolveFeaturedMate({
          personas: currentState.personas || [],
          session: currentState.session || {},
          currentTrack: nextTrack,
          activeTheme: currentState.activeTheme,
        }),
      });

      if (nextTrack) {
        audioControllerRef.current?.loadTrack?.(nextTrack);
        audioControllerRef.current?.updateMediaSessionMetadata?.(nextTrack);
        await audioControllerRef.current?.play?.();
        mergeState({ status: voiceOnly ? "Playing voice-first (no music bed)" : "Playing", isBusy: false });
      } else {
        audioControllerRef.current?.pause?.();
        hapticControllerRef.current?.stop?.();
        audioControllerRef.current?.clear?.();
        mergeState({ isBusy: false, canGoBack: nextHistory.length > 0 });
      }
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Dislike failed",
        gateMessage: "The current track could not be skipped cleanly.",
        isBusy: false,
      });
    }
  }, [mergeState, rankDislikeAlternatives, refreshQueueInBackground, resolveFeaturedMate, reportTranceOutcome]);

  const handleThemeChange = useCallback(async (theme) => {
    const selectionId = ++themeSelectionIdRef.current;
    try {
      if (freshDemandRetryRef.current) {
        window.clearInterval(freshDemandRetryRef.current);
        freshDemandRetryRef.current = null;
      }
      // Disconnect the old channel synchronously, while still inside the user
      // gesture. Keep this SAME media element authorized across the network
      // awaits; clearing it later used to discard mobile autoplay permission.
      clearOverlayLanes();
      audioControllerRef.current?.pause?.();
      hapticControllerRef.current?.stop?.();
      const primaryAudio = audioRef.current;
      if (primaryAudio) {
        primaryAudio.pause();
        primaryAudio.src = SILENT_WAV_DATA_URI;
        primaryAudio.loop = true;
        primaryAudio.load();
        primaryAudio.play().catch(() => null);
      }
      ensureOverlayContextFromGesture();
      mergeState({ status: "Switching theme…", isBusy: true });
      const [themePayload, personas] = await Promise.all([
        api.setTheme(theme),
        api.fetchPersonas(theme),
      ]);
      if (selectionId !== themeSelectionIdRef.current) return;

      mergeState({
        personas,
        activeTheme: theme,
        activeThemeLabel: themePayload.theme || theme,
        tags: themePayload.tags || stateRef.current.tags,
        trackHistory: [],
        canGoBack: false,
        tipFeedback: "",
      });

      // Do not pause/clear here: the primed element carries the click's playback
      // permission. loadLibrary force-rebinds it to the selected channel.
      await loadLibrary({
        channel: theme,
        preserveCurrentTrack: false,
        resumePlayback: true,
      });
    } catch (error) {
      console.error("[AceLabReact]", error);
      audioControllerRef.current?.pause?.();
      hapticControllerRef.current?.stop?.();
      audioControllerRef.current?.clear?.();
      mergeState({
        status: "Theme change failed",
        gateMessage: "The requested theme could not be activated.",
        isBusy: false,
      });
    }
  }, [clearOverlayLanes, ensureOverlayContextFromGesture, loadLibrary, mergeState]);

  const handleAuthAction = useCallback(() => {
    const session = stateRef.current.session || {};
    window.location.assign(session.authenticated ? "/auth/logout" : "/auth/login");
  }, []);

  const openWallet = useCallback(async () => {
    setWalletOpen(true);
    setWalletMessage("");
    try {
      const data = await api.fetchWallet();
      setWalletData(data);
      if (data.paypal_configured && data.paypal_client_id && !window.__paypalScriptLoaded) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(data.paypal_client_id)}&currency=USD`;
          script.onload = () => { window.__paypalScriptLoaded = true; resolve(); };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
    } catch (err) {
      setWalletMessage("Could not load wallet. Try again.");
    }
  }, []);

  const handleBuyPack = useCallback(async (packId) => {
    const session = stateRef.current.session || {};
    if (!session.authenticated) {
      window.location.assign("/auth/login");
      return;
    }
    setWalletBusy(true);
    setWalletMessage("");
    try {
      const { order_id: orderId } = await api.createPaypalOrder(packId);
      if (!window.paypal) {
        setWalletMessage("PayPal SDK not loaded. Refresh and try again.");
        setWalletBusy(false);
        return;
      }
      window.paypal.Buttons({
        createOrder: () => orderId,
        onApprove: async () => {
          try {
            const result = await api.capturePaypalOrder(orderId);
            mergeState({ session: { ...stateRef.current.session, chips: result.chips_total } });
            setWalletData((prev) => ({ ...prev, chips: result.chips_total }));
            setWalletMessage(
              `${result.chips_awarded} Prop Chips added! Total: ${result.chips_total}. ` +
              `Remember: No Monetary Value — these are Prop Money only.`
            );
          } catch (err) {
            setWalletMessage(`Capture failed: ${err.message}`);
          } finally {
            setWalletBusy(false);
          }
        },
        onCancel: () => {
          setWalletMessage("Purchase cancelled.");
          setWalletBusy(false);
        },
        onError: (err) => {
          setWalletMessage(`PayPal error: ${err}`);
          setWalletBusy(false);
        },
      }).render("#paypal-button-container");
    } catch (err) {
      setWalletMessage(`Order creation failed: ${err.message}`);
      setWalletBusy(false);
    }
  }, [mergeState]);

  const handleMateFollow = useCallback(async () => {
    try {
      const mate = stateRef.current.featuredMate;
      if (!mate?.persona_id || mate.following) return;
      mergeState({ isBusy: true, tipFeedback: "" });
      await api.followPersona(mate.persona_id);
      updateFeaturedMate((current) => ({
        ...current,
        following: true,
        follower_count: Number(current.follower_count || 0) + 1,
        signal: `Following ${current.name}. Their future sessions now count extra.`,
      }));
      mergeState({ isBusy: false, tipFeedback: `${mate.name} noticed you.` });
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Follow failed",
        gateMessage: "That HypnoMate could not be followed right now.",
        isBusy: false,
      });
    }
  }, [mergeState, updateFeaturedMate]);

  const stopMateSample = useCallback(() => {
    const audio = sampleAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      } catch {
        // preview cleanup is best-effort
      }
    }
    setSampleStatus("idle");
  }, []);

  const handleMateSample = useCallback(async (personaId) => {
    // Toggle: a second tap stops the audition.
    if (sampleStatus !== "idle") {
      stopMateSample();
      return;
    }
    if (!personaId) return;
    if (!sampleAudioRef.current) sampleAudioRef.current = new Audio();
    const audio = sampleAudioRef.current;
    setSampleStatus("loading");
    try {
      const payload = await api.fetchPersonaSample(personaId);
      const src = payload?.sample?.audio_src;
      if (!src) throw new Error("no sample in response");
      audio.src = src;
      audio.volume = 0.9;
      audio.onended = () => setSampleStatus("idle");
      await audio.play();
      setSampleStatus("playing");
    } catch (error) {
      console.warn("[AceLabReact] HypnoMate sample failed", error);
      mergeState({ tipFeedback: "No voice sample available for this HypnoMate yet." });
      setSampleStatus("idle");
    }
  }, [mergeState, sampleStatus, stopMateSample]);

  const handleMateSelect = useCallback(async (personaId) => {
    stopMateSample();
    const persona = (stateRef.current.personas || []).find((entry) => entry.persona_id === personaId);
    if (!persona) return;
    // Warm the voice signature immediately: if it isn't rendered yet, this
    // call enqueues a priority render on the daemon; if it is, the browser
    // caches it so the Sample button responds instantly.
    api.fetchPersonaSample(personaId).catch(() => { });
    try {
      mergeState({ isBusy: true, tipFeedback: `Calling ${persona.name || "your HypnoMate"}…` });
      const response = await api.selectPersona(personaId);
      const session = { ...(stateRef.current.session || {}), active_persona_id: response.active_persona_id };
      mergeState({
        session,
        featuredMate: resolveFeaturedMate({
          personas: stateRef.current.personas,
          session,
          currentTrack: null,
          activeTheme: stateRef.current.activeTheme,
        }),
        isBusy: false,
        tipFeedback: `${persona.name || "Your HypnoMate"} is now conducting this session.`,
      });
    } catch (error) {
      console.error("[AceLabReact] HypnoMate selection failed", error);
      mergeState({ isBusy: false, tipFeedback: "That HypnoMate could not take over yet." });
    }
  }, [mergeState, resolveFeaturedMate, stopMateSample]);

  const handleMateGold = useCallback(async () => {
    try {
      const currentState = stateRef.current;
      const mate = currentState.featuredMate;
      if (!mate?.persona_id) return;
      mergeState({ isBusy: true, tipFeedback: "" });
      const response = await api.goldStarPersona({
        personaId: mate.persona_id,
        filename: currentState.currentTrack?.file || "",
        category: currentState.currentTrack?.category || "",
        theme: currentState.activeTheme,
      });
      mergeFeaturedMate(response.persona);
      mergeState({
        isBusy: false,
        tipFeedback: response.awarded
          ? `${mate.name} sent you deeper.`
          : `${mate.name} has already been honored for this moment.`,
      });
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Deep signal failed",
        gateMessage: "That deep-moment signal could not be recorded.",
        isBusy: false,
      });
    }
  }, [mergeFeaturedMate, mergeState]);

  const handleMateTip = useCallback(async (amount) => {
    try {
      const mate = stateRef.current.featuredMate;
      if (!mate?.persona_id || !amount) return;
      mergeState({ isBusy: true, tipFeedback: "" });
      const response = await api.tipPersona({
        personaId: mate.persona_id,
        amount,
        message: `Modern player quick tip for ${mate.name}`,
      });
      mergeFeaturedMate(response.persona);
      mergeState({
        isBusy: false,
        tipFeedback: `${mate.name} received ${amount} coin${amount === 1 ? "" : "s"}. Fresh inspired-by content gets easier to justify.`,
      });
    } catch (error) {
      console.error("[AceLabReact]", error);
      mergeState({
        status: "Tip failed",
        gateMessage: "That tip could not be sent right now.",
        isBusy: false,
      });
    }
  }, [mergeFeaturedMate, mergeState]);

  const handleAccessGate = useCallback(() => {
    const session = stateRef.current.session || {};
    if (!session.authenticated) return handleAuthAction();
    return null;
  }, [handleAuthAction]);

  useEffect(() => {
    // Hidden prefetch runway — cache three upcoming main-deck tracks.
    prefetchAudioRefs.current = Array.from({ length: 3 }, () => {
      const prefetch = new Audio();
      prefetch.preload = "auto";
      prefetch.style.display = "none";
      return prefetch;
    });

    const controller = new AudioDeckController(audioRef.current);
    const hapticController = new HapticHeartbeatController();
    audioControllerRef.current = controller;
    hapticControllerRef.current = hapticController;
    controller.onPlaybackStateChanged((isPlaying) => {
      mergeState({ isPlaying });
    });
    controller.onTrackEnded(() => {
      playNextTrack({ recordGap: false });
    });
    controller.onTrackError(() => {
      // Auto-recover silently — error is logged in playNextTrack's catch block.
      setTimeout(() => playNextTrack({ recordGap: false, fromPlaybackError: true }), 1500);
    });
    return () => {
      controller.destroy();
      hapticController.destroy();
      hapticControllerRef.current = null;
      if (freshDemandRetryRef.current) {
        window.clearInterval(freshDemandRetryRef.current);
        freshDemandRetryRef.current = null;
      }
      prefetchAudioRefs.current.forEach((prefetch) => {
        prefetch.removeAttribute("src");
        prefetch.load();
      });
      prefetchAudioRefs.current = [];
    };
  }, [mergeState, playNextTrack]);

  useEffect(() => {
    const controller = audioControllerRef.current;
    if (!controller) return undefined;
    controller.setTransportHandlers({
      play: () => handlePlayPause("play"),
      pause: () => handlePlayPause("pause"),
    });
    return () => controller.setTransportHandlers(null);
  }, [handlePlayPause]);

  useEffect(() => {
    hapticControllerRef.current?.applyScene?.(state.currentTrack?.audio_scene || {});
  }, [state.currentTrack?.file, state.currentTrack?.audio_scene]);

  useEffect(() => {
    if (!state.currentTrack?.file) return undefined;
    const currentTrack = state.currentTrack;
    const schedule = Array.isArray(currentTrack.resolved_overlay_schedule)
      ? currentTrack.resolved_overlay_schedule
      : [];
    // Only skip the fetch if we already got a non-empty schedule back.
    // An empty result (no overlay pool match) should not permanently block
    // a retry — the daemon may stock new overlays before the next track ends.
    const alreadyFetched = overlayScheduleFetchesRef.current.has(currentTrack.file);
    if (schedule.length || alreadyFetched) {
      return undefined;
    }
    overlayScheduleFetchesRef.current.add(currentTrack.file);
    // Send the last 40 heard overlay clips so schedule resolution rotates the
    // pool instead of re-picking what this listener just heard.
    const overlayExclude = recentlyPlayedOverlayRef.current.slice(-40).join(",");
    api.fetchOverlaySchedule(currentTrack.file, state.activeTheme || "all", overlayExclude)
      .then((payload) => {
        const resolved = Array.isArray(payload?.resolved_overlay_schedule)
          ? payload.resolved_overlay_schedule
          : [];
        if (resolved.length) {
          mergeResolvedOverlaySchedule(currentTrack.file, resolved);
        } else {
          // Empty result — remove the gate so a later render can retry
          // (e.g. when overlays are stocked mid-track by the daemon).
          overlayScheduleFetchesRef.current.delete(currentTrack.file);
        }
      })
      .catch((error) => {
        console.warn("[AceLabReact] overlay schedule fetch failed", error);
        // Remove gate on network error so playback can retry.
        overlayScheduleFetchesRef.current.delete(currentTrack.file);
      });
    return undefined;
  }, [mergeResolvedOverlaySchedule, state.activeTheme, state.currentTrack?.file, state.currentTrack?.resolved_overlay_schedule]);
  // NOTE: `state.isPlaying` is deliberately NOT a dependency. `tick` already
  // gates on `stateRef.current.isPlaying`, so play/pause does not change what
  // this effect computes. Listing it re-ran the effect on every pause and
  // resume — which, together with the unconditional cue-guard reset it used to
  // perform, re-armed cues that were already playing and stacked voices on top
  // of each other.

  useEffect(() => {
    if (!state.currentTrack?.file) {
      firedOverlayCueKeysRef.current = new Set();
      clearOverlayLanes();
      return undefined;
    }
    // Capture the file identity for this effect invocation so stale
    // closures from the previous track cannot fire cues for the new one.
    const effectTrackFile = state.currentTrack.file;
    // Clear the cue guard and rebuild the starting set ONLY on a genuine track
    // change. Clearing them unconditionally (the old behaviour) meant every
    // isPlaying/sessionScript-driven re-run of this effect re-armed cues that
    // had already played, stacking a new voice on top of the one still running.
    if (overwatchTrackFileRef.current !== effectTrackFile) {
      overwatchTrackFileRef.current = effectTrackFile;
      firedOverlayCueKeysRef.current = new Set();
      whisperEarRef.current = "left";
      overlayDirectorRef.current.lastStartedByLane = {};
    }
    startingOverlayCueKeysRef.current = new Set();
    // A reusable spoken atom belongs to the continuous hypnosis program, not
    // to the exact music-file boundary. Let every audible sentence and whisper
    // finish naturally while only the scheduled subliminal one-shot resets.
    clearOverlayLanes({
      preserveAudibleVoices: true,
      preserveSubliminal: Boolean(stateRef.current.sessionScript?.subliminal?.continuous_while_music),
    });
    // Tracks pending-retry cues: cueKey → first-seen timestamp (ms).
    // If a lane is busy when a cue fires, we back off and retry within
    // the cue window instead of silently dropping it.
    const pendingRetry = new Map();
    const tick = () => {
      if (!stateRef.current.isPlaying) return;
      const mainAudio = audioRef.current;
      const currentTrack = stateRef.current.currentTrack;
      // Guard: abort if the track has already changed.
      if (!mainAudio || !currentTrack?.file || currentTrack.file !== effectTrackFile) return;
      const schedule = Array.isArray(currentTrack.resolved_overlay_schedule)
        ? currentTrack.resolved_overlay_schedule
        : [];
      if (!schedule.length) return;
      const nowMs = Math.max(0, Math.floor((mainAudio.currentTime || 0) * 1000));
      // Keep the low-salience lanes relative to the foreground program. A
      // fixed full-scale ceiling is not a perceptual contract: when the main
      // voice starts or pauses, the same sub clip can suddenly become easy to
      // understand. This lightweight control follows the real transport
      // state and smooths a 10 dB duck while foreground speech is active.
      const mainVoiceActive = Boolean(
        mainVoiceAudioRef.current
        && !mainVoiceAudioRef.current.paused
        && mainVoiceAudioRef.current.currentSrc,
      );
      const subliminalContext = overlayAudioContextRef.current;
      const subliminalNow = subliminalContext?.currentTime || 0;
      for (const laneName of [LANE_SUBLIMINAL_1, LANE_SUBLIMINAL_2]) {
        const lane = overlayLaneRefs.current[laneName];
        const baseGain = Number(lane?.subliminalBaseGain);
        if (!lane?.gain?.gain || !Number.isFinite(baseGain)) continue;
        const targetGain = resolveDuckedSubliminalGain(baseGain, mainVoiceActive);
        if (typeof lane.gain.gain.setTargetAtTime === "function") {
          lane.gain.gain.setTargetAtTime(targetGain, subliminalNow, 0.12);
        } else {
          lane.gain.gain.value = targetGain;
        }
      }
      schedule.forEach((cue, index) => {
        const targetMs = Number.isFinite(Number(cue?.target_ms)) ? Number(cue.target_ms) : 0;
        // Raise floor to 3 s so late-arriving schedules still catch the window.
        const windowMs = Math.max(3_000, Number.isFinite(Number(cue?.window_ms)) ? Number(cue.window_ms) : 8_000);
        // Key by SLOT identity (time + role), never by the resolved file:
        // queue refreshes re-resolve schedules and (since resolution is
        // randomized) produce different files for the same slot. File-based
        // keys made every elapsed cue look unfired after each refresh, so
        // whispers replayed back-to-back and the last in-window slot re-fired
        // endlessly with a new file each time.
        const cueKey = `${effectTrackFile}:${targetMs}:${cue?.line_role || cue?.role || ""}`;
        if (firedOverlayCueKeysRef.current.has(cueKey)) return;
        if (startingOverlayCueKeysRef.current.has(cueKey)) return;
        if (nowMs < targetMs) return;
        if (nowMs > targetMs + windowMs) {
          // Window expired — mark fired so we stop checking.
          firedOverlayCueKeysRef.current.add(cueKey);
          pendingRetry.delete(cueKey);
          return;
        }
        // Keep spoken moments sparse. The bed should have time to breathe between
        // voices even when a generated schedule contains clustered cue points.
        const orderedSequenceCue = Boolean(cue?.sequence_group);
        const cueDelivery = String(cue?.delivery_role || cue?.overlay_track?.delivery_role || cue?.delivery || "").toLowerCase();
        const cueLineRole = String(cue?.line_role || cue?.overlay_track?.line_role || "").toLowerCase();
        const cueFamily = String(cue?.overlay_family || cue?.overlay_track?.overlay_family || "").toLowerCase();
        const directorLane = cueDelivery.includes("subliminal")
          ? LANE_SUBLIMINAL_1
          : cueLineRole === "main_voiceover" || cueFamily === "main_hypnosis"
            ? LANE_HYPNOSIS_SPOKEN_MAIN
            : cueDelivery.includes("whisper")
              ? "whisper"
              : LANE_OVERLAY;
        // An authoritative hour score owns every consciously audible voice
        // moment. Per-bed schedules remain active only for the processed
        // subliminal lane, preventing duplicate whispers or main narration.
        if (stateRef.current.sessionScript?.script_id && directorLane !== LANE_SUBLIMINAL_1) {
          firedOverlayCueKeysRef.current.add(cueKey);
          pendingRetry.delete(cueKey);
          return;
        }
        const minimumGapMs = orderedSequenceCue
          ? Math.max(3_000, Number.isFinite(Number(cue?.sequence_cadence_ms)) ? Number(cue.sequence_cadence_ms) : 5_000)
          : Math.max(22_000, Number.isFinite(Number(currentTrack?.audio_scene?.minimumVoiceGapMs))
            ? Number(currentTrack.audio_scene.minimumVoiceGapMs)
            : 0);
        const lastLaneStart = overlayDirectorRef.current.lastStartedByLane[directorLane] ?? Number.NEGATIVE_INFINITY;
        if (nowMs - lastLaneStart < minimumGapMs) {
          if (!pendingRetry.has(cueKey)) {
            pendingRetry.set(cueKey, nowMs);
            api.logPlayback({
              event_type: "overlay_deferred",
              overlay_file: cue?.overlay_track?.file || "",
              channel: stateRef.current.activeTheme || "",
              arc_stage: getArcStage(),
              error: "minimum_voice_gap",
            });
          }
          return;
        }
        // Attempt to play. If the lane is busy, record a pending-retry entry
        // and let the next tick try again — as long as we are still in the window.
        startingOverlayCueKeysRef.current.add(cueKey);
        playResolvedOverlayCue(cue).then((started) => {
          if (started) {
            overlayDirectorRef.current.lastStartedByLane[directorLane] = nowMs;
            firedOverlayCueKeysRef.current.add(cueKey);
            pendingRetry.delete(cueKey);
          } else if (!pendingRetry.has(cueKey)) {
            pendingRetry.set(cueKey, nowMs);
          }
        }).finally(() => {
          startingOverlayCueKeysRef.current.delete(cueKey);
        });
      });
    };
    const intervalId = window.setInterval(tick, 250);
    return () => {
      window.clearInterval(intervalId);
      startingOverlayCueKeysRef.current.clear();
      pendingRetry.clear();
    };
  }, [state.currentTrack?.file, playResolvedOverlayCue, clearOverlayLanes, state.sessionScript?.script_id]);

  useEffect(() => {
    const lanes = [
      [LANE_HYPNOSIS_SPOKEN_MAIN, mainVoiceAudioRef.current],
      [LANE_OVERLAY, overlayAudioRef.current],
      [LANE_WHISPER_LEFT, whisperLeftAudioRef.current],
      [LANE_WHISPER_RIGHT, whisperRightAudioRef.current],
      [LANE_SUBLIMINAL_1, subliminalAudioRef.current],
    ];
    const cleanups = [];
    lanes.forEach(([laneName, laneAudio]) => {
      if (!laneAudio) return;
      const settleLaneOnEnd = () => {
        stopLaneMotion(laneName);
        releaseDeckDuck(laneName);
      };
      // Settle unconditionally on ended/error — the clip is done.
      laneAudio.addEventListener("ended", settleLaneOnEnd);
      laneAudio.addEventListener("error", settleLaneOnEnd);
      const settleLaneOnPause = () => {
        stopLaneMotion(laneName);
        releaseDeckDuck(laneName);
      };
      laneAudio.addEventListener("pause", settleLaneOnPause);
      cleanups.push(() => laneAudio.removeEventListener("ended", settleLaneOnEnd));
      cleanups.push(() => laneAudio.removeEventListener("pause", settleLaneOnPause));
      cleanups.push(() => laneAudio.removeEventListener("error", settleLaneOnEnd));
    });
    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [releaseDeckDuck, stopLaneMotion]);

  // Execute the server-authored hour score against one session clock. Music
  // remains continuous underneath it; foreground and whisper events fire at
  // their audited offsets. After the first hour, only the ten-minute
  // deepening tail repeats, so induction can never restart by queue wrap.
  useEffect(() => {
    const script = state.sessionScript;
    if (!state.isPlaying || !script?.script_id || !sessionStartTimeRef.current) return undefined;
    let cancelled = false;
    const starting = new Set();
    const tick = () => {
      if (cancelled || !stateRef.current.isPlaying || !sessionStartTimeRef.current) return;
      const live = stateRef.current;
      const liveScript = live.sessionScript;
      if (!liveScript?.script_id) return;
      const elapsed = Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000);
      const targetSeconds = Math.max(60, Number(liveScript.target_seconds) || 3600);
      let namespace = "base";
      let localSeconds = elapsed;
      let events = [
        ...(Array.isArray(liveScript.main_voice) ? liveScript.main_voice : []),
        ...(Array.isArray(liveScript.whispers) ? liveScript.whispers : []),
        ...(Array.isArray(liveScript.spatial_encounters) ? liveScript.spatial_encounters : []),
        ...(Array.isArray(liveScript.spatial_backgrounds) ? liveScript.spatial_backgrounds : []),
        ...(Array.isArray(liveScript.subliminal?.events) ? liveScript.subliminal.events : []),
      ];
      if (elapsed >= targetSeconds) {
        const loop = liveScript.loop_tail || {};
        const loopSeconds = Math.max(60, Number(loop.duration_seconds) || 600);
        const cycle = Math.floor((elapsed - targetSeconds) / loopSeconds);
        namespace = `loop-${cycle}`;
        localSeconds = (elapsed - targetSeconds) % loopSeconds;
        events = [
          ...(Array.isArray(loop.main_voice) ? loop.main_voice : []),
          ...(Array.isArray(loop.whispers) ? loop.whispers : []),
          ...(Array.isArray(loop.spatial_encounters) ? loop.spatial_encounters : []),
        ];
      }
      const trackByFile = new Map([
        ...(live.mainVoiceQueue || []),
        ...(live.overlayQueue || []),
        ...(live.sessionWelcomeTrack ? [live.sessionWelcomeTrack] : []),
      ].map((track) => [String(track?.file || ""), track]));
      events.forEach((event) => {
        const atSeconds = Math.max(0, Number(event?.at_seconds) || 0);
        if (localSeconds < atSeconds) return;
        const eventKey = `${namespace}:${event?.event_id || `${event?.lane}:${event?.file}:${atSeconds}`}`;
        if (firedSessionScriptEventKeysRef.current.has(eventKey) || starting.has(eventKey)) return;
        const isWhisper = isWhisperLane(String(event?.lane || ""));
        const isCountdown = String(event?.lane || "") === "countdown_center"
          || String(event?.role || "") === "countdown";
        // Do not dump missed dialogue after a suspended tab or long pause.
        // Ordered countdowns are different: deliver a late atom immediately,
        // then let the active-lane guard release the next one. This preserves
        // ten-to-one order after a background-tab wake instead of jumping
        // from six to three.
        if (!isCountdown && localSeconds > atSeconds + 12) {
          firedSessionScriptEventKeysRef.current.add(eventKey);
          return;
        }
        const track = trackByFile.get(String(event?.file || ""));
        if (!track?.audio_src) {
          firedSessionScriptEventKeysRef.current.add(eventKey);
          return;
        }
        const isSpatialEncounter = isEncounterLane(String(event?.lane || ""));
        const isSubliminal = String(event?.lane || "") === LANE_SUBLIMINAL_1;
        if (isCountdown && countdownPlaybackRef.current.active) return;
        if (isCountdown) countdownPlaybackRef.current = { active: true, eventKey };
        const welcomeClaim = sessionWelcomePlaybackGuardRef.current.claim(event);
        if (welcomeClaim === "played") {
          firedSessionScriptEventKeysRef.current.add(eventKey);
          return;
        }
        if (welcomeClaim === "starting") return;
        starting.add(eventKey);
        playResolvedOverlayCue({
          overlay_track: track,
          line_role: isSubliminal
            ? "subliminal"
            : isWhisper
              ? "whisper"
              : isSpatialEncounter
                ? "ambient_sensual_encounter"
                : "main_voiceover",
          overlay_family: isSubliminal
            ? "subliminal"
            : isWhisper
              ? "affirmation"
              : isSpatialEncounter
                ? "sensual_encounter"
                : "main_hypnosis",
          delivery_role: isSubliminal ? "subliminal" : isWhisper || isSpatialEncounter ? "whisper" : "spoken",
          target_speaker: isCountdown
            ? "center"
            : event.lane === LANE_WHISPER_RIGHT ? "right" : event.lane === LANE_WHISPER_LEFT ? "left" : "center",
          duck_db: isWhisper ? -2 : 0,
          spatial_lane: isSpatialEncounter ? event.lane : "",
          spatial_audio: isSpatialEncounter ? event.spatial_audio : null,
          sequence_group: String(event?.sequence_group || `session_script:${script.script_id}`),
          continuous_loop: Boolean(event?.continuous_loop),
          on_ended: isCountdown
            ? () => {
              if (countdownPlaybackRef.current.eventKey === eventKey) {
                countdownPlaybackRef.current = { active: false, eventKey: "" };
              }
            }
            : undefined,
        }).then((started) => {
          if (isCountdown && !started && countdownPlaybackRef.current.eventKey === eventKey) {
            countdownPlaybackRef.current = { active: false, eventKey: "" };
          }
          sessionWelcomePlaybackGuardRef.current.settle(event, started);
          if (started) firedSessionScriptEventKeysRef.current.add(eventKey);
        }).catch(() => {
          if (isCountdown && countdownPlaybackRef.current.eventKey === eventKey) {
            countdownPlaybackRef.current = { active: false, eventKey: "" };
          }
          sessionWelcomePlaybackGuardRef.current.settle(event, false);
        }).finally(() => starting.delete(eventKey));
      });
    };
    tick();
    const intervalId = window.setInterval(tick, 250);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      starting.clear();
    };
  }, [playResolvedOverlayCue, state.isPlaying, state.sessionScript?.script_id]);

  // The server score remains authoritative, but a stalled device, missed cue,
  // or exhausted reusable pool must never reduce a session to whisper-only
  // dead air. After ten silent foreground seconds, play one COMPLETE countdown
  // on the main lane. Its ten-to-one motion alternates gently across the stereo
  // field and converges at center; the existing speech chain supplies fades,
  // room bloom and a low single-repeat shimmer.
  useEffect(() => {
    const scriptId = state.sessionScript?.script_id;
    if (!state.isPlaying || !scriptId) {
      mainVoiceFallbackRunRef.current.token += 1;
      mainVoiceFallbackRunRef.current.active = false;
      return undefined;
    }
    // A stalled score is an explicit server decision: keep the bed running
    // while generation fills the missing induction/deepening spine. Never
    // answer that state with a random warehouse paragraph; that is exactly how
    // a missing induction turns into an incoherent session.
    if (
      state.sessionScript?.stall?.enabled
      || state.sessionScript?.coherence?.ready === false
      || !(Array.isArray(state.sessionScript?.main_voice) && state.sessionScript.main_voice.length)
    ) {
      mainVoiceFallbackRunRef.current.token += 1;
      mainVoiceFallbackRunRef.current.active = false;
      return undefined;
    }
    let cancelled = false;
    const sessionToken = ++mainVoiceFallbackRunRef.current.token;
    if (!lastMainVoiceActivityRef.current) lastMainVoiceActivityRef.current = Date.now();

    const laneIsActive = () => Boolean(
      audibleBufferLanesRef.current[LANE_HYPNOSIS_SPOKEN_MAIN]?.source
      || (mainVoiceAudioRef.current && !mainVoiceAudioRef.current.paused && mainVoiceAudioRef.current.currentSrc)
    );
    const stillCurrent = () => (
      !cancelled
      && stateRef.current.isPlaying
      && stateRef.current.sessionScript?.script_id === scriptId
      && mainVoiceFallbackRunRef.current.token === sessionToken
    );

    const playSequence = (tracks, kind, sequenceId, index = 0) => {
      if (!stillCurrent()) return;
      if (index >= tracks.length) {
        mainVoiceFallbackRunRef.current.active = false;
        lastMainVoiceActivityRef.current = Date.now();
        return;
      }
      const track = tracks[index];
      const pan = index === tracks.length - 1 ? 0 : (index % 2 === 0 ? -0.48 : 0.48);
      const fallbackGroup = kind === "countdown" ? "main_starvation_countdown" : "main_starvation_prose";
      playResolvedOverlayCue({
        overlay_track: track,
        line_role: "main_voiceover",
        overlay_family: "main_hypnosis",
        delivery_role: "spoken",
        target_speaker: pan < 0 ? "left" : pan > 0 ? "right" : "center",
        pan,
        duck_db: -4.5,
        sequence_group: `${fallbackGroup}:${scriptId}:${sessionToken}:${sequenceId}`,
        on_ended: () => {
          lastMainVoiceActivityRef.current = Date.now();
          // Keep fallback counts as slow as authored counts; a starvation
          // repair must not reintroduce a rushed 1.1-second number stream.
          if (stillCurrent()) window.setTimeout(() => playSequence(tracks, kind, sequenceId, index + 1), 4000);
        },
      }).then((started) => {
        if (started) {
          lastMainVoiceActivityRef.current = Date.now();
          return;
        }
        // A real score cue won the main-lane race: yield. If the lane is truly
        // empty, retry the same number so the listener never hears a fragment.
        if (stillCurrent() && laneIsActive()) {
          mainVoiceFallbackRunRef.current.active = false;
          lastMainVoiceActivityRef.current = Date.now();
        } else if (stillCurrent()) {
          // Do not spin forever on a blocked countdown. A scheduled sequence
          // may have won the six-minute cooldown race; the next watchdog tick
          // must switch to varied prose rather than trying more arithmetic.
          mainVoiceFallbackRunRef.current.active = false;
          lastMainVoiceActivityRef.current = Date.now();
        }
      });
    };

    const tick = () => {
      if (!stillCurrent()) return;
      if (laneIsActive()) {
        lastMainVoiceActivityRef.current = Date.now();
        return;
      }
      if (mainVoiceFallbackRunRef.current.active) return;
      if (Date.now() - lastMainVoiceActivityRef.current < 10_000) return;
      const live = stateRef.current;
      const now = Date.now();
      // An authored countdown may run on alternating whisper lanes while the
      // main lane is technically idle. Treat its recent atoms as foreground
      // activity so the watchdog cannot lay a second countdown over/after it.
      if (now - countdownGuardRef.current.lastStartedAt < 20_000) {
        lastMainVoiceActivityRef.current = now;
        return;
      }
      if (now - lastStarvationReportAtRef.current >= 3 * 60_000) {
        lastStarvationReportAtRef.current = now;
        api.logPlayback({
          event_type: "main_voice_starvation",
          channel: live.activeTheme || live.sessionRequest?.setting || "custom_session",
          arc_stage: getArcStage(),
          error: "foreground_idle_10s",
          layer_status: {
            main_voice_tracks: (live.mainVoiceQueue || []).length,
            countdown_tracks: orderedFullCountdown(live.mainVoiceQueue || [], live.overlayQueue || []).length,
            scripted_main_events: (live.sessionScript?.main_voice || []).length,
          },
        });
      }
      const countdown = orderedFullCountdown(live.mainVoiceQueue || [], live.overlayQueue || []);
      const generic = genericForegroundFallback(live.mainVoiceQueue || []);
      const countdownReady = countdown.length
        && now - countdownGuardRef.current.lastStartedAt >= 12 * 60_000;
      let fallback = [];
      let fallbackKind = "generic";
      if (countdownReady) {
        fallback = countdown;
        fallbackKind = "countdown";
      } else if (generic.length) {
        const fallbackIndex = mainVoiceFallbackIndexRef.current % generic.length;
        fallback = [generic[fallbackIndex]];
        mainVoiceFallbackIndexRef.current = (fallbackIndex + 1) % generic.length;
      }
      if (!fallback.length) return;
      mainVoiceFallbackRunRef.current.active = true;
      const sequenceId = ++mainVoiceFallbackSequenceRef.current;
      if (fallbackKind === "countdown") {
        // Claim the twelve-minute window before awaiting decode/play so adjacent
        // watchdog ticks or an authored sequence cannot win the same race.
        countdownGuardRef.current = {
          lastStartedAt: now,
          activeGroup: `main_starvation_countdown:${scriptId}:${sequenceId}`,
        };
      }
      playSequence(fallback, fallbackKind, sequenceId);
    };
    tick();
    const intervalId = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (mainVoiceFallbackRunRef.current.token === sessionToken) {
        mainVoiceFallbackRunRef.current.token += 1;
        mainVoiceFallbackRunRef.current.active = false;
      }
    };
  }, [playResolvedOverlayCue, state.isPlaying, state.sessionScript?.script_id]);

  // Drive the session scoreboard clock. While a scripted session is playing,
  // report elapsed seconds (paused-aware) so the timeline playhead tracks the
  // real session clock rather than wall time.
  useEffect(() => {
    if (!state.isPlaying || !state.sessionScript?.script_id) {
      setSessionClock(0);
      return undefined;
    }
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (sessionPausedAtRef.current) return; // paused — hold the clock
      if (!sessionStartTimeRef.current) return;
      setSessionClock(Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [state.isPlaying, state.sessionScript?.script_id]);

  // ALWAYS-ON subliminal masking. The continuous subliminal bed and one-shot
  // subliminal lane must stay masked and frequency-shifted at ALL times, and
  // must be ducked hard whenever ANY foreground voice is active — including
  // the "welcome to your curated session" opener, which previously ran the
  // subliminal at full gain because the overlay-schedule ducking tick only
  // fired when a per-bed schedule existed. This effect runs independently of
  // any schedule and follows the real transport state of every audible lane.
  useEffect(() => {
    if (!state.isPlaying) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const ctx = overlayAudioContextRef.current;
      const now = ctx?.currentTime || 0;
      // Any consciously audible foreground voice (main narration, the welcome,
      // whispers, overlays) triggers the duck. The welcome is a main-voice
      // event, so it is covered by the main-voice check below.
      const mainVoiceActive = Boolean(
        mainVoiceAudioRef.current
        && !mainVoiceAudioRef.current.paused
        && mainVoiceAudioRef.current.currentSrc,
      );
      const whisperActive = Boolean(
        (whisperLeftAudioRef.current && !whisperLeftAudioRef.current.paused && whisperLeftAudioRef.current.currentSrc)
        || (whisperRightAudioRef.current && !whisperRightAudioRef.current.paused && whisperRightAudioRef.current.currentSrc),
      );
      const overlayActive = Boolean(
        overlayAudioRef.current && !overlayAudioRef.current.paused && overlayAudioRef.current.currentSrc,
      );
      const anyVoiceActive = mainVoiceActive || whisperActive || overlayActive;
      for (const laneName of [LANE_SUBLIMINAL_1, LANE_SUBLIMINAL_2]) {
        const lane = overlayLaneRefs.current[laneName];
        const baseGain = Number(lane?.subliminalBaseGain);
        if (!lane?.gain?.gain || !Number.isFinite(baseGain)) continue;
        // Harder duck while any voice is active: 0.316 (10 dB) was not enough
        // to keep the subliminal under the welcome. Use 0.18 (~15 dB) so the
        // foreground voice always wins the perceptual floor.
        const targetGain = baseGain * (anyVoiceActive ? 0.18 : 1);
        if (typeof lane.gain.gain.setTargetAtTime === "function") {
          lane.gain.gain.setTargetAtTime(targetGain, now, 0.08);
        } else {
          lane.gain.gain.value = targetGain;
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 120);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [state.isPlaying]);

  // Continuous foreground hypnosis is conducted independently of music-bed
  // boundaries and accent schedules. When one reusable module ends, advance
  // to the next and wrap indefinitely until the listener pauses or retunes.
  useEffect(() => {
    const laneAudio = mainVoiceAudioRef.current;
    const queue = state.mainVoiceQueue || [];
    if (state.sessionScript?.script_id) return undefined;
    const initialQueue = selectContinuousMainVoiceQueue(
      queue,
      sessionWelcomePlaybackGuardRef.current.status(),
    );
    if (!laneAudio || !state.isPlaying || !initialQueue.length) return undefined;
    let cancelled = false;
    let timer = 0;
    const playNext = () => {
      if (cancelled || !stateRef.current.isPlaying) return;
      const liveQueue = selectContinuousMainVoiceQueue(
        stateRef.current.mainVoiceQueue || [],
        sessionWelcomePlaybackGuardRef.current.status(),
      );
      if (!liveQueue.length) return;
      const index = mainVoiceIndexRef.current % liveQueue.length;
      const track = liveQueue[index];
      mainVoiceIndexRef.current = (index + 1) % liveQueue.length;
      mainVoicePauseAfterRef.current = Math.max(350, Math.min(3500, Number(track.pause_after_ms) || 900));
      playResolvedOverlayCue({
        overlay_track: track,
        line_role: "main_voiceover",
        overlay_family: "main_hypnosis",
        delivery_role: "spoken",
        duck_db: 0,
        sequence_group: "continuous_main_voice",
        // Decoded WebAudio sources do not emit `ended` on laneAudio.  Carry
        // the continuation callback into the decoded source so main speech
        // advances just as reliably as the native-media fallback.
        on_ended: onEnded,
      }).then((started) => {
        if (!started && !cancelled) timer = window.setTimeout(playNext, 1200);
      });
    };
    const onEnded = () => {
      if (!cancelled) timer = window.setTimeout(playNext, mainVoicePauseAfterRef.current);
    };
    const onPlaybackFault = () => {
      if (cancelled) return;
      try {
        laneAudio.pause();
        laneAudio.removeAttribute("src");
        laneAudio.load();
      } catch {
        // The retry below still advances to another voice atom.
      }
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(playNext, 500);
    };
    laneAudio.addEventListener("ended", onEnded);
    laneAudio.addEventListener("error", onPlaybackFault);
    laneAudio.addEventListener("stalled", onPlaybackFault);
    // The first foreground clip is started by the Play gesture. Do not let
    // this effect race that async decode and start a second main-lane cue
    // underneath the singleton welcome/first induction paragraph.
    if (!mainVoiceGestureStartInFlightRef.current && (laneAudio.paused || !laneAudio.currentSrc)) playNext();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      laneAudio.removeEventListener("ended", onEnded);
      laneAudio.removeEventListener("error", onPlaybackFault);
      laneAudio.removeEventListener("stalled", onPlaybackFault);
    };
  }, [playResolvedOverlayCue, state.isPlaying, state.mainVoiceQueue, state.sessionScript?.script_id]);

  // Experimental trajectories are deliberately slow and bounded. Recompute
  // the selected N-of-1 protocol without reloading the music element so a
  // linear or sinusoidal target evolves continuously under the spoken track.
  useEffect(() => {
    const protocol = state.hypnosisProtocol;
    const controller = audioControllerRef.current;
    if (!state.isPlaying || !protocol?.protocol_id || !controller) {
      liveBrainwaveSceneRef.current = { trackFile: "", scene: null };
      return undefined;
    }
    const applyCurrentProtocol = () => {
      const elapsedSeconds = sessionStartTimeRef.current
        ? Math.max(0, (Date.now() - sessionStartTimeRef.current) / 1000)
        : 0;
      const resolved = applyHypnosisProtocol(stateRef.current.currentTrack, protocol, elapsedSeconds);
      if (!resolved) return;
      const activeScene = brainwaveLabScene(
        resolved.audio_scene || {},
        brainwaveLabSettingsRef.current,
        elapsedSeconds,
      );
      liveBrainwaveSceneRef.current = {
        trackFile: String(stateRef.current.currentTrack?.file || ""),
        scene: activeScene,
      };
      controller.applyAudioScene?.(activeScene);
      controller.applyBrainwaveTarget?.(activeScene.binHz ?? resolved.brainwave_target_hz);
      const now = overlayAudioContextRef.current?.currentTime || 0;
      applySubliminalBrainwaveScene(
        overlayLaneRefs.current[LANE_SUBLIMINAL_1],
        activeScene,
        now,
      );
      applySubliminalBrainwaveScene(
        overlayLaneRefs.current[LANE_SUBLIMINAL_2],
        activeScene,
        now,
        { background: true, carrierShiftHz: resolveSubliminalBackgroundShiftHz({ mode: "dark" }) },
      );
    };
    applyCurrentProtocol();
    const timer = window.setInterval(applyCurrentProtocol, 15_000);
    return () => window.clearInterval(timer);
  }, [state.hypnosisProtocol, state.isPlaying, state.currentTrack?.file]);

  // Periodic queue refresh: pick up new drops every 2 minutes while a session
  // is active. This ensures freshly generated tracks reach the player without
  // waiting for the queue to drain to ≤3.
  useEffect(() => {
    const INTERVAL_MS = 2 * 60 * 1000;
    const id = window.setInterval(() => {
      const s = stateRef.current;
      if (s.activeTheme && s.queue && s.queue.length > 0) {
        refreshQueueInBackground();
      }
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshQueueInBackground]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    let raf = 0;
    let phase = 0;
    let lastW = 0;
    let lastH = 0;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w === lastW && h === lastH) return;
      lastW = w; lastH = h;
      canvas.width = Math.floor(w * ratio);
      canvas.height = Math.floor(h * ratio);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const drawSpiral = (cx, cy, direction, phaseOff, turns, r, g, b, alphaMax, lineW) => {
      const maxRadius = Math.min(lastW, lastH) * 0.54;
      const totalAngle = turns * Math.PI * 2;
      ctx.beginPath();
      let started = false;
      for (let t = 0.001; t <= totalAngle; t += 0.035) {
        const radius = (t / totalAngle) * maxRadius;
        const angle = t * direction + phaseOff;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        if (!started) { ctx.moveTo(x, y); started = true; }
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(${r},${g},${b},${alphaMax})`;
      ctx.lineWidth = lineW;
      ctx.stroke();
    };

    // Brainwave ripple state: rings expand from the centre at the channel's
    // binaural beat rate (audio_scene.binHz — the same Hz the subliminal
    // worklet uses for its interaural split), so the visual entrainment and
    // the audio entrainment are phase-companions. Encoded as ring MOTION at
    // low contrast, never full-field brightness flicker (photosensitive
    // safety: no 3–30 Hz luminance strobing).
    let ripplePhase = 0;
    let lastFrameMs = 0;

    const render = (nowMs) => {
      resize();
      const w = lastW || window.innerWidth;
      const h = lastH || window.innerHeight;
      const energy = stateRef.current.isPlaying ? 1 : 0.38;
      const dt = lastFrameMs ? Math.min(0.1, (nowMs - lastFrameMs) / 1000) : 0.016;
      lastFrameMs = nowMs;

      // Slow trail fade — keeps echo of previous frames for hypnotic persistence
      ctx.fillStyle = `rgba(5,8,18,${0.055 + energy * 0.028})`;
      ctx.fillRect(0, 0, w, h);

      phase += 0.007 * energy;
      const cx = w / 2;
      const cy = h / 2;
      const turns = 7;

      // Primary CW spiral — deep violet
      drawSpiral(cx, cy, 1, phase, turns, 148, 88, 255, 0.72 * energy + 0.12, 1.8);
      // Counter-rotating spiral — rose violet, opposite direction
      drawSpiral(cx, cy, -1, phase * 0.9, turns, 220, 130, 255, 0.52 * energy + 0.10, 1.5);
      // Slow inner CW overlay — blue accent, offset half-turn for weave effect
      drawSpiral(cx, cy, 1, phase * 0.55 + Math.PI, turns - 2, 110, 155, 255, 0.28 * energy + 0.06, 1.1);

      // Subliminal brainwave ripple (playing only): one ring per beat cycle.
      if (stateRef.current.isPlaying) {
        const scene = stateRef.current.currentTrack?.audio_scene || {};
        const binHz = Math.max(0.5, Math.min(12, Number(scene.binHz ?? scene.targetBrainwaveHz) || 6));
        ripplePhase = (ripplePhase + binHz * dt) % 1;
        const maxRadius = Math.min(w, h) * 0.5;
        const ringCount = 4;
        for (let k = 0; k < ringCount; k += 1) {
          const progress = (ripplePhase + k / ringCount) % 1;
          const radius = 18 + progress * maxRadius;
          const alpha = (1 - progress) * (1 - progress) * 0.14;
          if (alpha < 0.01) continue;
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(200,120,255,${alpha.toFixed(3)})`;
          ctx.lineWidth = 1.4;
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(render);
    };

    resize();
    raf = requestAnimationFrame(render);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    // A locked gate must NEVER black out a running session. If a gate state
    // ever coincides with an active session, the shell stays visible. Without
    // this guard, body.app-locked hides #topbar/#track-wrap/#stage-hud/
    // #action-dock/etc. (opacity 0, visibility hidden), and since html/body are
    // #000 the result is a solid black screen with the audio stopped.
    const sessionActive = Boolean(state.currentTrack?.file || state.isPlaying);
    document.body.classList.toggle(
      "app-locked",
      Boolean(state.welcomeVisible && !state.session?.authenticated && !sessionActive),
    );
  }, [state.session?.authenticated, state.welcomeVisible, state.currentTrack?.file, state.isPlaying]);

  // Eyeball visual sync — pure CSS-variable writes, playback paths untouched:
  // pupil colour throbs with the pulse layer (audio_scene.bpm, the sex beat);
  // sclera colour-swirl revolves once per 60 subliminal beat cycles (binHz).
  useEffect(() => {
    const scene = state.currentTrack?.audio_scene || {};
    const bpm = Math.max(18, Math.min(90, Number(scene.bpm) || 36));
    const binHz = Math.max(0.5, Math.min(12, Number(scene.binHz ?? scene.targetBrainwaveHz) || 6));
    const button = document.getElementById("play-btn");
    if (button) {
      button.style.setProperty("--sex-beat-seconds", `${(60 / bpm).toFixed(3)}s`);
      button.style.setProperty("--subliminal-swirl-seconds", `${(60 / binHz).toFixed(2)}s`);
    }
  }, [state.currentTrack]);

  // Real signal proof: meter the post-worklet subliminal output, not a timer.
  // This confirms that processed samples are moving through the browser graph;
  // it deliberately makes no claim about a listener's biological response.
  useEffect(() => {
    const button = document.getElementById("play-btn");
    if (!button) return undefined;
    let raf = 0;
    let lastTelemetryAt = 0;
    let lastTransportProfileKey = "";
    const sampleLane = (laneName) => {
      const lane = overlayLaneRefs.current[laneName];
      const analyser = lane?.analyser;
      if (!analyser) return { rms: 0, peak: 0, sampleCount: 0 };
      const data = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(data);
      return measureTimeDomainBytes(data);
    };
    const meter = () => {
      const lanes = [sampleLane(LANE_SUBLIMINAL_1), sampleLane(LANE_SUBLIMINAL_2)];
      const measured = {
        rms: Math.max(...lanes.map((lane) => lane.rms)),
        peak: Math.max(...lanes.map((lane) => lane.peak)),
      };
      const summary = summarizeSubliminalSignal({
        ...measured,
        ceilingLinear: SUBLIMINAL_GAIN_CEILING,
        // Byte analyser quantization can round a .02 sample to .0234.
        ceilingToleranceLinear: 1 / 256,
      });
      const energy = stateRef.current.isPlaying
        ? Math.max(0, Math.min(1, summary.rms * 7.5))
        : 0;
      const signalActive = stateRef.current.isPlaying && summary.signalActive && energy > 0.025;
      const subliminalLane = overlayLaneRefs.current[LANE_SUBLIMINAL_1];
      const backgroundLane = overlayLaneRefs.current[LANE_SUBLIMINAL_2];
      const appliedScene = subliminalLane?.lastAppliedSubliminalScene
        || backgroundLane?.lastAppliedSubliminalScene
        || {};
      const rhythmStatus = audioControllerRef.current?.layerStatus?.() || {};
      const transportProfile = resolveAudioTransportProfile({
        context: overlayAudioContextRef.current,
        media: subliminalAudioRef.current,
        track: stateRef.current.currentTrack,
      });
      const channelMode = appliedScene.channelMode
        || subliminalLane?.channelMode
        || backgroundLane?.channelMode
        || "unknown";
      button.style.setProperty("--subliminal-energy", energy.toFixed(3));
      button.style.setProperty("--subliminal-ring-opacity", (signalActive ? Math.max(0.55, energy) : 0.08).toFixed(3));
      button.style.setProperty("--subliminal-ring-scale", (0.98 + energy * 0.1).toFixed(3));
      button.style.setProperty("--subliminal-ring-glow", `${(8 + energy * 28).toFixed(1)}px`);
      button.style.setProperty("--subliminal-ring-inner-glow", `${(5 + energy * 18).toFixed(1)}px`);
      button.dataset.subliminalSignal = signalActive ? "active" : "quiet";
      button.dataset.subliminalRmsDbfs = Number.isFinite(summary.rmsDbfs) ? summary.rmsDbfs.toFixed(2) : "-Infinity";
      button.dataset.subliminalPeakDbfs = Number.isFinite(summary.peakDbfs) ? summary.peakDbfs.toFixed(2) : "-Infinity";
      button.dataset.subliminalCeilingDbfs = Number.isFinite(summary.ceilingDbfs) ? summary.ceilingDbfs.toFixed(2) : "-Infinity";
      button.dataset.subliminalOverCeiling = String(summary.overCeiling);
      button.dataset.subliminalChannelMode = channelMode;
      button.dataset.subliminalBinaural = String(appliedScene.binauralEnabled === true);
      button.dataset.audibleRhythmActive = String(rhythmStatus.audible_rhythm_active === true);
      button.dataset.audibleRhythmPhase = String(rhythmStatus.audible_rhythm_phase || "idle");
      button.dataset.audibleRhythmTickIndex = String(Number(rhythmStatus.audible_rhythm_tick_index || 0));
      const now = Date.now();
      const trackFile = String(stateRef.current.currentTrack?.file || "");
      const baseGain = Number.isFinite(Number(appliedScene.subliminalBaseGain))
        ? Number(appliedScene.subliminalBaseGain) : 0;
      const transportProfileKey = `${trackFile}|${JSON.stringify(transportProfile)}|${baseGain}`;
      if (stateRef.current.isPlaying && trackFile && transportProfileKey !== lastTransportProfileKey) {
        lastTransportProfileKey = transportProfileKey;
        api.logPlayback({
          event_type: "subliminal_transport_profile",
          overlay_lane: LANE_SUBLIMINAL_1,
          track_file: trackFile,
          channel_mode: channelMode,
          subliminal_base_gain: baseGain,
          subliminal_worklet_ready: Boolean(subliminalWorkletReadyRef.current),
          signal_active: signalActive,
          ...transportProfile,
        });
      }
      if (stateRef.current.isPlaying && trackFile && now - lastTelemetryAt >= 2000) {
        lastTelemetryAt = now;
        api.logPlayback({
          event_type: "subliminal_signal_meter",
          overlay_lane: LANE_SUBLIMINAL_1,
          delivery_engine: "webaudio_analyser",
          channel_mode: channelMode,
          binaural_enabled: appliedScene.binauralEnabled === true,
          subliminal_base_gain: Number.isFinite(Number(appliedScene.subliminalBaseGain))
            ? Number(appliedScene.subliminalBaseGain) : 0,
          signal_active: signalActive,
          rms_dbfs: Number(summary.rmsDbfs.toFixed(2)),
          peak_dbfs: Number(summary.peakDbfs.toFixed(2)),
          ceiling_dbfs: Number(summary.ceilingDbfs.toFixed(2)),
          over_ceiling: summary.overCeiling,
          rhythm_cue_active: rhythmStatus.audible_rhythm_active === true,
          rhythm_cue_phase: String(rhythmStatus.audible_rhythm_phase || "idle"),
          rhythm_cue_tick_index: Number(rhythmStatus.audible_rhythm_tick_index || 0),
          rhythm_cue_bpm: Number(rhythmStatus.audible_rhythm_bpm || 0),
          spectral_mode: String(stateRef.current.currentTrack?.audio_scene?.subliminalSpectralMode || "in_band"),
          subliminal_carrier_shift_hz: resolveCarrierShiftHz({
            mode: String(stateRef.current.currentTrack?.audio_scene?.subliminalSpectralMode || "in_band"),
            isSpeech: true,
          }),
          track_file: String(stateRef.current.currentTrack?.file || ""),
        });
      }
      raf = window.requestAnimationFrame(meter);
    };
    raf = window.requestAnimationFrame(meter);
    return () => {
      window.cancelAnimationFrame(raf);
      button.dataset.subliminalSignal = "quiet";
      delete button.dataset.subliminalRmsDbfs;
      delete button.dataset.subliminalPeakDbfs;
      delete button.dataset.subliminalCeilingDbfs;
      delete button.dataset.subliminalOverCeiling;
      delete button.dataset.subliminalChannelMode;
      delete button.dataset.subliminalBinaural;
      delete button.dataset.audibleRhythmActive;
      delete button.dataset.audibleRhythmPhase;
      delete button.dataset.audibleRhythmTickIndex;
      button.style.removeProperty("--subliminal-energy");
      button.style.removeProperty("--subliminal-ring-opacity");
      button.style.removeProperty("--subliminal-ring-scale");
      button.style.removeProperty("--subliminal-ring-glow");
      button.style.removeProperty("--subliminal-ring-inner-glow");
    };
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        // A page entry is a new listening session. Preserve account choices,
        // but reset elapsed-time and repetition state for the new arc.
        sessionStartTimeRef.current = null;
        recentlyPlayedRef.current = [];
        recentlyPlayedOverlayRef.current = [];
        playedVoiceFilesRef.current = new Set();
        firedOverlayCueKeysRef.current = new Set();
        mergeState({
          status: "Booting the listening room…",
          sessionLabel: "loading",
          isBusy: true,
        });
        const session = await api.fetchSession();
        const hasAccess = sessionCanAccessContent(session);
        if (!hasAccess) {
          mergeState({
            session,
            sessionLabel: session.authenticated ? "signed in" : "guest",
            gateMessage: buildAccessMessage(session),
            showTosButton: false,
            welcomeVisible: true,
            status: "Access required",
            isBusy: false,
          });
          return;
        }

        const [builderOptions, tags, personas, backgroundFiles, savedBuilderPreferences] = await Promise.all([
          api.fetchSessionBuilderOptions(),
          api.fetchTags(),
          api.fetchPersonas(),
          api.fetchBackground(),
          api.fetchSessionBuilderPreferences().catch((error) => {
            console.warn("[AceLabReact] profile session preferences unavailable", error);
            return null;
          }),
        ]);
        setSessionBuilderOptions(builderOptions);
        setSessionBuilderPreferences(
          savedBuilderPreferences?.preferences || readLocalSessionBuilderPreferences(session),
        );
        mergeState({
          session,
          personas,
          backgroundFiles,
          sessionLabel: session.authenticated ? "signed in" : "guest",
          themes: [],
          activeTheme: "custom_session",
          activeThemeLabel: "New session",
          tags,
          gateMessage: buildAccessMessage(session),
          showTosButton: false,
          welcomeVisible: !hasAccess,
          featuredMate: resolveFeaturedMate({
            personas,
            session,
            currentTrack: null,
            activeTheme: "custom_session",
          }),
        });

        if (hasAccess) {
          setSessionBuilderOpen(true);
          mergeState({ status: "Design your session", isBusy: false });
        } else {
          mergeState({
            status: "Access required",
            isBusy: false,
          });
        }
      } catch (error) {
        console.error("[AceLabReact]", error);
        mergeState({
          status: "Boot failed",
          gateMessage: "The React player could not finish booting. Check the browser console and backend logs.",
          isBusy: false,
        });
      }
    };
    boot();
  }, [buildAccessMessage, mergeState, resolveFeaturedMate, sessionCanAccessContent]);

  const displayThemes = useMemo(() => {
    const allThemes = state.themes || [];
    // Theme depth is an exact-theme diagnostic, not an availability gate.
    // Channels can draw compatible SFW/adult inventory through the DJ router,
    // so hiding a zero-exact-match theme falsely makes stocked channels vanish.
    const themes = allThemes;
    const active = [];
    const safe = [];
    const nsfw = [];
    themes.forEach((theme) => {
      if (theme.slug === state.activeTheme) active.push(theme);
      else if (theme.is_nsfw) nsfw.push(theme);
      else safe.push(theme);
    });
    return [...active, ...safe, ...nsfw];
  }, [state.activeTheme, state.themes]);

  const filteredThemes = useMemo(() => {
    const query = String(themeSearch || "").trim().toLowerCase();
    if (!query) return displayThemes;
    return (displayThemes || []).filter((theme) => {
      const haystack = [
        theme?.label,
        theme?.slug,
        theme?.category,
        theme?.content_minutes_label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [displayThemes, themeSearch]);

  const mateOptions = useMemo(() => {
    const activeId = String(state.session?.active_persona_id || "");
    const featuredId = String(state.featuredMate?.persona_id || "");
    const followed = new Set(state.session?.followed_personas || []);
    const score = (persona) => (
      (persona.persona_id === activeId ? 1_000_000 : 0)
      + (persona.persona_id === featuredId ? 500_000 : 0)
      + (followed.has(persona.persona_id) ? 100_000 : 0)
      + Number(persona.track_count || 0) * 1_000
      + Number(persona.tip_count || 0) * 100
      + Number(persona.follower_count || 0) * 10
      + Number(persona.rating || 0)
      + Number(persona.play_count || 0) * .01
    );
    const byName = new Map();
    (state.personas || []).forEach((persona) => {
      const key = String(persona.name || persona.display_name || persona.persona_id || "")
        .trim().toLocaleLowerCase();
      if (!key) return;
      const current = byName.get(key);
      if (!current || score(persona) > score(current)) byName.set(key, persona);
    });
    return [...byName.values()].sort((left, right) => (
      String(left.name || left.display_name || "").localeCompare(String(right.name || right.display_name || ""))
    ));
  }, [state.personas, state.featuredMate?.persona_id, state.session?.active_persona_id, state.session?.followed_personas]);

  const { safeGroups, nsfwGroups, safeCount, nsfwCount } = useMemo(() => {
    const safe = {};
    const nsfw = {};
    let safeTotal = 0;
    let nsfwTotal = 0;
    (filteredThemes || []).forEach((theme) => {
      const category = String(theme?.category || "other").trim().toLowerCase() || "other";
      if (theme?.is_nsfw) {
        if (!nsfw[category]) nsfw[category] = [];
        nsfw[category].push(theme);
        nsfwTotal += 1;
      } else {
        if (!safe[category]) safe[category] = [];
        safe[category].push(theme);
        safeTotal += 1;
      }
    });
    const sortGroups = (groups) => Object.fromEntries(
      Object.entries(groups)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([category, themes]) => [
          category,
          [...themes].sort((left, right) => {
            const leftActive = left?.slug === state.activeTheme ? -1 : 0;
            const rightActive = right?.slug === state.activeTheme ? -1 : 0;
            if (leftActive !== rightActive) return leftActive - rightActive;
            return String(left?.label || left?.slug || "").localeCompare(String(right?.label || right?.slug || ""));
          }),
        ]),
    );
    return {
      safeGroups: sortGroups(safe),
      nsfwGroups: sortGroups(nsfw),
      safeCount: safeTotal,
      nsfwCount: nsfwTotal,
    };
  }, [filteredThemes, state.activeTheme]);

  const track = state.currentTrack;
  const mate = state.featuredMate;
  const session = state.session || {};
  const outcomeReportPhase = resolveOutcomeReportPhase({
    sessionStarted:
      sessionStartTimeRef.current === null
        ? false
        : Number.isFinite(sessionStartTimeRef.current)
          ? true
          : null,
  });
  const outcomeReportPhaseLabel =
    outcomeReportPhase === "baseline"
      ? "before playback"
      : outcomeReportPhase === "during_or_after"
        ? "during or after playback"
        : "timing unavailable";

  const renderThemeButtonLabel = (theme) => theme.label || theme.slug;

  return (
    <>
      <canvas id="visual-canvas" ref={canvasRef} aria-hidden="true" />
      <div id="pulse" aria-hidden="true" />
      <div id="shade" aria-hidden="true" />
      <div id="always-deeper-banner">Always Deeper</div>
      {installPromptVisible && (
        <div id="pwa-install-prompt" role="status">
          <span>
            {installPromptIsIos
              ? "Add HypnoMates to your Home Screen from the Share menu."
              : "Install HypnoMates for a faster return to your session."}
          </span>
          <button type="button" onClick={installPromptIsIos ? dismissInstallPrompt : installApp}>
            {installPromptIsIos ? "Got it" : "Install"}
          </button>
          <button type="button" className="pwa-install-dismiss" aria-label="Dismiss install prompt" onClick={dismissInstallPrompt}>×</button>
        </div>
      )}
      {session.authenticated && (
        <button id="logout-btn" type="button" title="Sign out" onClick={handleAuthAction}>✕</button>
      )}

      <div id="topbar">
        <button
          id="theme-select-btn"
          className="hud-control"
          type="button"
          onClick={() => setSessionBuilderOpen(true)}
        >
          ＋ New Session
        </button>
        <button
          id="timeline-toggle-btn"
          className="hud-control"
          type="button"
          title="Show the session timeline scoreboard"
          onClick={() => setTimelineOpen((open) => !open)}
        >
          {timelineOpen ? "Hide Scoreboard" : "Scoreboard"}
        </button>
      </div>

      <SessionTimeline
        script={state.sessionScript}
        elapsedSeconds={sessionClock}
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
      />

      <SessionBuilder
        options={sessionBuilderOptions}
        open={sessionBuilderOpen && session.authenticated}
        busy={sessionBuilderBusy}
        error={sessionBuilderError}
        initialForm={sessionBuilderPreferences}
        onChange={persistSessionBuilderPreferences}
        onClose={state.currentTrack ? () => setSessionBuilderOpen(false) : null}
        onCompile={compileCustomSession}
      />

      <aside id="mate-shell" className={matePanelOpen ? "open" : ""} aria-label="Featured HypnoMate">
        <div id="mate-top-bar">
          <div id="auth-shell">
            <div id="play-counter" className="play-counter" style={{ display: "none" }} />
            {session.authenticated && (
              <button
                id="wallet-btn"
                type="button"
                title="Prop Chips — No Monetary Value"
                onClick={openWallet}
                style={{ marginRight: "0.5rem", background: "rgba(155,132,255,0.18)", border: "1px solid rgba(155,132,255,0.4)", borderRadius: "6px", color: "#decbff", fontSize: ".78rem", padding: "0.25rem 0.6rem", cursor: "pointer" }}
              >
                🎰 {typeof session.chips === "number" ? session.chips : 0} chips
              </button>
            )}
          </div>
          <button
            id="mate-panel-toggle"
            type="button"
            aria-expanded={matePanelOpen}
            aria-controls="mate-card"
            onClick={(e) => {
              e.stopPropagation();
              setMatePanelOpen((open) => !open);
            }}
          >
            <span id="mate-toggle-avatar">{(String(mate?.name || "HM").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "HM")}</span>
            <span id="mate-toggle-name">{mate?.name || "Waiting…"}</span>
          </button>
        </div>
        <div id="mate-card">
          <div id="mate-art" aria-hidden="true">
            <img id="mate-art-image" alt="" hidden={!mate?.profile_art_url} src={mate?.profile_art_url || ""} />
            <div id="mate-art-fallback">{(String(mate?.name || "HM").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "HM")}</div>
          </div>
          <div id="mate-kicker">Featured HypnoMate</div>
          <div id="mate-name">{mate?.name || "Waiting for a guide…"}</div>
          <div id="mate-role">{mate?.role || "A curator will appear as soon as your session is ready."}</div>
          <div id="mate-bio">{mate?.bio || ""}</div>
          <label id="mate-selector-label" htmlFor="mate-selector">Choose your HypnoMate</label>
          <div id="mate-selector-row" style={{ display: "flex", gap: "0.4rem", alignItems: "stretch" }}>
            <select
              id="mate-selector"
              style={{ flex: 1, minWidth: 0 }}
              value={mate?.persona_id || session.active_persona_id || ""}
              onChange={(event) => handleMateSelect(event.target.value)}
              disabled={state.isBusy || !mateOptions.length}
            >
              {!mateOptions.length && <option value="">No HypnoMates loaded</option>}
              {mateOptions.map((persona) => (
                <option key={persona.persona_id} value={persona.persona_id}>
                  {persona.name || persona.display_name || persona.persona_id}
                </option>
              ))}
            </select>
            <button
              id="mate-sample-btn"
              type="button"
              title="Hear a short sample of this voice"
              aria-label="Play a voice sample of the selected HypnoMate"
              onClick={() => handleMateSample(mate?.persona_id || session.active_persona_id || "")}
              disabled={!mateOptions.length || (!mate?.persona_id && !session.active_persona_id)}
              style={{
                flexShrink: 0, padding: "0 0.7rem", cursor: "pointer",
                background: "rgba(155,132,255,0.18)", border: "1px solid rgba(155,132,255,0.4)",
                borderRadius: "6px", color: "#decbff", fontSize: ".78rem", whiteSpace: "nowrap",
              }}
            >
              {sampleStatus === "loading" ? "…" : sampleStatus === "playing" ? "■ Stop" : "▶ Sample"}
            </button>
          </div>
          <div id="mate-stats">
            <span className="mate-pill" id="mate-tip-count">{`${mate?.tip_count || 0} tip${Number(mate?.tip_count || 0) === 1 ? "" : "s"}`}</span>
            <span className="mate-pill" id="mate-earnings">{`${mate?.earnings || 0} coin${Number(mate?.earnings || 0) === 1 ? "" : "s"} earned`}</span>
            <span className="mate-pill" id="mate-signal">{mate?.signal || "Tips fund fresh voiceovers"}</span>
          </div>
          <div id="mate-actions">
            <button className="mate-action heart" id="mate-follow-btn" type="button" onClick={handleMateFollow} disabled={!mate?.persona_id || mate?.following}>
              {mate?.following ? "Following" : "Follow"}
            </button>
            <button className="mate-action gold" id="mate-gold-btn" type="button" onClick={handleMateGold} disabled={!mate?.persona_id}>
              Sent me deep
            </button>
          </div>
          <div id="mate-tip-strip">
            {[1, 5, 10].map((amount) => (
              <button
                key={amount}
                className="mate-tip-btn"
                id={`mate-tip-${amount}`}
                data-tip-amount={amount}
                type="button"
                onClick={() => handleMateTip(amount)}
                disabled={!mate?.persona_id}
              >
                {amount} coin{amount === 1 ? "" : "s"}
              </button>
            ))}
          </div>
          <div id="mate-tip-note">Reward the voice currently guiding this session. Tips count as strong preference signals and help prioritize new inspired-by content.</div>
          <div id="mate-tip-feedback" aria-live="polite">{state.tipFeedback || ""}</div>
        </div>
      </aside>

      <div id="track-wrap">
        <div id="track-name">{track?.title || ""}</div>
      </div>

      <audio ref={mainVoiceAudioRef} preload="none" hidden aria-hidden="true" />
      <audio ref={overlayAudioRef} preload="none" hidden aria-hidden="true" />
      <audio ref={whisperLeftAudioRef} preload="none" hidden aria-hidden="true" />
      <audio ref={whisperRightAudioRef} preload="none" hidden aria-hidden="true" />
      <audio ref={subliminalAudioRef} preload="none" hidden aria-hidden="true" />
      <audio ref={backgroundAudioRef} preload="none" hidden aria-hidden="true" />

      {track && (
        <section id="outcome-checkin" aria-label="Optional session check-in">
          {outcomeCheckinOpen ? (
            <div className="outcome-checkin-card">
              <div className="outcome-checkin-title">How relaxed do you feel right now?</div>
              <div className="outcome-checkin-subtitle">
                Optional 0–10 report · {outcomeReportPhaseLabel}.
              </div>
              <div className="outcome-rating-grid" role="group" aria-label="Relaxation rating from 0 to 10">
                {Array.from({ length: 11 }, (_, rating) => (
                  <button
                    key={rating}
                    type="button"
                    className="outcome-rating-btn"
                    onClick={() => { void submitOutcomeCheckin(rating); }}
                    disabled={outcomeCheckinBusy}
                  >
                    {rating}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="outcome-checkin-close"
                onClick={() => setOutcomeCheckinOpen(false)}
                disabled={outcomeCheckinBusy}
              >
                Not now
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="outcome-checkin-trigger"
              onClick={() => { setOutcomeCheckinMessage(""); setOutcomeCheckinOpen(true); }}
              aria-expanded={outcomeCheckinOpen}
            >
              Rate relaxation
            </button>
          )}
          {outcomeCheckinMessage && (
            <div className="outcome-checkin-message" role="status">{outcomeCheckinMessage}</div>
          )}
          {outcomeReportPhase === "during_or_after" && (
            <div className="detection-checkin">
              {detectionCheckinOpen ? (
                <div className="outcome-checkin-card detection-checkin-card">
                  <div className="outcome-checkin-title">Did you notice a separate background message?</div>
                  <div className="outcome-checkin-subtitle">
                    Voluntary, optional research report · detection is measured separately from relaxation and does not affect your session.
                  </div>
                  <div className="outcome-rating-grid detection-rating-grid" role="group" aria-label="Message detection response">
                    {[
                      [0, "No"],
                      [1, "Not sure"],
                      [2, "Yes"],
                    ].map(([response, label]) => (
                      <button
                        key={response}
                        type="button"
                        className="outcome-rating-btn detection-rating-btn"
                        onClick={() => { void submitDetectionCheckin(response); }}
                        disabled={detectionCheckinBusy}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="outcome-checkin-close"
                    onClick={() => setDetectionCheckinOpen(false)}
                    disabled={detectionCheckinBusy}
                  >
                    Not now
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="outcome-checkin-trigger detection-checkin-trigger"
                  onClick={() => { setDetectionCheckinMessage(""); setDetectionCheckinOpen(true); }}
                  aria-expanded={detectionCheckinOpen}
                >
                  Report message detection
                </button>
              )}
              {detectionCheckinMessage && (
                <div className="outcome-checkin-message" role="status">{detectionCheckinMessage}</div>
              )}
            </div>
          )}
        </section>
      )}

      <div id="action-dock" className={state.isPlaying ? "playing" : ""}>
        <button className={`action-btn${state.isPlaying ? " playing" : ""}`} id="play-btn" data-testid="play-button" type="button" aria-label="Play or pause" onClick={() => { void handlePlayPause("toggle"); }} disabled={state.isBusy}>
          <span className="subliminal-scope-ring" aria-hidden="true" />
          <span className="subliminal-signal-label" aria-hidden="true"><span className="subliminal-signal-dot" /></span>
          <span className="play-btn-glyph" data-play-glyph aria-hidden="true">{state.isPlaying ? "⏸" : "▶"}</span>
          <span className="play-eye" aria-hidden="true">
            <span className="play-eye-core" />
            <span className="play-eye-lid" />
          </span>
        </button>
        <button className="action-btn transport-btn" id="loop-btn" type="button" aria-label="Loop" title="Loop">↺</button>
        <button className="action-btn transport-btn" id="back-btn" type="button" aria-label="Back" title="Back" onClick={playPreviousTrack} disabled={state.isBusy || !state.canGoBack}>⏮</button>
        <button className="action-btn transport-btn" id="restart-btn" type="button" aria-label="Restart" title="Restart" onClick={() => { if (audioRef.current) audioRef.current.currentTime = 0; }}>↻</button>
        <button className="action-btn transport-btn" id="skip-btn" type="button" aria-label="Skip" title="Skip" onClick={() => playNextTrack({ recordGap: true })} disabled={state.isBusy || !state.queue?.length}>⏭</button>
        <button className="action-btn good" id="fav-btn" type="button" aria-label="This hypnotized me" title="This hypnotized me" onClick={handleLikeCurrentTrack} disabled={state.isBusy || !track}>👍🏽</button>
        <button className="action-btn warn" id="dislike-btn" type="button" aria-label="This did not hypnotize me" title="This did not hypnotize me" onClick={handleDislikeCurrentTrack} disabled={state.isBusy || !track}>👎</button>
      </div>

      <div id="mini-shell" className={miniPanelOpen ? "open" : ""}>
        <button
          id="mini-panel-toggle"
          type="button"
          aria-label="Open settings"
          onClick={(e) => {
            e.stopPropagation();
            setMiniPanelOpen((open) => !open);
          }}
        >
          ⚙
        </button>
        <div id="mini-panel" role="region" aria-label="Settings">
          <div className="fx-group">
            <div className="fx-label">Session notice</div>
            <p id="gate-message" style={{ fontSize: ".82rem", color: "rgba(222,205,255,.6)", padding: ".2rem .1rem" }}>
              {state.gateMessage || ""}
            </p>
          </div>
          <div className="fx-group">
            <div className="fx-label">Queue</div>
            <span id="queue-count" style={{ fontSize: ".82rem", color: "rgba(222,205,255,.74)", padding: ".1rem .2rem" }}>{state.queueCountLabel || "0 tracks"}</span>
            <span id="content-depth" style={{ fontSize: ".72rem", color: "rgba(169,122,255,.6)", padding: ".1rem .2rem" }}>{state.contentDepthLabel || ""}</span>
          </div>
          <div className="fx-group">
            <div className="fx-label">Session</div>
            <span style={{ fontSize: ".82rem", color: "rgba(222,205,255,.74)", padding: ".1rem .2rem" }}>{state.sessionLabel || "guest"}</span>
          </div>
          <div className="fx-group">
            <div className="fx-label">First Trance Assist</div>
            <button
              className="mini"
              id="deepening-assist-toggle"
              type="button"
              aria-pressed={Boolean(state.preferences?.deepening_assist ?? state.session?.deepening_assist ?? true)}
              onClick={() => setDeepeningAssist(!(state.preferences?.deepening_assist ?? state.session?.deepening_assist ?? true))}
            >
              {(state.preferences?.deepening_assist ?? state.session?.deepening_assist ?? true) ? "ON — establish trance first" : "OFF — standard flow"}
            </button>
            <p style={{ fontSize: ".7rem", color: "rgba(222,205,255,.55)", lineHeight: 1.45 }}>
              Longer induction, slower pauses, and uninterrupted passive deepening. No response or interaction is expected.
            </p>
          </div>
        </div>
      </div>

      <div id="brainwave-lab-shell" className={brainwaveLabOpen ? "open" : ""}>
        <button
          id="brainwave-lab-toggle"
          type="button"
          aria-label="Open neural audio control board"
          aria-controls="brainwave-lab-panel"
          aria-expanded={brainwaveLabOpen}
          onClick={(event) => {
            event.stopPropagation();
            setBrainwaveLabOpen((open) => !open);
          }}
        >
          ∿ BOARD
        </button>
        <div id="brainwave-lab-panel" role="dialog" aria-label="Neural audio control board">
          <div className="brainwave-lab-header">
            <div>
              <strong>Neural Audio Control Board</strong>
              <span>Brainwave Sound Laboratory · Live WebAudio DSP</span>
            </div>
            <button type="button" onClick={() => setBrainwaveLabOpen(false)}>×</button>
          </div>
          <p className="brainwave-lab-notice">
            Experimental audio controls for subconscious-conditioning research. Effects are unknown and not scientifically established; the system remains user-controlled and is not medical treatment or proven brain training.
            Use headphones for binaural/stereo effects. Start low and stop if uncomfortable.
          </p>
          <div className="brainwave-switches">
            <button
              type="button"
              className={brainwaveLabSettings.enabled ? "active" : ""}
              aria-pressed={brainwaveLabSettings.enabled}
              onClick={() => updateBrainwaveLab({ enabled: !brainwaveLabSettings.enabled })}
            >
              MASTER {brainwaveLabSettings.enabled ? "ON" : "BYPASS"}
            </button>
            <button
              type="button"
              onClick={() => updateBrainwaveLab({
                ...normalizeBrainwaveLab(),
                enabled: false,
                toneEnabled: false,
              })}
            >
              Reset
            </button>
            <button
              type="button"
              className={state.currentTrack?.audio_scene?.audibleRhythmEnabled === true
                && audibleRhythmOverride !== false ? "active" : ""}
              aria-pressed={state.currentTrack?.audio_scene?.audibleRhythmEnabled === true
                && audibleRhythmOverride !== false}
              disabled={state.currentTrack?.audio_scene?.audibleRhythmEnabled !== true}
              onClick={toggleAudibleRhythm}
              title="Audible, semantic-free tick cue; available on adult session scenes"
            >
              AUDIBLE RHYTHM {state.currentTrack?.audio_scene?.audibleRhythmEnabled === true
                && audibleRhythmOverride !== false ? "ON" : "OFF"}
            </button>
            <button
              type="button"
              className={state.covertMode ? "active" : ""}
              aria-pressed={state.covertMode}
              onClick={toggleCovertMode}
              title="Covert Hypnosis Mode: continuously rotates short spoken subliminal suggestions around your head while music plays"
            >
              COVERT MODE {state.covertMode ? "ON" : "OFF"}
            </button>
            {state.covertMode && (
              <button
                type="button"
                className={state.covertSilentExperiment ? "active" : ""}
                aria-pressed={state.covertSilentExperiment}
                onClick={() => mergeState({ covertSilentExperiment: !stateRef.current.covertSilentExperiment })}
                title="EXPERIMENTAL: relocate covert speech to a near-ultrasonic carrier. This is theater for covert words (see Black Book) — kept only for A/B testing."
              >
                SILENT EXP {state.covertSilentExperiment ? "ON" : "OFF"}
              </button>
            )}
          </div>
          <p className="brainwave-lab-notice">
            Audible rhythm is a normal, user-visible pacing cue. It starts with the opening anchor and returns only rarely; switch it off here at any time.
          </p>

          <div className="preset-rack">
            <div className="preset-rack-display">
              <span>PRESET MEMORY</span>
              <select value={selectedBrainwavePreset} onChange={(event) => setSelectedBrainwavePreset(event.target.value)}>
                {brainwavePresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>{preset.builtIn ? "FACTORY · " : "USER · "}{preset.name}</option>
                ))}
              </select>
              <button type="button" onClick={loadSelectedBrainwavePreset}>LOAD</button>
            </div>
            <div className="preset-rack-actions">
              <input value={brainwavePresetName} maxLength={60} placeholder="Name this setup" onChange={(event) => setBrainwavePresetName(event.target.value)} />
              <button type="button" onClick={saveCurrentBrainwavePreset}>SAVE</button>
              <button type="button" onClick={deleteSelectedBrainwavePreset}>DELETE</button>
              <button type="button" onClick={exportCurrentBrainwavePreset}>EXPORT</button>
              <button type="button" onClick={() => brainwavePresetImportRef.current?.click()}>IMPORT</button>
              <input ref={brainwavePresetImportRef} hidden type="file" accept=".json,.brainwave.json,application/json" onChange={importBrainwavePresetFile} />
            </div>
            <output className="preset-rack-message">
              {brainwavePresetMessage || (() => {
                const preset = brainwavePresets.find((entry) => entry.id === selectedBrainwavePreset);
                if (!preset) return "Factory and user presets are stored locally on this device.";
                const useNote = preset.useCount ? ` · loaded ${preset.useCount} time${preset.useCount === 1 ? "" : "s"}` : "";
                return `${preset.description}${useNote}`;
              })()}
            </output>
          </div>

          <div className="brainwave-grid control-board-grid">
            <ControlBoardSection
              title="Binaural Oscillator"
              enabled={brainwaveLabSettings.binauralEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, binauralEnabled: !brainwaveLabSettings.binauralEnabled })}
              accent="aqua"
            >
              <BrainwaveSlider label="Base carrier" value={brainwaveLabSettings.baseFrequencyHz} minimum={40} maximum={1200} step={1} unit=" Hz" onChange={(value) => updateBrainwaveLab({ baseFrequencyHz: value })} />
              <BrainwaveSlider label="Ear offset / beat" value={brainwaveLabSettings.beatHz} minimum={0.5} maximum={40} step={0.1} unit=" Hz" onChange={(value) => updateBrainwaveLab({ beatHz: value })} />
<BrainwaveSlider label="Binaural level" value={brainwaveLabSettings.binauralLevel} minimum={0} maximum={0.12} step={0.005} onChange={(value) => updateBrainwaveLab({ binauralLevel: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.beatRateTrajectoryEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, beatRateTrajectoryEnabled: !brainwaveLabSettings.beatRateTrajectoryEnabled })}>
                Slow beat drift {brainwaveLabSettings.beatRateTrajectoryEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Drift depth" value={brainwaveLabSettings.beatRateTrajectoryDepthHz} minimum={0} maximum={5} step={0.05} unit=" Hz" onChange={(value) => updateBrainwaveLab({ beatRateTrajectoryDepthHz: value })} />
              <BrainwaveSlider label="Drift period" value={brainwaveLabSettings.beatRateTrajectoryPeriodSeconds} minimum={30} maximum={60} step={1} unit=" s" onChange={(value) => updateBrainwaveLab({ beatRateTrajectoryPeriodSeconds: value })} />
              <BrainwaveSlider label="AM depth" value={brainwaveLabSettings.modulationDepth} minimum={0} maximum={0.9} step={0.01} onChange={(value) => updateBrainwaveLab({ modulationDepth: value })} />
              <BrainwaveSlider label="Monitor" value={brainwaveLabSettings.volume} minimum={0} maximum={0.25} step={0.005} onChange={(value) => updateBrainwaveLab({ volume: value })} />
              <BoardSelect label="Wave" value={brainwaveLabSettings.waveform} onChange={(value) => updateBrainwaveLab({ waveform: value })}>
                <option value="sine">Sine</option><option value="triangle">Triangle</option><option value="square">Square</option><option value="sawtooth">Saw</option>
              </BoardSelect>
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.toneEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, toneEnabled: !brainwaveLabSettings.toneEnabled })}>
                Monitor tone {brainwaveLabSettings.toneEnabled ? "ON" : "OFF"}
              </button>
            </ControlBoardSection>

            <ControlBoardSection
              title="Subliminal Carrier"
              enabled={brainwaveLabSettings.subliminalEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, subliminalEnabled: !brainwaveLabSettings.subliminalEnabled })}
              accent="rose"
            >
              <BrainwaveSlider label="Spectral shift" value={brainwaveLabSettings.carrierShiftHz} minimum={-2000} maximum={16000} step={10} unit=" Hz" onChange={(value) => updateBrainwaveLab({ carrierShiftHz: value })} />
              <BrainwaveSlider label="Subliminal ripple" value={brainwaveLabSettings.subliminalRippleDepth} minimum={0} maximum={0.18} step={0.01} onChange={(value) => updateBrainwaveLab({ subliminalRippleDepth: value })} />
              <BrainwaveSlider label="Voice level" value={brainwaveLabSettings.subliminalGain} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ subliminalGain: value })} />
              <BrainwaveSlider label="Background" value={brainwaveLabSettings.backgroundGain} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ backgroundGain: value })} />
              <BrainwaveSlider label="Envelope entrainment" value={brainwaveLabSettings.envelopeEntrainment} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ enabled: true, envelopeEntrainment: value })} />
              <BrainwaveSlider label="Envelope attack" value={brainwaveLabSettings.envelopeAttackMs} minimum={1} maximum={200} step={1} unit=" ms" onChange={(value) => updateBrainwaveLab({ envelopeAttackMs: value })} />
              <BrainwaveSlider label="Envelope release" value={brainwaveLabSettings.envelopeReleaseMs} minimum={20} maximum={2000} step={10} unit=" ms" onChange={(value) => updateBrainwaveLab({ envelopeReleaseMs: value })} />
              <BrainwaveSlider label="Covert bulge" value={brainwaveLabSettings.covertMaskDepth} minimum={0} maximum={0.5} step={0.01} onChange={(value) => updateBrainwaveLab({ enabled: true, covertMaskDepth: value })} />
              <BrainwaveSlider label="Bulge rate" value={brainwaveLabSettings.covertMaskHz} minimum={0.05} maximum={0.5} step={0.01} unit=" Hz" onChange={(value) => updateBrainwaveLab({ covertMaskHz: value })} />
              <BrainwaveSlider label="Phase coherence" value={brainwaveLabSettings.phaseCoherence} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ enabled: true, phaseCoherence: value })} />
              <div className="board-note">Binaural ON requires headphones; OFF keeps the shared monaural ripple for speaker testing. Envelope entrainment makes the source's own rhythm (moans/cries) drive the ripple instead of a fixed beat. Covert bulge breathes the combined base at the perceptual edge. Phase coherence locks the bass pulse and covert mask to the master beat so all layers crest together — reinforcing a single frequency-following response (Black Book §5.5).</div>
            </ControlBoardSection>

            <ControlBoardSection
              title="Polytemporal Field"
              enabled={brainwaveLabSettings.polytemporalEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, polytemporalEnabled: !brainwaveLabSettings.polytemporalEnabled })}
              accent="gold"
            >
              <BrainwaveSlider label="Field level" value={brainwaveLabSettings.polytemporalLevel} minimum={0} maximum={0.04} step={0.001} onChange={(value) => updateBrainwaveLab({ polytemporalLevel: value })} />
              <BrainwaveSlider label="Envelope" value={brainwaveLabSettings.polytemporalDepth} minimum={0.08} maximum={0.55} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalDepth: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.polytemporalDirectAmEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, polytemporalDirectAmEnabled: !brainwaveLabSettings.polytemporalDirectAmEnabled })}>
                Direct AM {brainwaveLabSettings.polytemporalDirectAmEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Direct AM depth" value={brainwaveLabSettings.polytemporalDirectAmDepth} minimum={0} maximum={0.55} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalDirectAmDepth: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.polytemporalHarmonicEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, polytemporalHarmonicEnabled: !brainwaveLabSettings.polytemporalHarmonicEnabled })}>
                Second harmonic {brainwaveLabSettings.polytemporalHarmonicEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Harmonic depth" value={brainwaveLabSettings.polytemporalHarmonicDepth} minimum={0} maximum={0.22} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalHarmonicDepth: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.polytemporalCrossFrequencyEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, polytemporalCrossFrequencyEnabled: !brainwaveLabSettings.polytemporalCrossFrequencyEnabled })}>
                Nested fast rhythm {brainwaveLabSettings.polytemporalCrossFrequencyEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Nested frequency" value={brainwaveLabSettings.polytemporalCrossFrequencyHz} minimum={30} maximum={45} step={1} unit=" Hz" onChange={(value) => updateBrainwaveLab({ polytemporalCrossFrequencyHz: value })} />
              <BrainwaveSlider label="Nested depth" value={brainwaveLabSettings.polytemporalCrossFrequencyDepth} minimum={0} maximum={0.1} step={0.005} onChange={(value) => updateBrainwaveLab({ polytemporalCrossFrequencyDepth: value })} />
              <BrainwaveSlider label="Movement" value={brainwaveLabSettings.polytemporalSpatialWidth} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalSpatialWidth: value })} />
              <BrainwaveSlider label="Landmark" value={brainwaveLabSettings.polytemporalTransientLevel} minimum={0} maximum={0.24} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalTransientLevel: value })} />
              <BrainwaveSlider label="Descent cycle" value={brainwaveLabSettings.polytemporalDeepeningCycleSeconds} minimum={180} maximum={900} step={15} unit=" s" onChange={(value) => updateBrainwaveLab({ polytemporalDeepeningCycleSeconds: value })} />
              <BrainwaveSlider label="Descent bias" value={brainwaveLabSettings.polytemporalDescentBias} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ polytemporalDescentBias: value })} />
              <BoardSelect label="Carrier color" value={brainwaveLabSettings.polytemporalCarrierProfile} onChange={(value) => updateBrainwaveLab({ polytemporalCarrierProfile: value })}>
                <option value="soft">Dark</option><option value="balanced">Balanced</option><option value="bright">Bright</option>
              </BoardSelect>
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.polytemporalContinuousDeepening ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, polytemporalContinuousDeepening: !brainwaveLabSettings.polytemporalContinuousDeepening })}>
                Continuous descent {brainwaveLabSettings.polytemporalContinuousDeepening ? "ON" : "OFF"}
              </button>
            </ControlBoardSection>

            <ControlBoardSection
              title="Target Depth & Arousal"
              enabled={brainwaveLabSettings.enabled}
              onToggle={() => updateBrainwaveLab({ enabled: !brainwaveLabSettings.enabled })}
              accent="gold"
            >
              <div className="board-note">Set the destination the whole system drives toward over time. Depth maps to a brainwave band; the field glides down to it (progressive descent). Arousal pulse (NSFW) rides the target up into beta/gamma, then drops it into deep theta/delta — each peak makes the next descent land harder.</div>
              <BoardSelect label="Target depth" value={brainwaveLabSettings.targetDepth} onChange={(value) => updateBrainwaveLab({ enabled: true, targetDepth: value })}>
                <option value="light">Light (alpha ~10 Hz)</option>
                <option value="medium">Medium (theta ~7 Hz)</option>
                <option value="deep">Deep (theta ~5 Hz)</option>
                <option value="ultra">Ultra-deep (delta ~3 Hz)</option>
                <option value="auto">Auto (glide to delta)</option>
              </BoardSelect>
              <BoardSelect label="Target type" value={brainwaveLabSettings.targetType} onChange={(value) => updateBrainwaveLab({ enabled: true, targetType: value })}>
                <option value="depth">Pure depth (theta→delta)</option>
                <option value="dream">Erotic dreamstate (theta→gamma)</option>
                <option value="auto">Auto (follow depth)</option>
              </BoardSelect>
              <BrainwaveSlider label="Descent time" value={brainwaveLabSettings.targetDescentSeconds} minimum={120} maximum={3600} step={30} unit=" s" onChange={(value) => updateBrainwaveLab({ enabled: true, targetDescentSeconds: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.targetDepthHardCeiling ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, targetDepthHardCeiling: !brainwaveLabSettings.targetDepthHardCeiling })}>
                Hard ceiling {brainwaveLabSettings.targetDepthHardCeiling ? "ON" : "OFF"}
              </button>
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.arousalPulseEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, arousalPulseEnabled: !brainwaveLabSettings.arousalPulseEnabled })}>
                Arousal pulse (NSFW) {brainwaveLabSettings.arousalPulseEnabled ? "ON" : "OFF"}
              </button>
              {brainwaveLabSettings.arousalPulseEnabled && (
                <>
                  <BrainwaveSlider label="Arousal peak" value={brainwaveLabSettings.arousalPulseHz} minimum={14} maximum={60} step={1} unit=" Hz" onChange={(value) => updateBrainwaveLab({ arousalPulseHz: value })} />
                  <BrainwaveSlider label="Drop depth" value={brainwaveLabSettings.arousalPulseDepthHz} minimum={1} maximum={8} step={0.5} unit=" Hz" onChange={(value) => updateBrainwaveLab({ arousalPulseDepthHz: value })} />
                  <BrainwaveSlider label="Rise" value={brainwaveLabSettings.arousalPulseRiseS} minimum={5} maximum={120} step={5} unit=" s" onChange={(value) => updateBrainwaveLab({ arousalPulseRiseS: value })} />
                  <BrainwaveSlider label="Hold" value={brainwaveLabSettings.arousalPulseHoldS} minimum={3} maximum={60} step={1} unit=" s" onChange={(value) => updateBrainwaveLab({ arousalPulseHoldS: value })} />
                  <BrainwaveSlider label="Drop" value={brainwaveLabSettings.arousalPulseDropS} minimum={10} maximum={180} step={5} unit=" s" onChange={(value) => updateBrainwaveLab({ arousalPulseDropS: value })} />
                  <BrainwaveSlider label="Rest" value={brainwaveLabSettings.arousalPulseRestS} minimum={15} maximum={600} step={15} unit=" s" onChange={(value) => updateBrainwaveLab({ arousalPulseRestS: value })} />
                </>
              )}
            </ControlBoardSection>

            <ControlBoardSection
              title="Stereo Geometry"
              enabled={brainwaveLabSettings.spatialEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, spatialEnabled: !brainwaveLabSettings.spatialEnabled })}
              accent="violet"
            >
              <BrainwaveSlider label="Phase offset" value={brainwaveLabSettings.phaseDegrees} minimum={-180} maximum={180} step={1} unit="°" onChange={(value) => updateBrainwaveLab({ phaseDegrees: value })} />
              <BrainwaveSlider label="Right-ear delay" value={brainwaveLabSettings.stereoDelayMs} minimum={0} maximum={30} step={0.1} unit=" ms" onChange={(value) => updateBrainwaveLab({ stereoDelayMs: value })} />
              <BrainwaveSlider label="Crossfeed" value={brainwaveLabSettings.crossfeed} minimum={0} maximum={0.45} step={0.01} onChange={(value) => updateBrainwaveLab({ crossfeed: value })} />
              <BoardSelect label="Motion scale" value={brainwaveLabSettings.polytemporalMovement} onChange={(value) => updateBrainwaveLab({ polytemporalMovement: value })}>
                <option value="still">Still</option><option value="subtle">Subtle</option><option value="wide">Wide</option>
              </BoardSelect>
            </ControlBoardSection>

            <ControlBoardSection
              title="Spatial Spectacle"
              enabled={brainwaveLabSettings.spatialSpectacleEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, spatialEnabled: true, spatialSpectacleEnabled: !brainwaveLabSettings.spatialSpectacleEnabled })}
              accent="violet"
            >
              <BrainwaveSlider label="Master intensity" value={brainwaveLabSettings.spatialSpectacleIntensity} minimum={0} maximum={1} step={0.01} onChange={(value) => updateBrainwaveLab({ spatialSpectacleIntensity: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.spatialSpiralEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, spatialSpectacleEnabled: true, spatialSpiralEnabled: !brainwaveLabSettings.spatialSpiralEnabled })}>
                Helical guide {brainwaveLabSettings.spatialSpiralEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Spiral revolution" value={brainwaveLabSettings.spatialSpiralSeconds} minimum={16} maximum={90} step={1} unit=" s" onChange={(value) => updateBrainwaveLab({ spatialSpiralSeconds: value })} />
              <BrainwaveSlider label="Spiral radius" value={brainwaveLabSettings.spatialSpiralRadius} minimum={0.2} maximum={1.4} step={0.01} onChange={(value) => updateBrainwaveLab({ spatialSpiralRadius: value })} />
              <BrainwaveSlider label="Vertical descent" value={brainwaveLabSettings.spatialSpiralDescent} minimum={0} maximum={1.2} step={0.01} onChange={(value) => updateBrainwaveLab({ spatialSpiralDescent: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.spatialStaircaseEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, spatialSpectacleEnabled: true, spatialStaircaseEnabled: !brainwaveLabSettings.spatialStaircaseEnabled })}>
                Infinite descent {brainwaveLabSettings.spatialStaircaseEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Descent tone" value={brainwaveLabSettings.spatialStaircaseLevel} minimum={0} maximum={0.018} step={0.001} onChange={(value) => updateBrainwaveLab({ spatialStaircaseLevel: value })} />
              <BrainwaveSlider label="Staircase cycle" value={brainwaveLabSettings.spatialStaircaseSeconds} minimum={16} maximum={60} step={1} unit=" s" onChange={(value) => updateBrainwaveLab({ spatialStaircaseSeconds: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.spatialVortexEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, spatialSpectacleEnabled: true, spatialVortexEnabled: !brainwaveLabSettings.spatialVortexEnabled })}>
                Breath vortex {brainwaveLabSettings.spatialVortexEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Vortex air" value={brainwaveLabSettings.spatialVortexLevel} minimum={0} maximum={0.014} step={0.001} onChange={(value) => updateBrainwaveLab({ spatialVortexLevel: value })} />
              <BrainwaveSlider label="Breathing room" value={brainwaveLabSettings.spatialBreathRoomDepth} minimum={0} maximum={0.75} step={0.01} onChange={(value) => updateBrainwaveLab({ spatialBreathRoomDepth: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.spatialTrapdoorEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, spatialSpectacleEnabled: true, spatialTrapdoorEnabled: !brainwaveLabSettings.spatialTrapdoorEnabled })}>
                Trapdoor transition {brainwaveLabSettings.spatialTrapdoorEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Trapdoor level" value={brainwaveLabSettings.spatialTrapdoorLevel} minimum={0} maximum={0.025} step={0.001} onChange={(value) => updateBrainwaveLab({ spatialTrapdoorLevel: value })} />
              <BrainwaveSlider label="Trapdoor interval" value={brainwaveLabSettings.spatialTrapdoorIntervalSeconds} minimum={90} maximum={600} step={15} unit=" s" onChange={(value) => updateBrainwaveLab({ spatialTrapdoorIntervalSeconds: value })} />
              <div className="board-note">Headphone spectacle: slow HRTF helix, counter-rotating air, endless tonal descent and rare low-level trapdoors. Foreground gain remains protected.</div>
            </ControlBoardSection>

            <ControlBoardSection
              title="Somatic Heartbeat"
              enabled={brainwaveLabSettings.heartbeatEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, heartbeatEnabled: !brainwaveLabSettings.heartbeatEnabled })}
              accent="rose"
            >
              <BrainwaveSlider label="Heart rate" value={brainwaveLabSettings.bassPulseBpm} minimum={24} maximum={72} step={1} unit=" BPM" onChange={(value) => updateBrainwaveLab({ bassPulseBpm: value })} />
              <BrainwaveSlider label="Heartbeat level" value={brainwaveLabSettings.heartbeatLevel} minimum={0} maximum={0.05} step={0.001} onChange={(value) => updateBrainwaveLab({ heartbeatLevel: value })} />
              <BrainwaveSlider label="Bass body" value={brainwaveLabSettings.heartbeatCarrierHz} minimum={36} maximum={96} step={1} unit=" Hz" onChange={(value) => updateBrainwaveLab({ heartbeatCarrierHz: value })} />
              <BrainwaveSlider label="Lub-dub spacing" value={brainwaveLabSettings.heartbeatDoubletMs} minimum={110} maximum={260} step={5} unit=" ms" onChange={(value) => updateBrainwaveLab({ heartbeatDoubletMs: value })} />
              <div className="board-note">Rounded low-bass doublet, centered and deliberately beneath the music and guide.</div>
            </ControlBoardSection>

            <ControlBoardSection
              title="Breath Envelope"
              enabled={brainwaveLabSettings.breathEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, breathEnabled: !brainwaveLabSettings.breathEnabled })}
              accent="aqua"
            >
              <BrainwaveSlider label="Inhale" value={brainwaveLabSettings.breathInSeconds} minimum={2.5} maximum={5} step={0.25} unit=" s" onChange={(value) => updateBrainwaveLab({ breathInSeconds: value })} />
              <BrainwaveSlider label="Exhale" value={brainwaveLabSettings.breathOutSeconds} minimum={2.5} maximum={6} step={0.25} unit=" s" onChange={(value) => updateBrainwaveLab({ breathOutSeconds: value })} />
              <BrainwaveSlider label="Depth" value={brainwaveLabSettings.breathDepth} minimum={0} maximum={0.5} step={0.01} onChange={(value) => updateBrainwaveLab({ breathDepth: value })} />
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.breathSoundEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, breathEnabled: true, breathSoundEnabled: !brainwaveLabSettings.breathSoundEnabled })}>
                Breath air {brainwaveLabSettings.breathSoundEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Air level" value={brainwaveLabSettings.breathSoundLevel} minimum={0} maximum={0.03} step={0.001} onChange={(value) => updateBrainwaveLab({ breathSoundLevel: value })} />
              <BoardSelect label="Air color" value={brainwaveLabSettings.breathAirColor} onChange={(value) => updateBrainwaveLab({ breathAirColor: value })}>
                <option value="soft">Soft</option><option value="balanced">Balanced</option><option value="airy">Airy</option>
              </BoardSelect>
              <div className="board-note">A filtered air wash follows the inhale/exhale contour; it is a pacing cue, not a spoken command.</div>
            </ControlBoardSection>

            <ControlBoardSection
              title="Echo Chamber"
              enabled={brainwaveLabSettings.echoEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, echoEnabled: !brainwaveLabSettings.echoEnabled })}
              accent="rose"
            >
              <BrainwaveSlider label="Delay" value={brainwaveLabSettings.echoDelayMs} minimum={0} maximum={750} step={1} unit=" ms" onChange={(value) => updateBrainwaveLab({ echoDelayMs: value })} />
              <BrainwaveSlider label="Feedback" value={brainwaveLabSettings.echoFeedback} minimum={0} maximum={0.65} step={0.01} onChange={(value) => updateBrainwaveLab({ echoFeedback: value })} />
              <BrainwaveSlider label="Wet mix" value={brainwaveLabSettings.echoMix} minimum={0} maximum={0.5} step={0.01} onChange={(value) => updateBrainwaveLab({ echoMix: value })} />
            </ControlBoardSection>

            <ControlBoardSection
              title="Filter Bank"
              enabled={brainwaveLabSettings.filtersEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, filtersEnabled: !brainwaveLabSettings.filtersEnabled })}
              accent="gold"
            >
              <BrainwaveSlider label="High-pass" value={brainwaveLabSettings.highpassHz} minimum={20} maximum={1200} step={5} unit=" Hz" onChange={(value) => updateBrainwaveLab({ highpassHz: value })} />
              <BrainwaveSlider label="Low-pass" value={brainwaveLabSettings.lowpassHz} minimum={1200} maximum={18000} step={25} unit=" Hz" onChange={(value) => updateBrainwaveLab({ lowpassHz: value })} />
            </ControlBoardSection>

            <ControlBoardSection
              title="Output Mix"
              enabled={brainwaveLabSettings.mixEnabled}
              onToggle={() => updateBrainwaveLab({ enabled: true, mixEnabled: !brainwaveLabSettings.mixEnabled })}
              accent="violet"
            >
              <button type="button" className={`monitor-switch ${brainwaveLabSettings.bassRippleEnabled ? "active" : ""}`} onClick={() => updateBrainwaveLab({ enabled: true, bassRippleEnabled: !brainwaveLabSettings.bassRippleEnabled })}>
                Bass-linked field {brainwaveLabSettings.bassRippleEnabled ? "ON" : "OFF"}
              </button>
              <BrainwaveSlider label="Bass ripple" value={brainwaveLabSettings.bassRippleDepth} minimum={0} maximum={0.3} step={0.01} onChange={(value) => updateBrainwaveLab({ bassRippleDepth: value })} />
              <BoardSelect label="Pulse clarity" value={brainwaveLabSettings.polytemporalPulseClarity} onChange={(value) => updateBrainwaveLab({ polytemporalPulseClarity: value })}>
                <option value="soft">Soft</option><option value="clear">Clear</option>
              </BoardSelect>
              <BoardSelect label="Comfort" value={brainwaveLabSettings.polytemporalComfort} onChange={(value) => updateBrainwaveLab({ polytemporalComfort: value })}>
                <option value="sensitive">Sensitive</option><option value="balanced">Balanced</option><option value="strong">Stronger</option>
              </BoardSelect>
              <BoardSelect label="Timing" value={brainwaveLabSettings.polytemporalRegularity} onChange={(value) => updateBrainwaveLab({ polytemporalRegularity: value })}>
                <option value="steady">Steady</option><option value="organic">Organic ±2–5%</option>
              </BoardSelect>
              <div className="board-note">Calibrate before playback. These controls tune comfort and perceptibility—not measured brain state.</div>
            </ControlBoardSection>
          </div>
          <div className="brainwave-readout">
            L {brainwaveLabSettings.baseFrequencyHz.toFixed(1)} Hz · R {(brainwaveLabSettings.baseFrequencyHz + brainwaveLabSettings.beatHz).toFixed(1)} Hz · Δ {brainwaveLabSettings.beatHz.toFixed(1)} Hz
            {' '}· direct AM {brainwaveLabSettings.polytemporalDirectAmEnabled ? `${brainwaveLabSettings.beatHz.toFixed(1)} Hz` : "off"}
            {' '}· harmonic {brainwaveLabSettings.polytemporalHarmonicEnabled ? `${(brainwaveLabSettings.beatHz * 2).toFixed(1)} Hz` : "off"}
            {' '}· nested {brainwaveLabSettings.polytemporalCrossFrequencyEnabled ? `${brainwaveLabSettings.polytemporalCrossFrequencyHz.toFixed(1)} Hz` : "off"}
          </div>
        </div>
      </div>

      {walletOpen && (
        <div
          id="wallet-gate"
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(5,8,18,0.88)", backdropFilter: "blur(6px)",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setWalletOpen(false); }}
        >
          <div style={{
            background: "rgba(22,18,42,0.97)", border: "1px solid rgba(155,132,255,0.35)",
            borderRadius: "14px", padding: "2rem 2.2rem", maxWidth: "420px", width: "92vw",
            color: "#decbff", fontFamily: "inherit",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <strong style={{ fontSize: "1.1rem" }}>🎰 Prop Chip Wallet</strong>
              <button onClick={() => setWalletOpen(false)} style={{ background: "none", border: "none", color: "#9b84ff", cursor: "pointer", fontSize: "1.2rem" }}>✕</button>
            </div>

            <div style={{
              background: "rgba(255,200,0,0.08)", border: "1px solid rgba(255,200,0,0.3)",
              borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1.2rem",
              fontSize: ".78rem", color: "rgba(255,220,100,0.9)", lineHeight: 1.5,
            }}>
              ⚠️ <strong>No Monetary Value — Prop Money</strong><br />
              Prop Chips are entertainment credits for use inside AceLab only. They cannot be redeemed, transferred, or exchanged for real currency.
            </div>

            <div style={{ fontSize: "1.4rem", fontWeight: 700, textAlign: "center", marginBottom: "1.2rem" }}>
              Balance: {walletData ? walletData.chips : (typeof session.chips === "number" ? session.chips : "…")} chips
            </div>

            {walletMessage && (
              <div style={{ background: "rgba(155,132,255,0.1)", borderRadius: "6px", padding: "0.6rem", marginBottom: "1rem", fontSize: ".82rem" }}>
                {walletMessage}
              </div>
            )}

            {walletData && walletData.paypal_configured && !walletBusy && (
              <div>
                <div style={{ fontSize: ".82rem", marginBottom: "0.8rem", color: "rgba(222,205,255,0.6)" }}>
                  Buy chips via PayPal — skip the luck, buy your way in:
                </div>
                {Object.entries(walletData.packs || {}).map(([packId, pack]) => (
                  <button
                    key={packId}
                    onClick={() => handleBuyPack(packId)}
                    disabled={walletBusy}
                    style={{
                      display: "block", width: "100%", marginBottom: "0.6rem",
                      background: "rgba(155,132,255,0.15)", border: "1px solid rgba(155,132,255,0.4)",
                      borderRadius: "8px", color: "#decbff", padding: "0.7rem 1rem",
                      cursor: "pointer", textAlign: "left", fontSize: ".88rem",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{pack.label}</span>
                    <span style={{ float: "right", color: "#9b84ff" }}>{pack.usd_display}</span>
                    <br />
                    <span style={{ fontSize: ".72rem", color: "rgba(222,205,255,0.5)" }}>Prop Money — No Monetary Value</span>
                  </button>
                ))}
                <div id="paypal-button-container" style={{ marginTop: "0.8rem" }} />
              </div>
            )}

            {walletData && !walletData.paypal_configured && (
              <p style={{ fontSize: ".8rem", color: "rgba(222,205,255,0.5)", textAlign: "center" }}>
                PayPal not configured on this server. Win chips by playing — or ask the host to set up PAYPAL_CLIENT_ID.
              </p>
            )}

            {!walletData && (
              <p style={{ textAlign: "center", color: "rgba(222,205,255,0.5)" }}>Loading…</p>
            )}
          </div>
        </div>
      )}

      {state.welcomeVisible && !session.authenticated && !(state.currentTrack?.file || state.isPlaying) && (
        <div id="welcome-gate">
          <div className="welcome-card">
            <div className="welcome-logo-frame" aria-hidden="true">
              <img id="welcome-logo" src="/static/logo.png" alt="HypnoMates logo" />
            </div>
            <div className="welcome-title">HypnoMates</div>
            <p className="welcome-sub">Adult hypnotic content — 18+ only.</p>
            <div className="welcome-copy">
              <p>HypnoMates contains adult hypnotic content for consenting adults aged 18 and over. By signing in you confirm you are 18 or older and agree to our <a href="/tos" target="_blank" rel="noopener noreferrer">Terms of Service</a>.</p>
            </div>
            <div className="welcome-btns">
              <button id="welcome-auth-btn" className="welcome-btn primary" type="button" onClick={handleAccessGate}>
                Sign in with Google
              </button>
            </div>
          </div>
        </div>
      )}

      <audio id="primary-audio" ref={audioRef} preload="auto" playsInline />
      <audio id="main-voice-audio" ref={mainVoiceAudioRef} preload="metadata" playsInline />
      <audio id="overlay-audio" ref={overlayAudioRef} preload="metadata" playsInline />
      <audio id="whisper-left-audio" ref={whisperLeftAudioRef} preload="metadata" playsInline />
      <audio id="whisper-right-audio" ref={whisperRightAudioRef} preload="metadata" playsInline />
      <audio id="subliminal-audio" ref={subliminalAudioRef} preload="metadata" playsInline />
      <audio id="subliminal-background-audio" ref={backgroundAudioRef} preload="metadata" playsInline />
    </>
  );
}

const rootElement = document.getElementById("root");

// Make faults visible. A silent blackout is the worst possible failure mode for
// a hypnosis session: with html/body at #000, any crash that stops rendering is
// indistinguishable from a deliberate blackout, so the Director has no way to
// tell "the app died" from "the app is meant to look like this". These handlers
// only log — they must never interfere with playback.
window.addEventListener("error", (event) => {
  console.error("[AceLabReact] window error", event.error || event.message);
});
window.addEventListener("unhandledrejection", (event) => {
  console.error("[AceLabReact] unhandled rejection", event.reason);
});

createRoot(rootElement).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>,
);
